/*
 * Carbisdale - EditorModel
 *
 * Datenmodell für den Prose-Editor.
 * Verwaltet Paragraphs, Cursor, Selection und Undo/Redo.
 */

import {
    cleanupEmptyFormatSpans as tpCleanupEmptyFormatSpans,
    getWordBounds as tpGetWordBounds,
    codepointLengthAt,
    codepointLengthBefore,
    snapToCodepointBoundary,
    findLinkSpans
} from '../TextProcessor.js'
import {
    markdownToRichText,
    markdownLinesToRichText,
    deserializeParagraphRT,
    adjustSpansForInsert,
    adjustSpansForDelete,
    adjustSpansForDeleteRange,
    adjustLinksForInsert,
    adjustLinksForDelete,
    adjustLinksForDeleteRange,
    shiftSpans,
    shiftLinks,
    splitSpansAtOffset,
    splitLinksAtOffset,
    mergeAdjacentSpans,
    extendOrCreateSpan,
    removeFormatFromRange,
    isRangeCovered,
    getFormatsBeforeOffset,
    getSpansInRange,
    getLinksInRange,
    richTextToMarkdown,
    richTextToMarkdownFull
} from '../RichTextOps.js'
import { EDITOR, IMPLICIT_HEAD_ID } from '../config.js'
import { generateUUID } from '../persistence/uuid.js'

/**
 * Paragraph-Typ-Konstanten (Outline-Redesign Phase 1).
 *
 * Drei kanonische Typen:
 * - 'normal': Fließtext-Para mit `text` / `info`. Trägt im v4-Modell weder
 *   `note` noch eigene `outlineDepth`-Semantik (die Tiefe gehört zum
 *   vorhergehenden Outline-Item).
 * - 'outline-item': Strukturmarker mit `note` + `outlineDepth`. Hat keinen
 *   Fließtext (`text=''`), bildet die Outline-Hierarchie.
 * - 'separator': Section-Boundary, unverändert.
 *
 * Legacy-Typen 'heading'/'list'/'blockquote'/'hr' im alten Konstruktor-Code
 * werden weiter wie 'normal' behandelt (die echten Block-Typen leben über
 * `headingLevel`/`listType`/`blockquote`-Felder, nicht über `type`).
 */
export const PARA_TYPE = Object.freeze({
    NORMAL: 'normal',
    OUTLINE_ITEM: 'outline-item',
    SEPARATOR: 'separator',
})

/**
 * Paragraph-Klasse
 * Repräsentiert einen einzelnen Absatz im Editor.
 */
export class Paragraph {
    /**
     * @param {number|string} id - Eindeutige ID (UUID)
     * @param {string} text - Plain-Text mit Markdown-Markern (ignored bei 'outline-item')
     * @param {string} type - 'normal' | 'outline-item' | 'separator' (+ legacy 'heading'/'list'/'blockquote'/'hr')
     */
    constructor(id, text = '', type = 'normal') {
        this.id = id
        this.text = type === PARA_TYPE.OUTLINE_ITEM ? '' : text
        this.type = type
        this.note = ''              // Outline-Notiz (nur bei 'outline-item' aussagekräftig)
        this.info = ''              // Context-Info (nur bei 'normal' aussagekräftig)
        this.outlineDepth = 0       // 0-10, Gliederungstiefe (nur bei 'outline-item')
        this._gen = 0               // COW-Generation (für Copy-on-Write Undo)

        // Rich-Text Felder (Phase 1 — parallel zum Markdown-Pfad)
        this.formats = { text: [], note: [], info: [] }  // FormatSpan[] pro Feld
        this.links = { text: [], note: [], info: [] }    // LinkEntry[] pro Feld
        this.headingLevel = 0       // 0-6 (0 = kein Heading)
        this.listType = null        // null | 'bullet' | 'numbered'
        this.listLevel = 0          // 0-6
        this.listStart = 1          // Startnummer für nummerierte Listen (Default 1)
        this.blockquote = false
        this.codeBlock = false      // true = Code-Block-Absatz (raw, mono, kein AutoCorrect)
        this.locked = false         // Section Lock (nur auf Separator-Paragraphen relevant)

        // Collapse-State (siehe docs/pane-formatting-specification.md §5)
        // sectionCollapsed: nur an Separatoren wirksam — versteckt nachfolgende
        //   tiefere Separatoren und deren Textabsätze bis zur Section-Grenze.
        // activeCollapsed: nur an Outline-Items wirksam — versteckt den
        //   gesamten Outline-Subtree.
        this.sectionCollapsed = false
        this.activeCollapsed = false
    }

    // --- Typ-bewusste API (Outline-Redesign Phase 1) ---

    /** True wenn dieser Para ein reiner Fließtext-Para ist. */
    isNormal() {
        return this.type !== PARA_TYPE.OUTLINE_ITEM && this.type !== PARA_TYPE.SEPARATOR
    }
    /** True wenn dieser Para ein v4-Outline-Item ist (Strukturmarker mit Note). */
    isOutlineItem() {
        return this.type === PARA_TYPE.OUTLINE_ITEM
    }
    /** True wenn dieser Para ein Separator ist (Section-Boundary). */
    isSeparator() {
        return this.type === PARA_TYPE.SEPARATOR
    }
    /**
     * Liefert den Text-Inhalt nur, wenn der Para ihn typ-bedingt tragen darf.
     * Outline-Items haben keinen `text` → undefined. Aufrufer können damit
     * defensiv prüfen, ob Text-Zugriff überhaupt sinnvoll ist.
     */
    getText() {
        return this.isOutlineItem() ? undefined : this.text
    }
    /**
     * Liefert die Outline-Note nur an Outline-Items.
     * Normal-Paras haben im v4-Modell keine Note → undefined.
     */
    getNote() {
        return this.isOutlineItem() ? this.note : undefined
    }
    /** Outline-Tiefe nur an Outline-Items, sonst undefined. */
    getDepth() {
        return this.isOutlineItem() ? this.outlineDepth : undefined
    }

    /**
     * Erstellt eine tiefe Kopie des Paragraphs. Typ-bewusst: Outline-Items
     * kopieren nur Strukturfelder + note-Formats/Links, Normal/Separator
     * kopieren alle klassischen Felder.
     * @returns {Paragraph}
     */
    clone() {
        const p = new Paragraph(this.id, '', this.type)
        if (this.isOutlineItem()) {
            p.note = this.note
            p.outlineDepth = this.outlineDepth
            p.activeCollapsed = this.activeCollapsed
            p.formats = {
                text: [],
                note: this.formats.note.map(f => ({ ...f })),
                info: []
            }
            p.links = {
                text: [],
                note: this.links.note.map(l => ({ ...l })),
                info: []
            }
        } else {
            p.text = this.text
            p.note = this.note
            p.info = this.info
            p.outlineDepth = this.outlineDepth
            p.formats = {
                text: this.formats.text.map(f => ({ ...f })),
                note: this.formats.note.map(f => ({ ...f })),
                info: this.formats.info.map(f => ({ ...f }))
            }
            p.links = {
                text: this.links.text.map(l => ({ ...l })),
                note: this.links.note.map(l => ({ ...l })),
                info: this.links.info.map(l => ({ ...l }))
            }
            p.headingLevel = this.headingLevel
            p.listType = this.listType
            p.listLevel = this.listLevel
            p.listStart = this.listStart
            p.blockquote = this.blockquote
            p.codeBlock = this.codeBlock
            p.sectionCollapsed = this.sectionCollapsed
            p.activeCollapsed = this.activeCollapsed
        }
        p.locked = this.locked
        return p
    }
}

/**
 * Position im Editor (Paragraph + Offset)
 */
export class Position {
    /**
     * @param {number} paragraphId - ID des Paragraphs
     * @param {number} offset - Zeichen-Offset innerhalb des Paragraphs
     * @param {string} field - 'text' oder 'note'
     */
    constructor(paragraphId, offset, field = 'text') {
        this.paragraphId = paragraphId
        this.offset = offset
        this.field = field
    }

    /**
     * Prüft Gleichheit mit anderer Position.
     * @param {Position} other
     * @returns {boolean}
     */
    equals(other) {
        if (!other) return false
        return this.paragraphId === other.paragraphId
            && this.offset === other.offset
            && this.field === other.field
    }

    /**
     * Erstellt eine Kopie.
     * @returns {Position}
     */
    clone() {
        return new Position(this.paragraphId, this.offset, this.field)
    }
}

/**
 * Selection im Editor (Anchor + Cursor)
 */
export class Selection {
    /**
     * @param {Position} anchor - Startpunkt der Selection
     * @param {Position} cursor - Endpunkt der Selection (wo der Cursor ist)
     */
    constructor(anchor, cursor) {
        this.anchor = anchor
        this.cursor = cursor
        this.isWordMode = false
    }

    /**
     * Prüft ob Selection kollabiert ist (keine echte Auswahl).
     * @returns {boolean}
     */
    isCollapsed() {
        return this.anchor.equals(this.cursor)
    }

    /**
     * Gibt die "normalisierte" Selection zurück (start <= end).
     * @param {EditorModel} model - Für Paragraph-Reihenfolge
     * @returns {{start: Position, end: Position}}
     */
    getNormalized(model) {
        const anchorIndex = model.getParagraphIndex(this.anchor.paragraphId)
        const cursorIndex = model.getParagraphIndex(this.cursor.paragraphId)

        if (anchorIndex < cursorIndex) {
            return { start: this.anchor.clone(), end: this.cursor.clone() }
        } else if (anchorIndex > cursorIndex) {
            return { start: this.cursor.clone(), end: this.anchor.clone() }
        } else {
            // Gleicher Paragraph
            if (this.anchor.offset <= this.cursor.offset) {
                return { start: this.anchor.clone(), end: this.cursor.clone() }
            } else {
                return { start: this.cursor.clone(), end: this.anchor.clone() }
            }
        }
    }

    /**
     * Erstellt eine Kopie.
     * @returns {Selection}
     */
    clone() {
        const sel = new Selection(this.anchor.clone(), this.cursor.clone())
        sel.isWordMode = this.isWordMode
        return sel
    }
}

/**
 * EditorModel-Klasse
 * Zentrales Datenmodell für den Prose-Editor.
 */
export class EditorModel {
    constructor() {
        this.paragraphs = []

        // COW-Generationszähler: wird bei jedem Snapshot inkrementiert,
        // damit _mutate() erkennt welche Absätze geklont werden müssen
        this._gen = 0

        // O(1)-Lookup-Maps (lazy rebuild via _mapsStale Flag)
        this._paragraphMap = new Map()   // id → Paragraph
        this._indexMap = new Map()        // id → index
        this._mapsStale = true

        // Cursor-Position (immer vorhanden)
        this.cursor = null

        // Optionale Selection
        this.selection = null

        // Undo/Redo Stacks
        this.undoStack = []
        this.redoStack = []
        this.MAX_UNDO_STACK_SIZE = 100

        // Für Undo-Konsolidierung: zählt Zeichen seit letztem Undo-Punkt
        this.charsSinceUndoPoint = 0
        this.UNDO_CONSOLIDATION_THRESHOLD = 50

        // Undo-Konsolidierung: Tracking für Paragraph/Field-Wechsel
        this._isInConsolidationGroup = false
        this._lastUndoParagraphId = undefined
        this._lastUndoField = undefined

        // Externe Edit-Gruppe (beginEditGroup/endEditGroup): waehrend einer
        // offenen Gruppe werden _pushUndo/_consolidateUndo no-ops; ein
        // einzelner Snapshot-Restore-Eintrag (am Gruppenstart angelegt) deckt
        // die gesamte Sequenz ab. Use-Case: Diktat — viele Interim-Inserts
        // pro Utterance kollabieren zu einem Cmd+Z-Schritt.
        this._inEditGroup = false
        this._editGroupSnapshot = null

        // Differenziertes Dirty-Tracking (CLEAN → CURSOR_ONLY → PARAGRAPH → STRUCTURAL)
        this._dirtyLevel = 'STRUCTURAL'
        this._dirtyParagraphIds = new Set()
        this._allContentDirty = true  // true = alle Absaetze potenziell geaendert (Undo, Import)
        this._dirtyVersion = 0  // Inkrementiert bei jeder Model-Änderung

        // Listener-Set fuer Paragraph-Dirty-Events (Spellcheck, ggf. spaeter
        // andere Subsysteme). Wird im _markParagraphDirty() benachrichtigt,
        // damit der SpellCheckManager pro Paragraph einen Worker-Job in die
        // Queue stellen kann (debounced auf seiner Seite).
        this._paragraphDirtyListeners = new Set()

        // Remote-Apply-Marker: true, waehrend wir gerade Server-Aenderungen
        // einspielen. Persist-Pfade (saveContent/saveContentImmediately)
        // skippen dann den IndexedDB-Write — sonst wuerden wir die gerade
        // empfangenen Server-Paragraphen sofort als lokale Aenderungen
        // zurueck zum Server pushen.
        this._isApplyingRemote = false

        // Wird true wenn eine Aktion blockiert wurde (z.B. Merge nicht möglich)
        this._actionBlocked = false

        // Outline-Redesign Phase 3b — Lazy-Cursor:
        // Statt einen passiven Normal-Para temp zu mutieren (v3-Pfad), trägt der
        // Editor einen "Pending"-Cursor in einer Phantom-Note-Zeile. Erst der
        // erste Edit-Tastendruck materialisiert das Outline-Item als realen Para
        // (siehe materializePendingCursor + _autoCreatedParagraphIds für das
        // Auto-Cleanup-Bookkeeping nach Spec §4.3/§4.4).
        this._pendingCursor = null
        this._autoCreatedParagraphIds = new Set()

        // Wird true wenn Backspace/Delete an Separator blockiert wurde (für Dialog-Trigger in EditorInput)
        this._separatorBlocked = false

        // Section Lock: Erster Abschnitt (vor erstem Separator) hat keinen eigenen Separator
        this._firstSectionLocked = false

        // Selektierter Link: wird gesetzt wenn Cursor per Pfeilnavigation auf einen Link trifft
        // {paragraphId, field, dataStart, dataEnd, targetId} oder null
        this._selectedLink = null

        // Outline-Selection: selektiert einen Range von Absätzen (getrennt von Text-Selection)
        // Format: { startIndex: number, endIndex: number } (inklusive) oder null
        this._outlineSelection = null

        // Aktive Formate am Cursor (werden bei nächster Eingabe angewendet)
        this._activeFormats = new Set()

        // Initialer leerer Paragraph
        this._initEmptyDocument()
    }

    /**
     * Initialisiert ein leeres Dokument mit einem Paragraph.
     */
    _initEmptyDocument() {
        const para = new Paragraph(this._getNextId(), '')
        this.paragraphs.push(para)
        this.cursor = new Position(para.id, 0)
    }

    // ===== Rich-Text =====

    /**
     * Setzt Block-Format-Eigenschaften eines Paragraphs (RT-Modus).
     * COW-Snapshot + Undo.
     * @param {number} paraId - Paragraph-ID
     * @param {object} changes - { listType?, listLevel?, headingLevel?, blockquote?, type? }
     */
    setBlockFormat(paraId, changes) {
        const oldPara = this.getParagraph(paraId)
        if (!oldPara || oldPara.type === PARA_TYPE.SEPARATOR) return

        // COW-Snapshot
        this._gen++
        const oldState = {
            listType: oldPara.listType,
            listLevel: oldPara.listLevel,
            listStart: oldPara.listStart,
            headingLevel: oldPara.headingLevel,
            blockquote: oldPara.blockquote,
            codeBlock: oldPara.codeBlock,
            type: oldPara.type
        }

        const para = this._mutate(oldPara)
        if ('listType' in changes) para.listType = changes.listType
        if ('listLevel' in changes) para.listLevel = changes.listLevel
        if ('listStart' in changes) para.listStart = changes.listStart
        if ('headingLevel' in changes) para.headingLevel = changes.headingLevel
        if ('blockquote' in changes) para.blockquote = changes.blockquote
        if ('codeBlock' in changes) para.codeBlock = changes.codeBlock
        if ('type' in changes) para.type = changes.type

        // Structural Dirty: Absatztyp-Änderung beeinflusst Spacing der Nachbar-Absätze
        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(paraId)

        // Undo
        this._pushUndo(() => {
            const p = this._mutate(this.getParagraph(paraId))
            if (p) {
                p.listType = oldState.listType
                p.listLevel = oldState.listLevel
                p.listStart = oldState.listStart
                p.headingLevel = oldState.headingLevel
                p.blockquote = oldState.blockquote
                p.codeBlock = oldState.codeBlock
                p.type = oldState.type
                this._markStructuralDirty()
            }
        })
        this.charsSinceUndoPoint = 0
    }

    /**
     * Aktualisiert _activeFormats basierend auf der Cursor-Position.
     * Liest die Formate des Zeichens links vom Cursor.
     */
    _updateActiveFormats() {
        if (!this.cursor) {
            this._activeFormats.clear()
            return
        }
        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para || para.type === PARA_TYPE.SEPARATOR) {
            this._activeFormats.clear()
            return
        }
        const field = this.cursor.field || 'text'
        this._activeFormats = getFormatsBeforeOffset(para.formats[field], this.cursor.offset)
    }

    /**
     * Generiert eine neue eindeutige ID (UUID).
     * @returns {string}
     */
    _getNextId() {
        return generateUUID()
    }

    // Abwärtskompatibel: isDirty als Getter
    get isDirty() { return this._dirtyLevel !== 'CLEAN' }

    /** @returns {string} Aktueller Dirty-Level ('CLEAN', 'CURSOR_ONLY', 'PARAGRAPH', 'STRUCTURAL') */
    getDirtyLevel() { return this._dirtyLevel }

    /** @returns {boolean} Ob gerade ein Remote-Apply-Block laeuft (Sync-Pull, Initial-Load). */
    isApplyingRemote() { return this._isApplyingRemote }

    /**
     * Markiert den Start eines Remote-Apply-Blocks. Nutze stattdessen
     * bevorzugt `ProseEditor.applyRemoteChanges(fn)` — das sorgt auch fuer
     * den passenden View-Rebuild + markClean.
     */
    beginApplyRemote() { this._isApplyingRemote = true }

    /** Markiert das Ende eines Remote-Apply-Blocks. */
    endApplyRemote() { this._isApplyingRemote = false }

    /**
     * Markiert nur Cursor/Selection als geändert (kein Line-Rebuild nötig).
     */
    _markCursorDirty() {
        if (this._dirtyLevel === 'CLEAN') {
            this._dirtyLevel = 'CURSOR_ONLY'
        }
        // Höhere Levels (PARAGRAPH, STRUCTURAL) bleiben bestehen
    }

    /**
     * Markiert einen bestimmten Paragraph als geändert (inkrementeller Rebuild).
     * Trackt die ID immer — auch bei STRUCTURAL — damit Persistence weiss,
     * welche Absaetze sich inhaltlich geaendert haben.
     * @param {string} paragraphId
     */
    _markParagraphDirty(paragraphId) {
        if (this._dirtyLevel === 'CLEAN' || this._dirtyLevel === 'CURSOR_ONLY') {
            this._dirtyLevel = 'PARAGRAPH'
        }
        this._dirtyParagraphIds.add(paragraphId)
        this._dirtyVersion++

        // Subscriber benachrichtigen (Spellcheck-Manager debounced selbst).
        // Listener-Fehler werden geschluckt, damit ein einzelner Subscriber
        // den Edit-Hotpath nicht aufhalten kann.
        if (this._paragraphDirtyListeners.size > 0) {
            for (const fn of this._paragraphDirtyListeners) {
                try { fn(paragraphId) } catch { /* listener-Fehler ignorieren */ }
            }
        }
    }

    /**
     * Registriert einen Listener fuer Paragraph-Dirty-Events.
     * @param {(paragraphId: string) => void} fn
     * @returns {() => void} Unsubscribe-Funktion
     */
    addParagraphDirtyListener(fn) {
        this._paragraphDirtyListeners.add(fn)
        return () => this._paragraphDirtyListeners.delete(fn)
    }

    /**
     * Markiert strukturelle Änderung (Full Rebuild nötig) UND loescht
     * die Content-Dirty-IDs und setzt _allContentDirty.
     * Wird fuer Undo-Callbacks und komplexe Operationen verwendet,
     * wo wir nicht wissen welche Absaetze sich geaendert haben —
     * Persistence markiert dann sicherheitshalber alle.
     */
    _markStructuralDirty() {
        this._dirtyLevel = 'STRUCTURAL'
        this._dirtyParagraphIds.clear()
        this._allContentDirty = true
        this._mapsStale = true
        this._dirtyVersion++
    }

    /**
     * Strukturelle Aenderung ohne Content-Dirty-IDs zu loeschen.
     * Fuer Forward-Operationen: Aufrufer muss _markParagraphDirty()
     * fuer betroffene Absaetze separat aufrufen.
     */
    _markStructuralDirtyIncremental() {
        this._dirtyLevel = 'STRUCTURAL'
        this._mapsStale = true
        this._dirtyVersion++
    }

    /**
     * Rein visuelles STRUCTURAL-Dirty (Full Rebuild fuer Renderer),
     * aber KEINE Persistence-Aenderung. Fuer UI-State wie Outline-Selection,
     * der nicht in IndexedDB/Cloud gespeichert wird.
     */
    _markStructuralDirtyVisualOnly() {
        this._dirtyLevel = 'STRUCTURAL'
        this._mapsStale = true
        this._dirtyVersion++
    }

    /**
     * Abwärtskompatibel: delegiert an _markStructuralDirty().
     */
    _markDirty() {
        this._markStructuralDirty()
    }

    /**
     * COW-Schutz: Klont einen Paragraph falls er aus einer älteren Generation stammt.
     * Gibt den (ggf. geklonten) Paragraph zurück — Aufrufer MUSS den Rückgabewert verwenden.
     * @param {Paragraph} para
     * @returns {Paragraph}
     */
    _mutate(para) {
        if (!para || para._gen >= this._gen) return para
        const idx = this.getParagraphIndex(para.id)
        if (idx === -1) return para
        const clone = para.clone()
        clone._gen = this._gen
        this.paragraphs[idx] = clone
        this._mapsStale = true
        return clone
    }

    /**
     * Stellt sicher, dass die Lookup-Maps aktuell sind.
     * Lazy Rebuild: wird nur bei Bedarf aufgerufen.
     */
    _ensureMaps() {
        if (!this._mapsStale) return
        this._paragraphMap.clear()
        this._indexMap.clear()
        for (let i = 0; i < this.paragraphs.length; i++) {
            const p = this.paragraphs[i]
            this._paragraphMap.set(p.id, p)
            this._indexMap.set(p.id, i)
        }
        this._mapsStale = false
    }

    /**
     * Setzt die Dirty-Flags zurück.
     */
    markClean() {
        this._dirtyLevel = 'CLEAN'
        this._dirtyParagraphIds.clear()
        this._allContentDirty = false
    }

    // ===== Paragraph-Zugriff =====

    /**
     * Gibt einen Paragraph per ID zurück.
     * @param {number} id
     * @returns {Paragraph|null}
     */
    getParagraph(id) {
        this._ensureMaps()
        return this._paragraphMap.get(id) || null
    }

    /**
     * Gibt den Index eines Paragraphs zurück.
     * @param {number} id
     * @returns {number} -1 wenn nicht gefunden
     */
    getParagraphIndex(id) {
        this._ensureMaps()
        const idx = this._indexMap.get(id)
        return idx !== undefined ? idx : -1
    }

    /**
     * Gibt den Paragraph an einem Index zurück.
     * @param {number} index
     * @returns {Paragraph|null}
     */
    getParagraphAt(index) {
        return this.paragraphs[index] || null
    }

    /**
     * Gibt die Anzahl der Paragraphs zurück.
     * @returns {number}
     */
    getParagraphCount() {
        return this.paragraphs.length
    }

    // ===== Feld-Zugriff =====

    /**
     * Gibt den Text des angegebenen Feldes zurück.
     * @param {Paragraph} para
     * @param {string} field - 'text' oder 'note'
     * @returns {string}
     */
    _getFieldText(para, field) {
        if (field === 'info') return para.info
        return field === 'note' ? para.note : para.text
    }

    /**
     * Setzt den Text des angegebenen Feldes.
     * @param {Paragraph} para
     * @param {string} field - 'text' oder 'note'
     * @param {string} value
     */
    _setFieldText(para, field, value) {
        para = this._mutate(para)
        if (field === 'info') {
            para.info = value
        } else if (field === 'note') {
            para.note = value
        } else {
            para.text = value
        }
    }

    // ===== Cursor-Verwaltung =====

    /**
     * Setzt die Cursor-Position.
     * @param {number} paragraphId
     * @param {number} offset
     */
    setCursor(paragraphId, offset, field) {
        const para = this.getParagraph(paragraphId)
        if (!para) return

        // Feld beibehalten wenn nicht explizit angegeben
        let cursorField = field || (this.cursor ? this.cursor.field : 'text')
        // v4 Spec §2.4: Outline-Items haben nur ein note-Feld. Ein Klick in
        // die Phantom-Main-Zeile oder ein Cursor-Move aus einem Text-Feld
        // muss field='note' nutzen — sonst landet Tastatureingabe in einem
        // nicht-existierenden text-Slot und wird beim Sync verworfen.
        if (para.isOutlineItem && para.isOutlineItem() && cursorField !== 'note') {
            cursorField = 'note'
        }
        const fieldText = this._getFieldText(para, cursorField)

        // Offset auf gültigen Bereich begrenzen
        offset = Math.max(0, Math.min(offset, (fieldText || '').length))
        this.cursor = new Position(paragraphId, offset, cursorField)
        this._markCursorDirty()
    }

    /**
     * Findet den nächsten Nicht-Separator-Index in der gegebenen Richtung.
     * @param {number} startIdx - Startindex (exklusive, erster zu prüfender ist startIdx ± 1)
     * @param {1|-1} direction - 1 für vorwärts, -1 für rückwärts
     * @returns {number} Index des nächsten Nicht-Separator-Absatzes, oder -1 wenn keiner gefunden
     */
    _findNonSeparatorIndex(startIdx, direction) {
        let idx = startIdx
        const limit = direction > 0 ? this.paragraphs.length : -1
        while (idx !== limit && this.paragraphs[idx].type === PARA_TYPE.SEPARATOR) idx += direction
        if (idx === limit || this.paragraphs[idx].type === PARA_TYPE.SEPARATOR) return -1
        return idx
    }

    /**
     * Bewegt den Cursor in eine Richtung.
     * @param {'left'|'right'|'up'|'down'} direction
     * @returns {boolean} true wenn Bewegung erfolgt ist
     */
    moveCursor(direction) {
        if (!this.cursor) return false

        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return false

        const field = this.cursor.field
        const fieldText = this._getFieldText(para, field)
        const paraIndex = this.getParagraphIndex(this.cursor.paragraphId)

        switch (direction) {
            case 'left':
                if (this.cursor.offset > 0) {
                    // Surrogate Pair: ganzen Codepoint überspringen
                    this.cursor.offset -= codepointLengthBefore(fieldText, this.cursor.offset)
                    this._markCursorDirty()
                    return true
                } else if (paraIndex > 0) {
                    // Direkter Nachbar — auch Separatoren (Label-Editing)
                    const prevPara = this.paragraphs[paraIndex - 1]
                    const prevFieldText = this._getFieldText(prevPara, field)
                    this.cursor.paragraphId = prevPara.id
                    this.cursor.offset = prevFieldText.length
                    this._markCursorDirty()
                    return true
                }
                break

            case 'right':
                if (this.cursor.offset < fieldText.length) {
                    // Surrogate Pair: ganzen Codepoint überspringen
                    this.cursor.offset += codepointLengthAt(fieldText, this.cursor.offset)
                    this._markCursorDirty()
                    return true
                } else if (paraIndex < this.paragraphs.length - 1) {
                    // Direkter Nachbar — auch Separatoren (Label-Editing)
                    const nextPara = this.paragraphs[paraIndex + 1]
                    this.cursor.paragraphId = nextPara.id
                    this.cursor.offset = 0
                    this._markCursorDirty()
                    return true
                }
                break

            case 'up':
                if (paraIndex > 0) {
                    const targetIdx = this._findNonSeparatorIndex(paraIndex - 1, -1)
                    if (targetIdx < 0) break
                    const prevPara = this.paragraphs[targetIdx]
                    const prevFieldText = this._getFieldText(prevPara, field)
                    this.cursor.paragraphId = prevPara.id
                    // Offset begrenzen und auf Codepoint-Grenze snappen
                    this.cursor.offset = snapToCodepointBoundary(
                        prevFieldText, Math.min(this.cursor.offset, prevFieldText.length))
                    this._markCursorDirty()
                    return true
                }
                break

            case 'down':
                if (paraIndex < this.paragraphs.length - 1) {
                    const targetIdx = this._findNonSeparatorIndex(paraIndex + 1, 1)
                    if (targetIdx < 0) break
                    const nextPara = this.paragraphs[targetIdx]
                    const nextFieldText = this._getFieldText(nextPara, field)
                    this.cursor.paragraphId = nextPara.id
                    // Offset begrenzen und auf Codepoint-Grenze snappen
                    this.cursor.offset = snapToCodepointBoundary(
                        nextFieldText, Math.min(this.cursor.offset, nextFieldText.length))
                    this._markCursorDirty()
                    return true
                }
                break
        }

        return false
    }

    /**
     * Bewegt den Cursor zum Anfang des aktuellen Paragraphs.
     */
    moveCursorToLineStart() {
        if (this.cursor) {
            this.cursor.offset = 0
            this._markCursorDirty()
        }
    }

    /**
     * Bewegt den Cursor zum Ende des aktuellen Paragraphs.
     */
    moveCursorToLineEnd() {
        if (!this.cursor) return
        const para = this.getParagraph(this.cursor.paragraphId)
        if (para) {
            const fieldText = this._getFieldText(para, this.cursor.field)
            this.cursor.offset = fieldText.length
            this._markCursorDirty()
        }
    }

    /**
     * Bewegt den Cursor zum Dokumentanfang.
     * Im Note-Feld: zum ersten aktiven Absatz.
     */
    moveCursorToDocStart() {
        if (this.paragraphs.length > 0) {
            const field = this.cursor ? this.cursor.field : 'text'
            let target = this.paragraphs[0]
            if (field === 'note') {
                // Ersten aktiven Absatz suchen
                for (const p of this.paragraphs) {
                    if (p.isOutlineItem()) { target = p; break }
                }
            }
            // Separator überspringen
            if (target.type === PARA_TYPE.SEPARATOR) {
                for (let i = 0; i < this.paragraphs.length; i++) {
                    if (this.paragraphs[i].type !== PARA_TYPE.SEPARATOR) { target = this.paragraphs[i]; break }
                }
            }
            this.cursor = new Position(target.id, 0, field)
            this._markCursorDirty()
        }
    }

    /**
     * Bewegt den Cursor zum Dokumentende.
     * Im Note-Feld: zum letzten aktiven Absatz.
     */
    moveCursorToDocEnd() {
        if (this.paragraphs.length > 0) {
            const field = this.cursor ? this.cursor.field : 'text'
            let target = this.paragraphs[this.paragraphs.length - 1]
            if (field === 'note') {
                // Letzten aktiven Absatz suchen
                for (let i = this.paragraphs.length - 1; i >= 0; i--) {
                    if (this.paragraphs[i].isOutlineItem()) { target = this.paragraphs[i]; break }
                }
            }
            const fieldText = this._getFieldText(target, field)
            this.cursor = new Position(target.id, fieldText.length, field)
            this._markCursorDirty()
        }
    }

    // ===== Selection-Verwaltung =====

    /**
     * Startet eine Selection am Cursor.
     */
    startSelection() {
        if (this.cursor) {
            this.selection = new Selection(this.cursor.clone(), this.cursor.clone())
        }
    }

    /**
     * Erweitert die Selection zur aktuellen Cursor-Position.
     */
    extendSelection() {
        if (this.selection && this.cursor) {
            this.selection.cursor = this.cursor.clone()
            this._snapSelectionOverCollapseHosts()
            this._markCursorDirty()
        }
    }

    /**
     * Setzt eine Selection direkt.
     * @param {Position} anchor
     * @param {Position} cursor
     */
    setSelection(anchor, cursor) {
        this._outlineSelection = null  // Gegenseitiger Ausschluss
        this.selection = new Selection(anchor.clone(), cursor.clone())
        this.cursor = cursor.clone()
        this._snapSelectionOverCollapseHosts()
        this._markCursorDirty()
    }

    /**
     * Erweitert eine aktive Text-Selection so, dass ein vom Selection-Bereich
     * berührter Collapse-Host inklusive seines gesamten Subtrees atomar darin
     * liegt (spec §5.2). Anchor-Seite und Cursor-Seite werden jeweils auf die
     * äußere Grenze des Subtrees gezogen, je nach Richtung der Selection.
     */
    _snapSelectionOverCollapseHosts() {
        if (!this.selection) return
        const sel = this.selection
        const anchorIdx = this.getParagraphIndex(sel.anchor.paragraphId)
        const cursorIdx = this.getParagraphIndex(sel.cursor.paragraphId)
        if (anchorIdx < 0 || cursorIdx < 0) return
        const forward = cursorIdx >= anchorIdx
        const lowIdx = Math.min(anchorIdx, cursorIdx)
        const highIdx = Math.max(anchorIdx, cursorIdx)

        // Subtree-Range eines Hosts an Index i ermitteln
        const subtreeRangeOf = (i) => {
            const p = this.paragraphs[i]
            if (p.type === PARA_TYPE.SEPARATOR && p.sectionCollapsed) {
                let end = this.paragraphs.length - 1
                for (let j = i + 1; j < this.paragraphs.length; j++) {
                    const q = this.paragraphs[j]
                    if (q.type === PARA_TYPE.SEPARATOR && q.outlineDepth <= p.outlineDepth) {
                        end = j - 1
                        break
                    }
                }
                return { start: i, end }
            }
            if (p.isOutlineItem() && p.activeCollapsed) {
                const r = this.getSubtreeRange(i)
                return { start: r.startIndex, end: r.endIndex }
            }
            return null
        }

        // Expandiere [lowIdx, highIdx] iterativ um alle berührten Host-Subtrees
        let low = lowIdx
        let high = highIdx
        let changed = true
        while (changed) {
            changed = false
            for (let i = low; i <= high; i++) {
                const r = subtreeRangeOf(i)
                if (!r) continue
                if (r.start < low) { low = r.start; changed = true }
                if (r.end > high) { high = r.end; changed = true }
            }
        }

        if (low === lowIdx && high === highIdx) return

        // Selection-Positionen neu setzen: anchor an der "ursprünglichen" Seite,
        // cursor an der "erweiterten" Seite, Richtung bleibt erhalten.
        const startPara = this.paragraphs[low]
        const endPara = this.paragraphs[high]
        const endFieldText = endPara.type === PARA_TYPE.SEPARATOR ? (endPara.text || '') : endPara.text
        if (forward) {
            sel.anchor = new Position(startPara.id, 0, 'text')
            sel.cursor = new Position(endPara.id, endFieldText.length, 'text')
        } else {
            sel.anchor = new Position(endPara.id, endFieldText.length, 'text')
            sel.cursor = new Position(startPara.id, 0, 'text')
        }
        this.cursor = sel.cursor.clone()
    }

    /**
     * Löscht die Selection.
     */
    clearSelection() {
        if (this.selection || this._outlineSelection) {
            // Selection-Highlighting erstreckt sich über mehrere Absätze →
            // STRUCTURAL erzwingt Full-Render (Partial kennt die betroffenen Zeilen nicht)
            this._dirtyLevel = 'STRUCTURAL'
        }
        this.selection = null
        this._outlineSelection = null
    }

    /**
     * Prüft ob eine Selection vorhanden ist.
     * @returns {boolean}
     */
    hasSelection() {
        return this.selection !== null && !this.selection.isCollapsed()
    }

    // ===== Outline-Selection =====

    /**
     * Setzt eine Outline-Selection (ganzer Absatz-Range).
     * @param {number} startIndex - Erster Absatz-Index (inklusive)
     * @param {number} endIndex - Letzter Absatz-Index (inklusive)
     */
    setOutlineSelection(startIndex, endIndex) {
        this._outlineSelection = { startIndex, endIndex }
        this.selection = null  // Gegenseitiger Ausschluss mit Text-Selection
        // Cursor auf den ersten selektierten Absatz setzen (für konsistente Undo-Position)
        const para = this.paragraphs[startIndex]
        if (para) {
            this.cursor = new Position(para.id, 0)
        }
        // Rein visuell: Outline-Selection ist UI-State, keine Paragraph-Daten
        this._markStructuralDirtyVisualOnly()
    }

    /**
     * Löscht die Outline-Selection.
     */
    clearOutlineSelection() {
        if (this._outlineSelection !== null) {
            this._outlineSelection = null
            this._markStructuralDirtyVisualOnly()
        }
    }

    /**
     * Prüft ob eine Outline-Selection vorhanden ist.
     * @returns {boolean}
     */
    hasOutlineSelection() {
        return this._outlineSelection !== null
    }

    /**
     * Gibt den selektierten Text zurück.
     * @returns {string}
     */
    getSelectedText() {
        if (!this.hasSelection()) return ''

        const { start, end } = this.selection.getNormalized(this)
        const startIndex = this.getParagraphIndex(start.paragraphId)
        const endIndex = this.getParagraphIndex(end.paragraphId)
        const field = start.field || 'text'

        if (startIndex === endIndex) {
            // Selection innerhalb eines Paragraphs
            const para = this.paragraphs[startIndex]
            const fieldText = this._getFieldText(para, field)
            return fieldText.substring(start.offset, end.offset)
        }

        // Selection über mehrere Paragraphs (nur bei field='text')
        let result = []

        // Erster Paragraph (ab start.offset)
        result.push(this.paragraphs[startIndex].text.substring(start.offset))

        // Mittlere Paragraphs (komplett)
        for (let i = startIndex + 1; i < endIndex; i++) {
            result.push(this.paragraphs[i].text)
        }

        // Letzter Paragraph (bis end.offset)
        result.push(this.paragraphs[endIndex].text.substring(0, end.offset))

        return result.join('\n')
    }

    /**
     * Selektiert das ganze Dokument.
     */
    selectAll() {
        if (this.paragraphs.length === 0) return

        const field = this.cursor ? this.cursor.field : 'text'

        if (field === 'note' || field === 'info') {
            // In Notes/Info: nur den aktuellen Absatz selektieren
            const para = this.getParagraph(this.cursor.paragraphId)
            if (!para) return
            const fieldText = this._getFieldText(para, field)
            this.selection = new Selection(
                new Position(para.id, 0, field),
                new Position(para.id, fieldText.length, field)
            )
        } else {
            // In Text: aktuellen Abschnitt selektieren (Separatoren als Grenzen)
            const { startIndex, endIndex } = this.getSectionBounds(this.cursor.paragraphId)
            const firstPara = this.paragraphs[startIndex]
            const lastPara = this.paragraphs[endIndex]
            this.selection = new Selection(
                new Position(firstPara.id, 0),
                new Position(lastPara.id, lastPara.text.length)
            )
        }

        this.cursor = this.selection.cursor.clone()
        this._markCursorDirty()
    }

    // ===== Text-Manipulation =====

    /**
     * Fügt Text an der Cursor-Position ein.
     * @param {string} text - Der einzufügende Text
     * @returns {Function} Undo-Funktion
     */
    insertText(text) {
        if (!this.cursor) return () => {}

        // Separator-Guard: keine Texteingabe in Separator
        if (this.isSeparator(this.cursor.paragraphId)) {
            this._actionBlocked = true
            return () => {}
        }

        // Phantom-Main-Zeile (Cursor auf Outline-Item, field='text'):
        // erst den Normal-Para materialisieren — nie in den text-Slot
        // des Items schreiben (Lesson 103).
        this._materializePhantomTextTarget()

        // Bei Selection: erst löschen
        if (this.hasSelection()) {
            this.deleteSelection()
        }

        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return () => {}

        const field = this.cursor.field
        const offset = this.cursor.offset
        const oldFieldText = this._getFieldText(para, field)

        // Text einfügen
        const newFieldText = oldFieldText.slice(0, offset) + text + oldFieldText.slice(offset)
        this._setFieldText(para, field, newFieldText)
        this.cursor.offset += text.length

        // Rich-Text: Spans/Links anpassen und aktive Formate anwenden
        const freshPara = this.getParagraph(para.id)
        const oldFormats = freshPara.formats[field].map(f => ({ ...f }))
        const oldLinks = freshPara.links[field].map(l => ({ ...l }))
        freshPara.formats[field] = adjustSpansForInsert(freshPara.formats[field], offset, text.length)
        freshPara.links[field] = adjustLinksForInsert(freshPara.links[field], offset, text.length)
        // Aktive Formate auf den eingefügten Text anwenden
        for (const type of this._activeFormats) {
            freshPara.formats[field] = extendOrCreateSpan(freshPara.formats[field], offset, offset + text.length, type)
        }

        this._markParagraphDirty(para.id)

        // Bei Paragraph- oder Field-Wechsel neue Konsolidierungsgruppe erzwingen
        const currentField = this.cursor.field || 'text'
        if ((this._lastUndoParagraphId !== undefined && this._lastUndoParagraphId !== para.id) ||
            (this._lastUndoField !== undefined && this._lastUndoField !== currentField)) {
            this._isInConsolidationGroup = false
            this.charsSinceUndoPoint = 0
        }
        this._lastUndoParagraphId = para.id
        this._lastUndoField = currentField

        // Undo-Konsolidierung
        this.charsSinceUndoPoint += text.length

        // Undo-Funktion
        const undoFn = () => {
            this._setFieldText(para, field, oldFieldText)
            const p = this.getParagraph(para.id)
            if (p) {
                p.formats[field] = oldFormats
                p.links[field] = oldLinks
            }
            this.cursor = new Position(para.id, offset, field)
            this._markParagraphDirty(para.id)
        }

        // Bei Schwellenwert: neuen Undo-Punkt erstellen
        if (this.charsSinceUndoPoint >= this.UNDO_CONSOLIDATION_THRESHOLD) {
            this._pushUndo(undoFn)
            this.charsSinceUndoPoint = 0
        } else {
            // Konsolidiere mit letztem Undo wenn möglich
            this._consolidateUndo(undoFn)
        }

        return undoFn
    }

    /**
     * Fügt Text an einer bestimmten Position ein (für Undo/Redo).
     * @param {number} paragraphId
     * @param {number} offset
     * @param {string} text
     */
    insertTextAt(paragraphId, offset, text, field = 'text') {
        const para = this.getParagraph(paragraphId)
        if (!para) return

        const fieldText = this._getFieldText(para, field)
        this._setFieldText(para, field, fieldText.slice(0, offset) + text + fieldText.slice(offset))
        this._markParagraphDirty(paragraphId)
    }

    /**
     * Fügt Text ein (auch mehrzeilig) als EINE atomare Undo-Operation.
     * Verwendet für Paste (Cmd-V), damit ein Undo reicht.
     * @param {string} text - Der einzufügende Text (kann Zeilenumbrüche enthalten)
     * @returns {Function} Undo-Funktion
     */
    pasteText(text, outlineInfo = null, paragraphTexts = null) {
        if (!this.cursor || !text) return () => {}
        this._actionBlocked = false

        // Phantom-Main-Zeile: nie in den text-Slot eines Outline-Items
        // schreiben (Lesson 103). Defense-in-Depth — der Input-Pfad
        // materialisiert bereits in handlePaste (vor der Span-Verankerung).
        this._materializePhantomTextTarget()

        // Bei Note/Info-Feld: Multi-Paragraph-Paste wird geflattet
        // (Newlines → Spaces, Spec "Zentrales Clipboard") — eingefuegt wird
        // trotzdem, _actionBlocked signalisiert dem Input nur den Ping.
        // Die RT-Nachverarbeitung des geflatteten Inhalts uebernimmt
        // EditorInput.handlePaste (System-Review 2026-06-10).
        if (this.cursor.field === 'note' || this.cursor.field === 'info') {
            if (text.includes('\n')) {
                this._actionBlocked = true
            }
            const flatText = text.replace(/\n/g, ' ')
            return this.insertText(flatText)
        }

        // Für Undo: COW-Snapshot VOR der Operation
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()
        const oldSelection = this.selection ? this.selection.clone() : null

        // Bei Selection: erst löschen (ohne eigenen Undo-Eintrag — der
        // COW-Snapshot oben deckt alles ab). Ueber den gemeinsamen Kern,
        // damit Attribut-Transfer, Span-Merge, Cleanup und Tiefenkorrektur
        // nicht gegenueber deleteRange driften (System-Review 2026-06-10:
        // die fruehere Inline-Kopie liess beim Voll-Absatz-Delete die
        // Absatzattribute des End-Paras und _correctOutlineDepths aus).
        if (this.hasSelection()) {
            const { start, end } = this.selection.getNormalized(this)
            this._deleteRangeCore(start, end)
            this.cursor = start.clone()
            this.selection = null
        }

        // Text einfügen — paragraphTexts bewahrt Soft-Linebreaks innerhalb der Absätze
        const lines = paragraphTexts || text.split('\n')
        let para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return () => {}

        if (lines.length === 1) {
            // Einzeilig: einfach einfügen
            const offset = this.cursor.offset
            para = this._mutate(para)
            // RT: bestehende Spans verschieben
            if (para.type !== PARA_TYPE.SEPARATOR) {
                const f = this.cursor.field || 'text'
                para.formats[f] = adjustSpansForInsert(para.formats[f], offset, text.length)
                para.links[f] = adjustLinksForInsert(para.links[f], offset, text.length)
            }
            para.text = para.text.slice(0, offset) + text + para.text.slice(offset)
            this.cursor.offset += text.length
        } else {
            // Mehrzeilig: Text aufteilen und neue Paragraphs erstellen
            const paraIndex = this.getParagraphIndex(para.id)
            const offset = this.cursor.offset
            const textAfterCursor = para.text.slice(offset)

            // RT: Spans splitten am Offset
            let rightFormats = [], rightLinks = []
            if (para.type !== PARA_TYPE.SEPARATOR) {
                const f = this.cursor.field || 'text'
                const splitF = splitSpansAtOffset(para.formats[f], offset)
                const splitL = splitLinksAtOffset(para.links[f], offset)
                rightFormats = splitF.right
                rightLinks = splitL.right
                para = this._mutate(para)
                para.formats[f] = splitF.left
                para.links[f] = splitL.left
            } else {
                para = this._mutate(para)
            }

            // Erste Zeile an aktuellen Paragraph anhängen
            para.text = para.text.slice(0, offset) + lines[0]

            // RT: Spans für eingefügte erste Zeile — left-Spans sind bereits korrekt
            // (enden bei offset), lines[0] wird angehängt → keine Anpassung nötig

            // Bei offset === 0: Original-Attribute sichern, bevor sie überschrieben werden.
            // Der Original-Text wandert komplett in lastPara → lastPara erbt die Attribute.
            // Ziel-Tiefe merken: alle eingefügten Absätze erben die Tiefe der Einfügestelle
            const targetDepth = para.outlineDepth

            let originalParaInfo = null
            if (offset === 0) {
                originalParaInfo = {
                    type: para.type, headingLevel: para.headingLevel,
                    listType: para.listType, listLevel: para.listLevel,
                    listStart: para.listStart, blockquote: para.blockquote,
                    codeBlock: para.codeBlock
                }
            }

            // Outline-Info für Zeile 0: Wenn Cursor am Absatz-Anfang steht,
            // wird der Absatz de facto durch den eingefügten Inhalt ersetzt
            const applyLine0 = outlineInfo && outlineInfo[0] && offset === 0
            if (applyLine0) {
                this._applyParagraphInfo(para, outlineInfo[0])
            }

            // Mittlere Zeilen als neue Paragraphs
            const newParagraphs = []
            for (let i = 1; i < lines.length - 1; i++) {
                const newPara = new Paragraph(this._getNextId(), lines[i])
                // Block-Format übertragen wenn vorhanden (Index i = Zeile i im Paste)
                if (outlineInfo && outlineInfo[i]) {
                    this._applyParagraphInfo(newPara, outlineInfo[i])
                }
                // Outline-Struktur: flach mit Ziel-Tiefe. Paste in v4 erzeugt
                // ausschliesslich Normal-Paras — Outline-Items werden nicht aus
                // Text-Paste gebaut (eigenes Strukturkonzept).
                newPara.outlineDepth = targetDepth
                newParagraphs.push(newPara)
            }

            // Letzte Zeile + Rest des ursprünglichen Texts
            const lastLine = lines[lines.length - 1]
            const lastPara = new Paragraph(this._getNextId(), lastLine + textAfterCursor)
            // RT: rechte Spans zum letzten Absatz (verschoben um lastLine.length)
            {
                const f = this.cursor.field || 'text'
                lastPara.formats[f] = shiftSpans(rightFormats, lastLine.length)
                lastPara.links[f] = shiftLinks(rightLinks, lastLine.length)
            }
            // Block-Format für letzte Zeile:
            // Explizite Clipboard-Info hat Vorrang, sonst Original-Attribute erben
            // (bei offset === 0 wandert der gesamte Original-Text in lastPara)
            const lastIdx = lines.length - 1
            if (outlineInfo && outlineInfo[lastIdx]) {
                this._applyParagraphInfo(lastPara, outlineInfo[lastIdx])
            } else if (originalParaInfo) {
                this._applyParagraphInfo(lastPara, originalParaInfo)
            }
            // Outline-Struktur: flach mit Ziel-Tiefe (siehe oben).
            lastPara.outlineDepth = targetDepth
            newParagraphs.push(lastPara)

            // Neue Paragraphs einfügen (Outline-Info wird 1:1 übernommen,
            // keine Tiefenkorrektur am eingefügten Block)
            this.paragraphs.splice(paraIndex + 1, 0, ...newParagraphs)
            this._mapsStale = true

            // Nur Absätze NACH dem eingefügten Block korrigieren (nicht den Block selbst)
            this._correctOutlineDepths(paraIndex + 1 + newParagraphs.length)

            // Cursor ans Ende der eingefügten Zeile (vor textAfterCursor)
            this.cursor = new Position(lastPara.id, lastLine.length)
        }

        this._markStructuralDirty()

        // EINE Undo-Funktion für die gesamte Paste-Operation
        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = oldSelection
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Löscht einen Bereich.
     * @param {Position} start
     * @param {Position} end
     * @returns {Function} Undo-Funktion
     */
    deleteRange(start, end) {
        const startIndex = this.getParagraphIndex(start.paragraphId)
        const endIndex = this.getParagraphIndex(end.paragraphId)

        if (startIndex === -1 || endIndex === -1) return () => {}

        // Für Undo: COW-Snapshot (inkl. Selection für Cut-Undo)
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()
        const oldSelection = this.selection ? this.selection.clone() : null
        const field = start.field || 'text'

        this._deleteRangeCore(start, end)

        // Cursor setzen
        this.cursor = start.clone()
        // Cursor-Offset anpassen falls er durch Cleanup verschoben wurde
        const cursorFieldText = this._getFieldText(this.getParagraph(start.paragraphId), field)
        if (cursorFieldText !== undefined && this.cursor.offset > cursorFieldText.length) {
            this.cursor.offset = cursorFieldText.length
        }

        // Undo-Funktion: Selection wiederherstellen für bessere UX
        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = oldSelection
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Kern des Range-Löschens OHNE Undo-/COW-Snapshot-Verwaltung und ohne
     * Cursor-Handling — geteilt von deleteRange (kanonisch) und pasteText
     * (Selection-Delete vor dem Einfügen), damit Attribut-Transfer,
     * Span-Merge, Cleanup und Tiefenkorrektur nicht driften
     * (System-Review 2026-06-10). Erwartet, dass der Aufrufer `_gen`
     * gebumpt und den COW-Snapshot gezogen hat.
     *
     * @param {Position} start - normalisierter Range-Anfang
     * @param {Position} end - normalisiertes Range-Ende
     * @private
     */
    _deleteRangeCore(start, end) {
        const startIndex = this.getParagraphIndex(start.paragraphId)
        const endIndex = this.getParagraphIndex(end.paragraphId)
        const field = start.field || 'text'

        if (startIndex === endIndex) {
            // Löschen innerhalb eines Paragraphs
            const para = this.paragraphs[startIndex]
            const fieldText = this._getFieldText(para, field)
            this._setFieldText(para, field, fieldText.slice(0, start.offset) + fieldText.slice(end.offset))
            // Nach COW-Mutation: frische Referenz holen für Cleanup
            const freshPara = this.getParagraph(para.id)
            freshPara.formats[field] = adjustSpansForDeleteRange(freshPara.formats[field], start.offset, end.offset)
            freshPara.links[field] = adjustLinksForDeleteRange(freshPara.links[field], start.offset, end.offset)
            this._cleanupEmptyFormatSpans(freshPara, field)
        } else {
            // Löschen über mehrere Paragraphs (nur bei field='text')
            let startPara = this._mutate(this.paragraphs[startIndex])
            const endPara = this.paragraphs[endIndex]

            // Erster Para: alles ab start.offset löschen
            const startLen = startPara.text.length
            startPara.formats.text = adjustSpansForDeleteRange(startPara.formats.text, start.offset, startLen)
            startPara.links.text = adjustLinksForDeleteRange(startPara.links.text, start.offset, startLen)
            // Letzter Para: alles vor end.offset löschen, dann ans Ende des ersten anhängen
            const endFormats = adjustSpansForDeleteRange(endPara.formats.text, 0, end.offset)
            const endLinks = adjustLinksForDeleteRange(endPara.links.text, 0, end.offset)
            // Verschieben um start.offset (Anhängepunkt)
            const shiftedFormats = shiftSpans(endFormats, start.offset)
            const shiftedLinks = shiftLinks(endLinks, start.offset)
            startPara.formats.text = mergeAdjacentSpans([...startPara.formats.text, ...shiftedFormats])
            startPara.links.text = [...startPara.links.text, ...shiftedLinks]

            // Ersten und letzten Paragraph zusammenführen
            startPara.text = startPara.text.slice(0, start.offset) + endPara.text.slice(end.offset)

            // Wenn startPara komplett gelöscht wird (start.offset === 0),
            // Absatzattribute vom endPara übernehmen — der überlebende Text stammt von dort
            if (start.offset === 0) {
                startPara.type = endPara.type
                startPara.headingLevel = endPara.headingLevel
                startPara.listType = endPara.listType
                startPara.listLevel = endPara.listLevel
                startPara.listStart = endPara.listStart
                startPara.blockquote = endPara.blockquote
                startPara.outlineDepth = endPara.outlineDepth
                startPara.note = endPara.note
                startPara.info = endPara.info
                startPara.formats.note = endPara.formats.note.map(f => ({ ...f }))
                startPara.formats.info = endPara.formats.info.map(f => ({ ...f }))
                startPara.links.note = endPara.links.note.map(l => ({ ...l }))
                startPara.links.info = endPara.links.info.map(l => ({ ...l }))
            }

            // Leere Format-Spans aufräumen (WYSIWYG)
            this._cleanupEmptyFormatSpans(startPara, 'text')

            // Mittlere Paragraphs entfernen
            this.paragraphs.splice(startIndex + 1, endIndex - startIndex)
            this._mapsStale = true

            // Outline-Tiefen korrigieren — bei Property-Transfer ab startIndex,
            // damit auch die übernommene Tiefe validiert wird
            this._correctOutlineDepths(start.offset === 0 ? startIndex : startIndex + 1)
        }

        // Dirty-Level: Single-Paragraph braucht nur PARAGRAPH, Multi braucht STRUCTURAL
        // (Zeilenanzahl-Änderungen werden durch _hadLineDelta in EditorView abgefangen)
        if (startIndex === endIndex) {
            this._markParagraphDirty(start.paragraphId)
        } else {
            this._markStructuralDirtyIncremental()
            this._markParagraphDirty(start.paragraphId)
        }
    }

    /**
     * Löscht die aktuelle Selection.
     * @returns {string} Der gelöschte Text
     */
    deleteSelection() {
        if (!this.hasSelection()) return ''

        const text = this.getSelectedText()
        const { start, end } = this.selection.getNormalized(this)

        this.deleteRange(start, end)
        this.selection = null

        return text
    }

    /**
     * Ersetzt einen Text-Bereich in einem einzelnen Paragraph atomar.
     * Ein einziger COW-Snapshot, ein einziger Undo-Push — damit Cmd+Z die
     * Replace-Operation in einem Schritt rueckgaengig macht (anders als
     * `setSelection` + `insertText`, was zwei Undo-Eintraege erzeugt).
     *
     * Aktueller Use-Case: Spellcheck-Replace im SpellCheckMenu. Span- und
     * Link-Anpassung folgt der Standard-Logik (delete-range → shift-insert),
     * Cursor steht nach dem Replace am Ende des neuen Texts.
     *
     * @param {string} paragraphId
     * @param {string} field         - 'text' | 'note' | 'info'
     * @param {number} start         - Start-Offset (inklusive) im aktuellen Feld
     * @param {number} end           - End-Offset (exklusive) im aktuellen Feld
     * @param {string} newText
     */
    replaceTextRange(paragraphId, field, start, end, newText) {
        const para = this.getParagraph(paragraphId)
        if (!para) return
        if (this.isSeparator(paragraphId)) return
        const safeField = field === 'note' || field === 'info' ? field : 'text'
        const oldFieldText = this._getFieldText(para, safeField)
        if (oldFieldText === undefined) return
        const lo = Math.max(0, Math.min(start, end, oldFieldText.length))
        const hi = Math.max(lo, Math.min(Math.max(start, end), oldFieldText.length))

        // COW-Snapshot — ausreichend fuer single-field-replace, weil wir
        // nur formats[field], links[field] und den Feldtext aendern.
        this._gen++
        const oldFieldTextSnapshot = oldFieldText
        const oldFormats = para.formats[safeField].map(f => ({ ...f }))
        const oldLinks = para.links[safeField].map(l => ({ ...l }))
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null

        const insertedText = newText || ''
        const updatedText = oldFieldTextSnapshot.slice(0, lo) + insertedText + oldFieldTextSnapshot.slice(hi)
        this._setFieldText(para, safeField, updatedText)

        // Format-Erhalt: alle Format-Typen sammeln, die irgendwo im
        // ersetzten Bereich [lo, hi) aktiv waren. Sonst wuerde z.B. Bold
        // verloren gehen, wenn der Span exakt das fehlerhafte Wort
        // abdeckt (Spellcheck-Replace: adjustSpansForDeleteRange entfernt
        // Spans, die komplett innerhalb des Loesch-Bereichs liegen).
        // Nach delete+insert decken wir mit extendOrCreateSpan jeden vorher
        // aktiven Format-Typ erneut den Ersatztext ab — mergeAdjacentSpans
        // im Helper kollabiert Ueberlappungen mit erhaltenen Spans.
        const formatsInRange = new Set()
        for (const s of para.formats[safeField]) {
            if (s.start < hi && s.end > lo) formatsInRange.add(s.type)
        }

        // Span-/Link-Adjustment: erst delete-range, dann shift-insert um den
        // Insert-Length-Delta — damit decken wir Format-Spans und Links ab,
        // die vor/nach/ueber den ersetzten Bereich gehen.
        const fresh = this.getParagraph(paragraphId)
        const afterDeleteFormats = adjustSpansForDeleteRange(fresh.formats[safeField], lo, hi)
        fresh.formats[safeField] = adjustSpansForInsert(afterDeleteFormats, lo, insertedText.length)
        // Vorher aktive Formate auf den Ersatztext anwenden (siehe oben).
        if (insertedText.length > 0) {
            for (const type of formatsInRange) {
                fresh.formats[safeField] = extendOrCreateSpan(
                    fresh.formats[safeField], lo, lo + insertedText.length, type
                )
            }
        }
        const afterDeleteLinks = adjustLinksForDeleteRange(fresh.links[safeField], lo, hi)
        fresh.links[safeField] = adjustLinksForInsert(afterDeleteLinks, lo, insertedText.length)
        this._cleanupEmptyFormatSpans(fresh, safeField)

        // Cursor ans Ende des ersetzten Texts; aktive Selection aufloesen.
        this.cursor = new Position(paragraphId, lo + insertedText.length, safeField)
        this.selection = null

        this._markParagraphDirty(paragraphId)

        this._pushUndo(() => {
            const p = this._mutate(this.getParagraph(paragraphId))
            if (!p) return
            this._setFieldText(p, safeField, oldFieldTextSnapshot)
            p.formats[safeField] = oldFormats
            p.links[safeField] = oldLinks
            this.cursor = oldCursor
            this.selection = oldSelection
            this._markParagraphDirty(paragraphId)
        })
        // Konsolidierungs-Gruppe brechen — der naechste insertText soll keinen
        // gemeinsamen Undo-Punkt mit dem Spellcheck-Replace bilden.
        this._resetUndoConsolidation()
    }

    /**
     * Cut-Operation: Text löschen, aber aktive Outline-Absätze behalten (Text leeren).
     * Passive Absätze werden entfernt, aktive bleiben als leere Gliederungspunkte.
     */
    cutRange(start, end) {
        const startIndex = this.getParagraphIndex(start.paragraphId)
        let endIndex = this.getParagraphIndex(end.paragraphId)
        if (startIndex === -1 || endIndex === -1) return

        // end.offset === 0 bei Multi-Paragraph: der Folgeabsatz ist nicht selektiert
        if (startIndex !== endIndex && end.offset === 0) {
            endIndex--
            end = new Position(this.paragraphs[endIndex].id, this.paragraphs[endIndex].text.length)
        }

        // Single-Paragraph
        if (startIndex === endIndex) {
            const para = this.paragraphs[startIndex]
            const fullySelected = start.offset === 0 && end.offset === para.text.length

            if (fullySelected && !para.isOutlineItem() && this.paragraphs.length > 1) {
                // Passiver Absatz komplett selektiert: Absatz entfernen
                this._gen++
                const oldParagraphs = this.paragraphs.slice()
                const oldCursor = this.cursor.clone()
                const oldSelection = this.selection ? this.selection.clone() : null

                this.paragraphs.splice(startIndex, 1)
                this._mapsStale = true
                if (this.paragraphs.length > 0 && this.paragraphs[0].type === PARA_TYPE.SEPARATOR) {
                    const emptyPara = new Paragraph(this._getNextId(), '')
                    emptyPara._gen = this._gen
                    this.paragraphs.splice(0, 0, emptyPara)
                }
                this._ensureSectionInvariant()
                // Cursor auf nächsten Nicht-Separator setzen (Indizes können durch Healing verschoben sein)
                let nextIdx = Math.min(startIndex, this.paragraphs.length - 1)
                while (nextIdx < this.paragraphs.length && this.paragraphs[nextIdx].type === PARA_TYPE.SEPARATOR) nextIdx++
                if (nextIdx >= this.paragraphs.length) {
                    nextIdx = Math.max(0, startIndex - 1)
                    while (nextIdx > 0 && this.paragraphs[nextIdx].type === PARA_TYPE.SEPARATOR) nextIdx--
                }
                this.cursor = new Position(this.paragraphs[nextIdx].id, 0)
                this.selection = null
                this._correctOutlineDepths(nextIdx)
                this._markStructuralDirty()

                const undoFn = () => {
                    this.paragraphs = oldParagraphs
                    this.cursor = oldCursor
                    this.selection = oldSelection
                    this._markStructuralDirty()
                }
                this._pushUndo(undoFn)
                this.charsSinceUndoPoint = 0
            } else {
                // Teilweise selektiert oder aktiver Absatz: deleteRange (Text leeren/kürzen)
                this.deleteRange(start, end)
                this.selection = null
            }
            return
        }

        // Multi-Paragraph: COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()
        const oldSelection = this.selection ? this.selection.clone() : null

        const startPara = this.paragraphs[startIndex]
        const endPara = this.paragraphs[endIndex]
        const startFullySelected = start.offset === 0
        const endFullySelected = end.offset === endPara.text.length

        // Sammle Indizes der zu löschenden passiven Absätze
        const toRemove = []

        // Erster Absatz
        if (startFullySelected) {
            if (startPara.isOutlineItem()) {
                // Aktiv: Text leeren, Format zurücksetzen, Absatz behalten
                const p = this._mutate(startPara)
                p.text = ''
                p.formats.text = []
                p.links.text = []
                p.type = 'normal'
                p.listType = null
                p.listLevel = 0
                p.blockquote = false
                p.codeBlock = false
            } else {
                toRemove.push(startIndex)
            }
        } else {
            // Teilweise selektiert: Rest-Text behalten
            const p = this._mutate(startPara)
            p.formats.text = adjustSpansForDeleteRange(p.formats.text, start.offset, p.text.length)
            p.links.text = adjustLinksForDeleteRange(p.links.text, start.offset, p.text.length)
            p.text = p.text.slice(0, start.offset)
        }

        // Mittlere Absätze
        for (let i = startIndex + 1; i < endIndex; i++) {
            const para = this.paragraphs[i]
            if (para.isOutlineItem()) {
                const p = this._mutate(para)
                p.text = ''
                p.formats.text = []
                p.links.text = []
                p.type = 'normal'
                p.listType = null
                p.listLevel = 0
                p.blockquote = false
                p.codeBlock = false
            } else {
                toRemove.push(i)
            }
        }

        // Letzter Absatz
        if (endFullySelected) {
            if (endPara.isOutlineItem()) {
                // Aktiv: Text leeren, Format zurücksetzen, Absatz behalten
                const p = this._mutate(endPara)
                p.text = ''
                p.formats.text = []
                p.links.text = []
                p.type = 'normal'
                p.listType = null
                p.listLevel = 0
                p.blockquote = false
                p.codeBlock = false
            } else {
                toRemove.push(endIndex)
            }
        } else {
            // Teilweise: Rest-Text (nach Selection) an letzten überlebenden Absatz davor anhängen
            const restText = endPara.text.slice(end.offset)
            const restFormats = adjustSpansForDeleteRange(endPara.formats.text, 0, end.offset)
            const restLinks = adjustLinksForDeleteRange(endPara.links.text, 0, end.offset)

            // Finde letzten überlebenden Absatz VOR oder gleich endIndex
            let targetIdx = -1
            for (let i = endIndex - 1; i >= startIndex; i--) {
                if (!toRemove.includes(i)) { targetIdx = i; break }
            }

            if (targetIdx >= 0) {
                const target = this._mutate(this.paragraphs[targetIdx])
                const appendOffset = target.text.length
                const shifted = shiftSpans(restFormats, appendOffset)
                const shiftedLinks = shiftLinks(restLinks, appendOffset)
                target.formats.text = mergeAdjacentSpans([...target.formats.text, ...shifted])
                target.links.text = [...target.links.text, ...shiftedLinks]
                    .sort((a, b) => a.offset - b.offset)
                target.text += restText
            }
            toRemove.push(endIndex)
        }

        // Passive Absätze entfernen (von hinten nach vorne, damit Indizes stimmen)
        for (let i = toRemove.length - 1; i >= 0; i--) {
            this.paragraphs.splice(toRemove[i], 1)
        }
        if (toRemove.length > 0) this._mapsStale = true

        // Cursor positionieren: auf den ersten überlebenden Absatz
        let cursorParaIdx = startIndex
        if (toRemove.includes(startIndex)) {
            // startPara wurde entfernt — finde nächsten überlebenden
            cursorParaIdx = Math.min(startIndex, this.paragraphs.length - 1)
        }
        if (cursorParaIdx >= this.paragraphs.length) cursorParaIdx = this.paragraphs.length - 1
        const cursorPara = this.paragraphs[cursorParaIdx]
        const cursorOffset = startFullySelected ? 0 : Math.min(start.offset, cursorPara.text.length)
        this.cursor = new Position(cursorPara.id, cursorOffset)
        this.selection = null

        // Outline-Tiefen korrigieren
        this._correctOutlineDepths(cursorParaIdx)

        // Abschnittsinvariante: kein leerer Abschnitt ohne Absatz
        // Dokumentanfang: wenn erster Absatz ein Separator ist, leeren Absatz davor einfügen
        if (this.paragraphs.length > 0 && this.paragraphs[0].type === PARA_TYPE.SEPARATOR) {
            const emptyPara = new Paragraph(this._getNextId(), '')
            emptyPara._gen = this._gen
            this.paragraphs.splice(0, 0, emptyPara)
            this._mapsStale = true
        }
        this._ensureSectionInvariant()

        // Cursor könnte durch eingefügten Absatz verschoben sein — neu positionieren
        cursorParaIdx = Math.min(cursorParaIdx, this.paragraphs.length - 1)
        if (this.paragraphs[cursorParaIdx].type === PARA_TYPE.SEPARATOR) {
            // Cursor auf den gerade eingefügten leeren Absatz setzen
            const healedIdx = Math.min(cursorParaIdx + 1, this.paragraphs.length - 1)
            if (this.paragraphs[healedIdx].type !== PARA_TYPE.SEPARATOR) {
                this.cursor = new Position(this.paragraphs[healedIdx].id, 0)
            }
        }

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = oldSelection
            this._markStructuralDirty()
        }
        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0
    }

    /**
     * Löscht ein Zeichen vor dem Cursor (Backspace).
     * @returns {Function} Undo-Funktion
     */
    deleteCharBefore() {
        if (!this.cursor) return () => {}
        this._actionBlocked = false
        this._separatorBlocked = false

        // Bei Selection: Selection löschen
        if (this.hasSelection()) {
            this.deleteSelection()
            return () => {}
        }

        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return () => {}

        const field = this.cursor.field

        if (this.cursor.offset > 0) {
            // Zeichen im aktuellen Feld löschen (Surrogate Pair: ganzen Codepoint)
            const oldFieldText = this._getFieldText(para, field)
            const offset = this.cursor.offset
            const cpLen = codepointLengthBefore(oldFieldText, offset)
            this._setFieldText(para, field, oldFieldText.slice(0, offset - cpLen) + oldFieldText.slice(offset))
            this.cursor.offset -= cpLen
            // Nach COW-Mutation: frische Referenz holen für Cleanup
            const freshPara = this.getParagraph(para.id)
            const savedFormats = freshPara.formats[field].map(f => ({ ...f }))
            const savedLinks = freshPara.links[field].map(l => ({ ...l }))
            freshPara.formats[field] = adjustSpansForDeleteRange(freshPara.formats[field], offset - cpLen, offset)
            freshPara.links[field] = adjustLinksForDeleteRange(freshPara.links[field], offset - cpLen, offset)
            this._cleanupEmptyFormatSpans(freshPara, field)
            this._markParagraphDirty(para.id)

            const undoFn = () => {
                this._setFieldText(freshPara, field, oldFieldText)
                const p = this.getParagraph(para.id)
                if (p) { p.formats[field] = savedFormats; p.links[field] = savedLinks }
                this.cursor = new Position(para.id, offset, field)
                this._markParagraphDirty(para.id)
            }
            this._pushUndo(undoFn)
            return undoFn
        } else {
            // Am Anfang: Merge mit vorherigem Absatz
            const paraIndex = this.getParagraphIndex(this.cursor.paragraphId)
            if (paraIndex > 0) {
                if (field === 'info') {
                    this._actionBlocked = true
                } else if (field === 'note') {
                    // Note-Merge: Vorherigen aktiven Absatz suchen
                    const prevActiveIdx = this._findPreviousActiveParagraph(paraIndex)
                    if (prevActiveIdx < 0) {
                        this._actionBlocked = true
                    } else if (this._isZuordnungsbereichEmptyInMain(paraIndex)
                            || this._isZuordnungsbereichEmptyInMain(prevActiveIdx)) {
                        return this._mergeOutlineGroups(prevActiveIdx, paraIndex)
                    } else {
                        this._actionBlocked = true
                    }
                } else {
                    // Separator-Guard: Vorgänger ist Separator → blockieren
                    if (this.paragraphs[paraIndex - 1].type === PARA_TYPE.SEPARATOR) {
                        this._separatorBlocked = true
                        return () => {}
                    }
                    return this.joinParagraphs(this.paragraphs[paraIndex - 1].id, para.id)
                }
            }
        }

        return () => {}
    }

    /**
     * Löscht ein Zeichen nach dem Cursor (Delete).
     * @returns {Function} Undo-Funktion
     */
    deleteCharAfter() {
        if (!this.cursor) return () => {}
        this._actionBlocked = false
        this._separatorBlocked = false

        // Bei Selection: Selection löschen
        if (this.hasSelection()) {
            this.deleteSelection()
            return () => {}
        }

        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return () => {}

        const field = this.cursor.field
        const fieldText = this._getFieldText(para, field)

        if (this.cursor.offset < fieldText.length) {
            // Zeichen im aktuellen Feld löschen (Surrogate Pair: ganzen Codepoint)
            const oldFieldText = fieldText
            const offset = this.cursor.offset
            const cpLen = codepointLengthAt(oldFieldText, offset)
            this._setFieldText(para, field, oldFieldText.slice(0, offset) + oldFieldText.slice(offset + cpLen))
            // Nach COW-Mutation: frische Referenz holen für Cleanup
            const freshPara = this.getParagraph(para.id)
            const savedFormats = freshPara.formats[field].map(f => ({ ...f }))
            const savedLinks = freshPara.links[field].map(l => ({ ...l }))
            freshPara.formats[field] = adjustSpansForDeleteRange(freshPara.formats[field], offset, offset + cpLen)
            freshPara.links[field] = adjustLinksForDeleteRange(freshPara.links[field], offset, offset + cpLen)
            this._cleanupEmptyFormatSpans(freshPara, field)
            this._markParagraphDirty(para.id)

            const undoFn = () => {
                this._setFieldText(freshPara, field, oldFieldText)
                const p = this.getParagraph(para.id)
                if (p) { p.formats[field] = savedFormats; p.links[field] = savedLinks }
                this._markParagraphDirty(para.id)
            }
            this._pushUndo(undoFn)
            return undoFn
        } else {
            // Am Ende: Merge mit nächstem Absatz
            const paraIndex = this.getParagraphIndex(this.cursor.paragraphId)
            if (paraIndex < this.paragraphs.length - 1) {
                if (field === 'info') {
                    this._actionBlocked = true
                } else if (field === 'note') {
                    // Note-Delete am Ende: Nächsten aktiven Absatz suchen
                    const nextActiveIdx = this._findNextActiveParagraph(paraIndex)
                    if (nextActiveIdx < 0) {
                        this._actionBlocked = true
                    } else if (this._isZuordnungsbereichEmptyInMain(paraIndex)
                            || this._isZuordnungsbereichEmptyInMain(nextActiveIdx)) {
                        return this._mergeOutlineGroups(paraIndex, nextActiveIdx)
                    } else {
                        this._actionBlocked = true
                    }
                } else {
                    // Separator-Guard: Nachfolger ist Separator → blockieren
                    if (this.paragraphs[paraIndex + 1].type === PARA_TYPE.SEPARATOR) {
                        this._separatorBlocked = true
                        return () => {}
                    }
                    return this.joinParagraphs(para.id, this.paragraphs[paraIndex + 1].id)
                }
            }
        }

        return () => {}
    }

    /**
     * Teilt einen Paragraph an der Cursor-Position (Enter).
     * @returns {Function} Undo-Funktion
     */
    splitParagraph() {
        if (!this.cursor) return () => {}

        // Separator-Guard: kein Split in Separator
        if (this.isSeparator(this.cursor.paragraphId)) {
            this._actionBlocked = true
            return () => {}
        }

        // Phantom-Main-Zeile: erst den Normal-Para materialisieren, dann
        // splitten — nie den text-Slot des Outline-Items anfassen. Ein
        // Split auf dem Item selbst wuerde es COW-clonen und dabei einen
        // ggf. illegal befuellten text-Slot verwerfen (Lesson 103).
        this._materializePhantomTextTarget()

        // Bei Selection: erst löschen
        if (this.hasSelection()) {
            this.deleteSelection()
        }

        const field = this.cursor.field

        if (field === 'info') {
            this._actionBlocked = true
            return () => {}
        }

        if (field === 'note') {
            return this._splitParagraphNote()
        }

        return this._splitParagraphText()
    }

    /**
     * Teilt einen Paragraph an der Cursor-Position im Text-Feld (Enter).
     * @returns {Function} Undo-Funktion
     */
    _splitParagraphText() {
        let para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return () => {}

        const paraIndex = this.getParagraphIndex(para.id)
        const offset = this.cursor.offset

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()

        // Neuen Paragraph erstellen mit Text nach Cursor
        const newPara = new Paragraph(this._getNextId(), para.text.slice(offset))
        newPara._gen = this._gen
        newPara.outlineDepth = para.outlineDepth  // Tiefe erben

        // Rich-Text: Spans/Links splitten BEVOR Text geändert wird
        const { left, right } = splitSpansAtOffset(para.formats.text, offset)
        const { left: leftLinks, right: rightLinks } = splitLinksAtOffset(para.links.text, offset)
        // Werden nach Convenient-Blank-Trimming angepasst
        newPara.formats.text = right
        newPara.links.text = rightLinks
        // Listen erben, Headings nicht
        if (para.listType) {
            newPara.listType = para.listType
            newPara.listLevel = para.listLevel
        }
        // left wird nach _mutate zugewiesen
        this._splitRichTextLeft = left
        this._splitRichTextLeftLinks = leftLinks

        // Aktuellen Paragraph kürzen (COW)
        para = this._mutate(para)
        para.text = para.text.slice(0, offset)

        // Rich-Text: Spans dem mutierten Paragraph zuweisen
        para.formats.text = this._splitRichTextLeft
        para.links.text = this._splitRichTextLeftLinks
        this._splitRichTextLeft = null
        this._splitRichTextLeftLinks = null

        // Convenient Blank: Trailing Space vom alten, Leading Space vom neuen Absatz trimmen
        const oldParaLen = para.text.length
        while (para.text.endsWith(' ')) para.text = para.text.slice(0, -1)
        const trimmedRight = oldParaLen - para.text.length
        if (trimmedRight > 0) {
            // Rechts getrimmt: Spans kürzen
            para.formats.text = adjustSpansForDeleteRange(para.formats.text, para.text.length, para.text.length + trimmedRight)
            para.links.text = adjustLinksForDeleteRange(para.links.text, para.text.length, para.text.length + trimmedRight)
        }

        const oldNewParaLen = newPara.text.length
        while (newPara.text.startsWith(' ')) newPara.text = newPara.text.slice(1)
        const trimmedLeft = oldNewParaLen - newPara.text.length
        if (trimmedLeft > 0) {
            // Links getrimmt: Spans verschieben
            newPara.formats.text = adjustSpansForDeleteRange(newPara.formats.text, 0, trimmedLeft)
            newPara.links.text = adjustLinksForDeleteRange(newPara.links.text, 0, trimmedLeft)
        }

        // Neuen Paragraph einfügen
        this.paragraphs.splice(paraIndex + 1, 0, newPara)
        this._mapsStale = true

        // Cursor zum Anfang des neuen Paragraphs
        this.cursor = new Position(newPara.id, 0)
        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(para.id)
        this._markParagraphDirty(newPara.id)

        // Undo-Funktion
        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Teilt einen Paragraph an der Cursor-Position im Note-Feld (Enter in Note).
     * Note wird gesplittet, Main-Text bleibt beim Original.
     * Sonderfall Offset 0: Alles wandert mit (leere Note bleibt, voller Text geht zum neuen Absatz).
     * @returns {Function} Undo-Funktion
     */
    _splitParagraphNote() {
        let para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return () => {}

        const paraIndex = this.getParagraphIndex(para.id)
        const offset = this.cursor.offset

        // Für Undo: COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()

        // Outline-Redesign Phase 3b §4.5: Folge-Para erbt den Typ.
        // In v4 ist Cursor in Note nur an Outline-Items moeglich, also wird der
        // Folge-Para faktisch immer ein Outline-Item. Der NORMAL-Pfad bleibt
        // als defensive Default-Form, falls jemand programmatisch einen Split
        // im Note-Feld eines Normal-Paras anstoesst (sollte in v4 nicht
        // passieren). Tiefe in beiden Faellen geerbt.
        const newType = para.isOutlineItem() ? PARA_TYPE.OUTLINE_ITEM : PARA_TYPE.NORMAL
        const newPara = new Paragraph(this._getNextId(), '', newType)
        newPara._gen = this._gen
        newPara.outlineDepth = para.outlineDepth

        // Note splitten: Text nach Cursor in neuen Absatz (COW)
        newPara.note = para.note.slice(offset)
        const { left: noteLeft, right: noteRight } = splitSpansAtOffset(para.formats.note, offset)
        const { left: noteLeftLinks, right: noteRightLinks } = splitLinksAtOffset(para.links.note, offset)
        newPara.formats.note = noteRight
        newPara.links.note = noteRightLinks
        para = this._mutate(para)
        para.note = para.note.slice(0, offset)
        para.formats.note = noteLeft
        para.links.note = noteLeftLinks

        // Convenient Blank: Trailing/Leading Space trimmen
        const oldNoteLen = para.note.length
        while (para.note.endsWith(' ')) para.note = para.note.slice(0, -1)
        const noteTrimRight = oldNoteLen - para.note.length
        if (noteTrimRight > 0) {
            para.formats.note = adjustSpansForDeleteRange(para.formats.note, para.note.length, para.note.length + noteTrimRight)
            para.links.note = adjustLinksForDeleteRange(para.links.note, para.note.length, para.note.length + noteTrimRight)
        }
        const oldNewNoteLen = newPara.note.length
        while (newPara.note.startsWith(' ')) newPara.note = newPara.note.slice(1)
        const noteTrimLeft = oldNewNoteLen - newPara.note.length
        if (noteTrimLeft > 0) {
            newPara.formats.note = adjustSpansForDeleteRange(newPara.formats.note, 0, noteTrimLeft)
            newPara.links.note = adjustLinksForDeleteRange(newPara.links.note, 0, noteTrimLeft)
        }

        // Main-Text bleibt beim Original (leerer neuer Absatz)

        // Insert-Position: nach dem Zuordnungsbereich des aktuellen Absatzes
        const owned = this.getOwnedParagraphs(paraIndex)
        const insertIndex = paraIndex + 1 + owned.length

        this.paragraphs.splice(insertIndex, 0, newPara)
        this._mapsStale = true

        // Cursor zum Anfang der Note im neuen Paragraph
        this.cursor = new Position(newPara.id, 0, 'note')
        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(para.id)
        this._markParagraphDirty(newPara.id)

        // Undo-Funktion
        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Fügt zwei Paragraphs zusammen.
     * @param {number} paraId1 - ID des ersten Paragraphs
     * @param {number} paraId2 - ID des zweiten Paragraphs
     * @returns {Function} Undo-Funktion
     */
    joinParagraphs(paraId1, paraId2) {
        let para1 = this.getParagraph(paraId1)
        const para2 = this.getParagraph(paraId2)

        if (!para1 || !para2) return () => {}

        const index1 = this.getParagraphIndex(paraId1)
        const index2 = this.getParagraphIndex(paraId2)

        // Müssen aufeinanderfolgend sein
        if (index2 !== index1 + 1) return () => {}

        // Separator-Guard: kein Join wenn einer der Absätze ein Separator ist
        if (para1.type === PARA_TYPE.SEPARATOR || para2.type === PARA_TYPE.SEPARATOR) {
            this._actionBlocked = true
            return () => {}
        }

        // Outline-Redesign Phase 3b §4.6: Cross-Type-Merges sind gesperrt.
        // Ein Outline-Item ist ein eigenstaendiger Strukturmarker — er kann
        // sich weder in einen Text-Para mergen noch von einem Text-Para
        // "geschluckt" werden. User muss das Item explizit loeschen (Cmd+D
        // oder Cleanup via leere Note + Verlassen, §4.4).
        if (para1.isOutlineItem() !== para2.isOutlineItem()) {
            this._actionBlocked = true
            return () => {}
        }
        // Zwei Outline-Items mergen nicht — jedes Item ist ein eigener
        // Strukturmarker mit eigener Note.
        if (para1.isOutlineItem() && para2.isOutlineItem()) {
            this._actionBlocked = true
            return () => {}
        }

        // Für Undo: COW-Snapshot (wegen Outline-Korrektur)
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()

        // COW: para1 wird mutiert
        para1 = this._mutate(para1)

        // Cursor-Ziel VOR den Merges messen (System-Review 2026-06-10):
        // Der Cursor soll an der Nahtstelle landen — nach dem Note-/Info-
        // Merge waere die Feldlaenge bereits die SUMME beider Felder und der
        // Cursor spraenge ans Ende. Praktisch nur defensiv erreichbar
        // (Note-Joins laufen ueber _mergeOutlineGroups, Info-Joins sind
        // blockiert), aber so ist die Messung fuer alle Felder korrekt.
        const field = this.cursor ? this.cursor.field : 'text'
        const cursorOffset = this._getFieldText(para1, field).length

        // Beide sind Normal-Paras (Cross-Type + Item+Item ist blockiert).
        // Notes zusammenfuehren (in v4 typisch beide leer, defensiv aber stabil).
        {
            const noteJoinOffset = para1.note.length
            const shiftedNoteFormats = shiftSpans(para2.formats.note, noteJoinOffset)
            const shiftedNoteLinks = shiftLinks(para2.links.note, noteJoinOffset)
            para1.formats.note = mergeAdjacentSpans([...para1.formats.note, ...shiftedNoteFormats])
            para1.links.note = [...para1.links.note, ...shiftedNoteLinks]
            para1.note = para1.note + para2.note
        }
        // Info zusammenführen
        {
            const infoJoinOffset = para1.info.length
            const shiftedInfoFormats = shiftSpans(para2.formats.info, infoJoinOffset)
            const shiftedInfoLinks = shiftLinks(para2.links.info, infoJoinOffset)
            para1.formats.info = mergeAdjacentSpans([...para1.formats.info, ...shiftedInfoFormats])
            para1.links.info = [...para1.links.info, ...shiftedInfoLinks]
        }
        para1.info = para1.info + para2.info

        // Text zusammenführen — mit Space wenn nötig (Absatzgrenzen sind Wortgrenzen)
        const t1 = para1.text, t2 = para2.text
        const needsSpace = t1.length > 0 && t2.length > 0
            && !t1.endsWith(' ') && !t2.startsWith(' ')
        {
            const textJoinOffset = t1.length + (needsSpace ? 1 : 0)
            const shiftedTextFormats = shiftSpans(para2.formats.text, textJoinOffset)
            const shiftedTextLinks = shiftLinks(para2.links.text, textJoinOffset)
            para1.formats.text = mergeAdjacentSpans([...para1.formats.text, ...shiftedTextFormats])
            para1.links.text = [...para1.links.text, ...shiftedTextLinks]
        }
        para1.text = needsSpace ? t1 + ' ' + t2 : t1 + t2

        // Zweiten Paragraph entfernen
        this.paragraphs.splice(index2, 1)
        this._mapsStale = true

        // Outline-Tiefen korrigieren (ehemalige Kinder von para2 anpassen)
        this._correctOutlineDepths(index1 + 1)

        // Cursor an Verbindungsstelle (im richtigen Feld, Space berücksichtigen)
        const spaceDelta = (needsSpace && field === 'text') ? 1 : 0
        this.cursor = new Position(para1.id, cursorOffset + spaceDelta, field)
        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(para1.id)

        // Undo-Funktion
        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    // ===== Formatierung =====

    /**
     * Wendet Formatierung auf eine Selection an.
     * @param {string} format - 'bold', 'italic', 'strikethrough'
     * @returns {Function} Undo-Funktion
     */
    applyFormat(format) {
        this._actionBlocked = false
        return this._applyFormatRichText(format)
    }

    /**
     * Rich-Text Format-Toggle: arbeitet direkt mit FormatSpan[] statt Markdown-Markern.
     * @param {string} format - 'bold', 'italic', 'strikethrough', 'underline', 'code'
     * @returns {Function} Undo-Funktion
     */
    _applyFormatRichText(format) {
        if (!this.cursor) return () => {}

        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para || para.type === PARA_TYPE.SEPARATOR) {
            this._actionBlocked = true
            return () => {}
        }

        const field = this.cursor.field || 'text'

        if (this.hasSelection()) {
            const { start, end } = this.selection.getNormalized(this)
            const startIndex = this.getParagraphIndex(start.paragraphId)
            const endIndex = this.getParagraphIndex(end.paragraphId)

            // Cross-Paragraph-Formatierung nicht unterstützt
            if (startIndex !== endIndex) {
                this._actionBlocked = true
                return () => {}
            }

            // COW-Snapshot
            this._gen++
            const oldParagraphs = this.paragraphs.slice()
            const oldSelection = this.selection.clone()

            const selPara = this._mutate(this.paragraphs[startIndex])
            const covered = isRangeCovered(selPara.formats[field], start.offset, end.offset, format)
            if (covered) {
                selPara.formats[field] = removeFormatFromRange(selPara.formats[field], start.offset, end.offset, format)
            } else {
                selPara.formats[field] = extendOrCreateSpan(selPara.formats[field], start.offset, end.offset, format)
            }

            this._markParagraphDirty(selPara.id)

            const undoFn = () => {
                this.paragraphs = oldParagraphs
                this.selection = oldSelection
                this._markStructuralDirty()
            }
            this._pushUndo(undoFn)
            return undoFn
        }

        // Ohne Selection: Wort unter Cursor finden
        const text = this._getFieldText(para, field)
        const wordBounds = this._getWordBoundsAt(text, this.cursor.offset)

        // Sonderfall: Cursor am Wortende, Format aktiv, Span endet hier
        // → nur activeFormats deaktivieren (User beendet gerade den Format-Span)
        if (wordBounds && this.cursor.offset === wordBounds.end && this._activeFormats.has(format)) {
            const spans = para.formats[field] || []
            const spanEndsHere = spans.some(s => s.type === format && s.end === this.cursor.offset)
            if (spanEndsHere) {
                this._activeFormats.delete(format)
                return () => {}
            }
        }

        if (wordBounds && wordBounds.start < wordBounds.end) {
            // COW-Snapshot
            this._gen++
            const oldParagraphs = this.paragraphs.slice()

            const mutPara = this._mutate(para)
            const covered = isRangeCovered(mutPara.formats[field], wordBounds.start, wordBounds.end, format)
            if (covered) {
                mutPara.formats[field] = removeFormatFromRange(mutPara.formats[field], wordBounds.start, wordBounds.end, format)
            } else {
                mutPara.formats[field] = extendOrCreateSpan(mutPara.formats[field], wordBounds.start, wordBounds.end, format)
            }

            this._markParagraphDirty(mutPara.id)

            const undoFn = () => {
                this.paragraphs = oldParagraphs
                this._markStructuralDirty()
            }
            this._pushUndo(undoFn)
            return undoFn
        }

        // Kein Wort unter Cursor: _activeFormats togglen
        if (this._activeFormats.has(format)) {
            this._activeFormats.delete(format)
        } else {
            this._activeFormats.add(format)
        }
        return () => {}
    }

    /**
     * Findet die Wort-Grenzen an einer Position im Text.
     * @param {string} text
     * @param {number} offset
     * @returns {{start: number, end: number}|null}
     */
    _getWordBoundsAt(text, offset) {
        return tpGetWordBounds(text, offset)
    }

    /**
     * Entfernt leere Format-Marker-Paare nach Deletion.
     * z.B. "****" → "", "~~~~" → "", "**" → "" (leerer Bold/Italic)
     * @param {Paragraph} para
     * @param {string} field - 'text' oder 'note'
     */
    _cleanupEmptyFormatSpans(para, field) {
        const oldText = this._getFieldText(para, field)
        const cursorOffset = (this.cursor && this.cursor.paragraphId === para.id
            && (this.cursor.field || 'text') === field) ? this.cursor.offset : 0
        const result = tpCleanupEmptyFormatSpans(oldText, cursorOffset)
        if (result.newText !== oldText) {
            if (this.cursor && this.cursor.paragraphId === para.id
                && (this.cursor.field || 'text') === field) {
                this.cursor.offset = result.newCursorOffset
            }
            this._setFieldText(para, field, result.newText)
        }
    }

    /**
     * Toggle Bold-Formatierung.
     */
    toggleBold() {
        return this.applyFormat('bold')
    }

    /**
     * Toggle Italic-Formatierung.
     */
    toggleItalic() {
        return this.applyFormat('italic')
    }

    /**
     * Toggle Strikethrough-Formatierung.
     */
    toggleStrikethrough() {
        return this.applyFormat('strikethrough')
    }

    /**
     * Toggle Underline-Formatierung.
     */
    toggleUnderline() {
        return this.applyFormat('underline')
    }

    /**
     * Toggle Inline-Code-Formatierung (mono + Sondercolor).
     */
    toggleCode() {
        return this.applyFormat('code')
    }

    // ===== Undo/Redo =====

    /**
     * Fügt eine Undo-Funktion zum Stack hinzu.
     * @param {Function} undoFn
     */
    _pushUndo(undoFn) {
        // Waehrend einer offenen Edit-Gruppe (z.B. Diktat-Utterance) decken
        // wir alle Aenderungen mit dem in beginEditGroup() angelegten
        // Snapshot-Restore ab — einzelne Edits NICHT mehr pushen.
        if (this._inEditGroup) return
        this.undoStack.push(undoFn)
        // Älteste Einträge verwerfen wenn Stack-Limit erreicht
        if (this.undoStack.length > this.MAX_UNDO_STACK_SIZE) {
            this.undoStack.shift()
        }
        // Redo-Stack leeren bei neuer Aktion
        this.redoStack = []
        // Konsolidierungsgruppe beenden (nächste insertText-Sequenz startet neue Gruppe)
        this._isInConsolidationGroup = false
    }

    /**
     * Beendet die laufende Undo-Konsolidierungsgruppe und setzt die
     * Zaehler zurueck — die naechste Eingabe startet einen frischen
     * Undo-Punkt. (System-Review 2026-06-10, Kleinigkeit 16: vorher
     * 11x wortgleich kopiert.)
     */
    _resetUndoConsolidation() {
        this.charsSinceUndoPoint = 0
        this._isInConsolidationGroup = false
        this._lastUndoParagraphId = undefined
        this._lastUndoField = undefined
    }

    /**
     * Konsolidiert mit dem letzten Undo-Eintrag wenn möglich.
     * @param {Function} undoFn
     */
    _consolidateUndo(undoFn) {
        // Edit-Gruppe (Diktat etc.) deckt alles ab — kein Push, kein
        // Group-State-Toggle waehrend der Gruppe.
        if (this._inEditGroup) return
        if (!this._isInConsolidationGroup) {
            // Erste Eingabe einer neuen Gruppe: als Undo-Punkt pushen
            this.undoStack.push(undoFn)
            if (this.undoStack.length > this.MAX_UNDO_STACK_SIZE) this.undoStack.shift()
            this.redoStack = []
            this._isInConsolidationGroup = true
        }
        // Folge-Eingaben: verwerfen (erster Eintrag deckt die ganze Gruppe ab)
    }

    /**
     * Beginnt eine konsolidierte Edit-Gruppe: bis zum naechsten
     * `endEditGroup()` werden alle Aenderungen mit EINEM Undo-Eintrag
     * abgedeckt (Snapshot vor Gruppenstart). Idempotent — mehrfaches
     * `beginEditGroup()` ist sicher und no-op.
     *
     * Use-Case: Diktat. Pro Utterance gibt es viele Interim-Inserts
     * (jeder mit Selection-Delete + Insert). Ohne Gruppe landet jede
     * Phase einzeln im Undo-Stack — Cmd+Z muesste 20+ mal gedrueckt
     * werden, um die Utterance zu entfernen. Mit Gruppe: ein Cmd+Z.
     */
    beginEditGroup() {
        if (this._inEditGroup) return
        // Snapshot ANLEGEN bevor wir die Gruppe oeffnen — danach gehen
        // alle Edits durch _pushUndo (no-op). Der Snapshot deckt alles ab.
        this._gen++
        const snapshot = {
            paragraphs: this.paragraphs.slice(),
            cursor: this.cursor ? this.cursor.clone() : null,
            selection: this.selection ? this.selection.clone() : null
        }
        const undoFn = () => {
            this.paragraphs = snapshot.paragraphs.slice()
            this.cursor = snapshot.cursor ? snapshot.cursor.clone() : null
            this.selection = snapshot.selection ? snapshot.selection.clone() : null
            this._markStructuralDirty()
        }
        this.undoStack.push(undoFn)
        if (this.undoStack.length > this.MAX_UNDO_STACK_SIZE) this.undoStack.shift()
        this.redoStack = []
        this._editGroupSnapshot = snapshot
        this._inEditGroup = true
        // Konsolidierungs-Gruppe sauber resetten — naechste Aktion nach
        // endEditGroup() startet sonst evtl. in einer alten Gruppe.
        this._isInConsolidationGroup = false
        this.charsSinceUndoPoint = 0
        this._lastUndoParagraphId = undefined
        this._lastUndoField = undefined
    }

    /**
     * Beendet die laufende Edit-Gruppe. No-op wenn keine offen.
     * Folgende Aktionen pushen wieder normal in den Undo-Stack.
     */
    endEditGroup() {
        if (!this._inEditGroup) return
        this._inEditGroup = false
        this._editGroupSnapshot = null
        // Naechste Aktion startet eine frische Konsolidierungs-Gruppe.
        this._isInConsolidationGroup = false
        this.charsSinceUndoPoint = 0
    }

    /**
     * Führt Undo aus.
     * @returns {boolean} true wenn Undo ausgeführt wurde
     */
    undo() {
        if (this.undoStack.length === 0) return false

        // Aktuellen State für Redo speichern (COW-Snapshot)
        this._gen++
        const currentState = {
            paragraphs: this.paragraphs.slice(),
            cursor: this.cursor.clone(),
            selection: this.selection ? this.selection.clone() : null
        }

        const redoFn = () => {
            this.paragraphs = currentState.paragraphs
            this.cursor = currentState.cursor
            this.selection = currentState.selection
            this._markDirty()
        }

        this.redoStack.push(redoFn)

        // Undo ausführen
        const undoFn = this.undoStack.pop()
        undoFn()

        // Wiederhergestellte Absätze für zukünftige Mutationen einfrieren
        this._gen++

        this.charsSinceUndoPoint = 0
        return true
    }

    /**
     * Führt Redo aus.
     * @returns {boolean} true wenn Redo ausgeführt wurde
     */
    redo() {
        if (this.redoStack.length === 0) return false

        // Aktuellen State für Undo speichern (COW-Snapshot)
        this._gen++
        const currentState = {
            paragraphs: this.paragraphs.slice(),
            cursor: this.cursor.clone(),
            selection: this.selection ? this.selection.clone() : null
        }

        const undoFn = () => {
            this.paragraphs = currentState.paragraphs
            this.cursor = currentState.cursor
            this.selection = currentState.selection
            this._markDirty()
        }

        this.undoStack.push(undoFn)
        // Älteste Undo-Einträge verwerfen wenn Stack-Limit erreicht
        if (this.undoStack.length > this.MAX_UNDO_STACK_SIZE) {
            this.undoStack.shift()
        }

        // Redo ausführen
        const redoFn = this.redoStack.pop()
        redoFn()

        // Wiederhergestellte Absätze für zukünftige Mutationen einfrieren
        this._gen++

        return true
    }

    /**
     * Prüft ob Undo möglich ist.
     * @returns {boolean}
     */
    canUndo() {
        return this.undoStack.length > 0
    }

    /**
     * Prüft ob Redo möglich ist.
     * @returns {boolean}
     */
    canRedo() {
        return this.redoStack.length > 0
    }

    // ===== Outline-Hilfsfunktionen =====

    /**
     * Korrigiert die Outline-Tiefen ab einem bestimmten Index,
     * damit die Sequenz gültig bleibt.
     * Maximale erlaubte Tiefe am Insert-Punkt:
     *   Vorgänger aktiv → Vorgänger.outlineDepth + 1
     *   Vorgänger passiv → Vorgänger.outlineDepth
     *   Kein Vorgänger → 0
     *
     * @param {number} startIndex - Ab diesem Index prüfen
     */
    /**
     * Wendet gespeicherte Absatz-Attribute (Outline + Block-Format) auf einen Absatz an.
     * @param {Paragraph} para - Ziel-Absatz
     * @param {Object} info - Gespeicherte Attribute aus der Zwischenablage
     */
    _applyParagraphInfo(para, info) {
        if (info.type !== undefined) para.type = info.type
        if (info.headingLevel !== undefined) para.headingLevel = info.headingLevel
        if (info.listType !== undefined) para.listType = info.listType
        if (info.listLevel !== undefined) para.listLevel = info.listLevel
        if (info.listStart !== undefined) para.listStart = info.listStart
        if (info.blockquote !== undefined) para.blockquote = info.blockquote
        if (info.codeBlock !== undefined) para.codeBlock = info.codeBlock
    }

    _correctOutlineDepths(startIndex) {
        if (startIndex >= this.paragraphs.length) return

        // Defense-in-Depth: Aufrufer hat paragraphs evtl. gerade via splice
        // mutiert. _mutate() unten nutzt getParagraphIndex() — der darf
        // keinen stalen _indexMap treffen, sonst schreibt er in den falschen
        // Slot. Entry-Guard garantiert frische Maps beim ersten Lookup.
        this._mapsStale = true

        for (let i = startIndex; i < this.paragraphs.length; i++) {
            const p = this.paragraphs[i]
            // Nur Outline-Items tragen eine eigene Outline-Tiefe. Normal-Paras
            // erben ihre Tiefe implizit vom vorhergehenden Outline-Item
            // (Spec §2.3) — sie haben kein zu clampendes outlineDepth-Feld.
            // Separatoren haben ihre eigene Abschnitts-Hierarchie.
            if (!p.isOutlineItem()) continue

            const maxDepth = this._getMaxAllowedDepth(i)

            if (p.outlineDepth > maxDepth) {
                const mp = this._mutate(p)
                mp.outlineDepth = maxDepth
                this._markParagraphDirty(mp.id)
            }
        }
    }

    /**
     * Berechnet die maximal erlaubte Tiefe für ein Outline-Item an einem Index.
     *
     * Gemessen am nächsten Outline-Vorfahren (rückwärts bis zum ersten
     * Outline-Item oder Separator), damit z.B. ein T1-Item nach mehreren
     * Normal-Paras unter einem T0-Item nicht künstlich beschränkt wird. Ein
     * Separator definiert die Abschnitts-Baseline (frei tief), ebenso der
     * Dokumentanfang (index === 0, max 10 — Import/neue Dokumente erlauben
     * beliebige Start-Tiefen).
     *
     * Wird ausschließlich aus `_correctOutlineDepths` für Outline-Items
     * aufgerufen — Normal-Paras tragen keine eigene Outline-Tiefe (Spec §2.3).
     *
     * @param {number} index - Index des Outline-Items
     * @returns {number} Maximale erlaubte Tiefe
     */
    _getMaxAllowedDepth(index) {
        if (index <= 0) return EDITOR.MAX_OUTLINE_ITEM_DEPTH // Erstes Item darf jede Tiefe haben

        for (let j = index - 1; j >= 0; j--) {
            const p = this.paragraphs[j]
            if (p.type === PARA_TYPE.SEPARATOR) return EDITOR.MAX_OUTLINE_ITEM_DEPTH  // neuer Abschnitt: frei
            if (p.isOutlineItem()) return p.outlineDepth + 1
        }
        return EDITOR.MAX_OUTLINE_ITEM_DEPTH  // kein Outline-Vorfahre im Abschnitt
    }

    // ===== Outline-Operationen =====

    /**
     * Gibt alle nach der Ordnungsregel zugeordneten Absätze zurück.
     * Für einen aktiven Absatz A mit Tiefe N bei Index i:
     *   Iteriere ab i+1:
     *     - Wenn aktiv und Tiefe ≤ N → STOP
     *     - Wenn passiv und Tiefe < N → STOP
     *     - Sonst → gehört zu A
     *
     * @param {number} paragraphIndex - Index des Absatzes
     * @returns {number[]} Array von Indizes der zugeordneten Absätze
     */
    getOwnedParagraphs(paragraphIndex) {
        const para = this.paragraphs[paragraphIndex]
        if (!para || !para.isOutlineItem()) return []

        const depth = para.outlineDepth
        const owned = []

        // v4-Range (Spec §2.3): endet bei nächstem Outline-Item mit gleicher
        // oder geringerer Tiefe oder bei Separator. Normal-Paras bleiben drin.
        for (let i = paragraphIndex + 1; i < this.paragraphs.length; i++) {
            const p = this.paragraphs[i]
            if (p.type === PARA_TYPE.SEPARATOR) break
            if (p.isOutlineItem() && p.outlineDepth <= depth) break
            owned.push(i)
        }

        return owned
    }

    /**
     * Gibt den kompletten Subtree-Range für einen aktiven Absatz zurück.
     * Umfasst den Absatz selbst plus alle zugeordneten Absätze.
     * @param {number} paragraphIndex - Index des aktiven Absatzes
     * @returns {{startIndex: number, endIndex: number}} Inklusiver Range
     */
    getSubtreeRange(paragraphIndex) {
        const owned = this.getOwnedParagraphs(paragraphIndex)
        return {
            startIndex: paragraphIndex,
            endIndex: owned.length > 0 ? owned[owned.length - 1] : paragraphIndex
        }
    }

    /**
     * Sucht rückwärts nach dem nächsten aktiven Absatz.
     * @param {number} paraIndex - Startindex (exklusiv)
     * @returns {number} Index des gefundenen aktiven Absatzes, oder -1
     */
    _findPreviousActiveParagraph(paraIndex) {
        for (let i = paraIndex - 1; i >= 0; i--) {
            if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) return -1  // Separator = Grenze
            if (this.paragraphs[i].isOutlineItem()) return i
        }
        return -1
    }

    /**
     * Sucht vorwärts nach dem nächsten aktiven Absatz.
     * @param {number} paraIndex - Startindex (exklusiv)
     * @returns {number} Index des gefundenen aktiven Absatzes, oder -1
     */
    _findNextActiveParagraph(paraIndex) {
        for (let i = paraIndex + 1; i < this.paragraphs.length; i++) {
            if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) return -1  // Separator = Grenze
            if (this.paragraphs[i].isOutlineItem()) return i
        }
        return -1
    }

    /**
     * Prüft ob der Zuordnungsbereich eines aktiven Absatzes leeren Main-Text hat.
     * Der ZB umfasst den aktiven Absatz selbst + alle owned passiven Absätze.
     * @param {number} activeParaIndex - Index des aktiven Absatzes
     * @returns {boolean} true wenn alle Main-Texte im ZB leer sind
     */
    _isZuordnungsbereichEmptyInMain(activeParaIndex) {
        const para = this.paragraphs[activeParaIndex]
        if (!para || !para.isOutlineItem()) return false

        if (para.text !== '') return false

        const owned = this.getOwnedParagraphs(activeParaIndex)
        for (const idx of owned) {
            if (this.paragraphs[idx].text !== '') return false
        }

        return true
    }

    /**
     * Mergt zwei Outline-Gruppen (aktiver Absatz + ZB).
     * Der leere ZB wird gelöscht, Notes werden zusammengeführt.
     *
     * @param {number} activeAIndex - Index des ersten aktiven Absatzes
     * @param {number} activeBIndex - Index des zweiten aktiven Absatzes
     * @returns {Function} Undo-Funktion
     */
    _mergeOutlineGroups(activeAIndex, activeBIndex) {
        const paraA = this.paragraphs[activeAIndex]
        const paraB = this.paragraphs[activeBIndex]
        if (!paraA || !paraB) return () => {}

        // Für Undo: COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()

        const aEmpty = this._isZuordnungsbereichEmptyInMain(activeAIndex)
        const bEmpty = this._isZuordnungsbereichEmptyInMain(activeBIndex)

        let survivorIndex, deleteIndex
        if (aEmpty) {
            // ZB_A ist leer → B ist Survivor, lösche A + owned(A)
            survivorIndex = activeBIndex
            deleteIndex = activeAIndex
        } else {
            // ZB_B ist leer (oder beide leer) → A ist Survivor, lösche B + owned(B)
            survivorIndex = activeAIndex
            deleteIndex = activeBIndex
        }

        const survivor = this._mutate(this.paragraphs[survivorIndex])
        const deleted = this.paragraphs[deleteIndex]

        // Notes zusammenführen — mit Space wenn nötig
        const sNote = survivor.note, dNote = deleted.note
        const noteNeedsSpace = sNote.length > 0 && dNote.length > 0
            && !sNote.endsWith(' ') && !dNote.startsWith(' ')
        const noteSpaceDelta = noteNeedsSpace ? 1 : 0

        let noteJoinOffset
        if (survivorIndex < deleteIndex) {
            noteJoinOffset = sNote.length + noteSpaceDelta
            survivor.note = noteNeedsSpace ? sNote + ' ' + dNote : sNote + dNote
        } else {
            noteJoinOffset = dNote.length + noteSpaceDelta
            survivor.note = noteNeedsSpace ? dNote + ' ' + sNote : dNote + sNote
        }

        // Zu löschende Indizes sammeln (aktiver Absatz + owned)
        const ownedIndices = this.getOwnedParagraphs(deleteIndex)
        const toDelete = [deleteIndex, ...ownedIndices]
        // Von hinten nach vorne löschen (Index-Stabilität)
        toDelete.sort((a, b) => b - a)
        for (const idx of toDelete) {
            this.paragraphs.splice(idx, 1)
        }
        if (toDelete.length > 0) this._mapsStale = true

        // Outline-Tiefen korrigieren
        const correctionStart = Math.min(activeAIndex, activeBIndex)
        this._correctOutlineDepths(correctionStart)

        // Cursor an Verbindungsstelle in Survivor's Note
        this.cursor = new Position(survivor.id, noteJoinOffset, 'note')

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Alt+Pfeil-Links / Shift-Tab in Note: Outline-Item nach links bewegen.
     * Outline-Redesign v4: ausschliesslich Tiefen-Operation an Outline-Items
     * (gecappt 0..10). Normal-Paras haben in v4 keine Outline-Semantik mehr —
     * der Aufruf gibt false zurueck.
     *
     * @param {number} paragraphId - ID des Absatzes
     * @returns {boolean} true wenn Änderung vorgenommen
     */
    handleOutlineLeft(paragraphId) {
        const para = this.getParagraph(paragraphId)
        if (!para || !para.isOutlineItem()) return false
        return this._adjustOutlineItemDepth(para, -1)
    }

    /**
     * Alt+Pfeil-Rechts / Tab in Note: Outline-Item nach rechts bewegen.
     * Outline-Redesign v4: ausschliesslich Tiefen-Operation an Outline-Items
     * (gecappt 0..10, Collapse-Schutz via _adjustOutlineItemDepth). Normal-
     * Paras haben in v4 keine Outline-Semantik mehr — der Aufruf gibt false
     * zurueck.
     *
     * @param {number} paragraphId - ID des Absatzes
     * @returns {boolean} true wenn Änderung vorgenommen
     */
    handleOutlineRight(paragraphId) {
        const para = this.getParagraph(paragraphId)
        if (!para || !para.isOutlineItem()) return false
        return this._adjustOutlineItemDepth(para, +1)
    }

    // ===== Outline-Subtree-Operationen =====

    /**
     * Löscht einen Outline-Subtree (Range von Absätzen).
     * Schützt Sections vor dem Leerlaufen.
     * @param {number} startIndex - Erster Absatz-Index (inklusive)
     * @param {number} endIndex - Letzter Absatz-Index (inklusive)
     * @returns {Function} Undo-Funktion
     */
    /**
     * Verschiebt einen Outline-Subtree nach oben oder unten (Alt+Pfeil im Note-Feld).
     * Tauscht mit dem vorherigen/nächsten Geschwister-Subtree gleicher Tiefe.
     * @param {number} paragraphId - ID des aktiven Absatzes
     * @param {'up'|'down'} direction
     * @returns {boolean} true wenn verschoben
     */
    /**
     * Sucht den Geschwister-Subtree eines Outline-Items (gleiche Tiefe, innerhalb
     * derselben Section) in der angegebenen Richtung. Geteilt von moveOutlineSubtree
     * und canMoveOutlineSubtree, damit Menü-Vorschau und Aktion garantiert
     * dieselbe Bedingung prüfen.
     * @param {number} paragraphId
     * @param {'up'|'down'} direction
     * @returns {number} Index des Geschwister-Items oder -1
     */
    _findOutlineSubtreeSibling(paragraphId, direction) {
        const para = this.getParagraph(paragraphId)
        if (!para || !para.isOutlineItem()) return -1
        const idx = this.getParagraphIndex(paragraphId)
        const depth = para.outlineDepth
        const myRange = this.getSubtreeRange(idx)

        if (direction === 'up') {
            // Rückwärts suchen ab myRange.startIndex - 1
            for (let i = myRange.startIndex - 1; i >= 0; i--) {
                const p = this.paragraphs[i]
                if (p.type === PARA_TYPE.SEPARATOR) break
                if (p.isOutlineItem() && p.outlineDepth === depth) return i
                if (p.isOutlineItem() && p.outlineDepth < depth) break
            }
        } else {
            // Vorwärts suchen ab myRange.endIndex + 1
            for (let i = myRange.endIndex + 1; i < this.paragraphs.length; i++) {
                const p = this.paragraphs[i]
                if (p.type === PARA_TYPE.SEPARATOR) break
                if (p.isOutlineItem() && p.outlineDepth === depth) return i
                if (p.isOutlineItem() && p.outlineDepth < depth) break
            }
        }
        return -1
    }

    /**
     * Vorschau: Würde moveOutlineSubtree in dieser Richtung etwas tun?
     * @param {number} paragraphId
     * @param {'up'|'down'} direction
     * @returns {boolean}
     */
    canMoveOutlineSubtree(paragraphId, direction) {
        return this._findOutlineSubtreeSibling(paragraphId, direction) !== -1
    }

    moveOutlineSubtree(paragraphId, direction) {
        const para = this.getParagraph(paragraphId)
        if (!para || !para.isOutlineItem()) return false

        const idx = this.getParagraphIndex(paragraphId)
        const myRange = this.getSubtreeRange(idx)

        const siblingIdx = this._findOutlineSubtreeSibling(paragraphId, direction)
        if (siblingIdx === -1) return false

        const sibRange = this.getSubtreeRange(siblingIdx)

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Subtrees extrahieren und in getauschter Reihenfolge einsetzen
        const firstRange = direction === 'up' ? sibRange : myRange
        const secondRange = direction === 'up' ? myRange : sibRange
        const firstSlice = this.paragraphs.slice(firstRange.startIndex, firstRange.endIndex + 1)
        const secondSlice = this.paragraphs.slice(secondRange.startIndex, secondRange.endIndex + 1)

        this.paragraphs.splice(
            firstRange.startIndex,
            secondRange.endIndex - firstRange.startIndex + 1,
            ...secondSlice, ...firstSlice
        )
        this._mapsStale = true

        this._markStructuralDirty()
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        return true
    }

    /**
     * Verschiebt einen Absatz nach oben oder unten (Alt+Pfeil).
     * Outline-Struktur wird nicht mitgenommen (Absatz wird passiv).
     * @param {number} paragraphId
     * @param {'up'|'down'} direction
     * @returns {boolean} true wenn verschoben
     */
    moveParagraph(paragraphId, direction) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx === -1) return false

        const targetIdx = direction === 'up' ? idx - 1 : idx + 1
        if (targetIdx < 0 || targetIdx >= this.paragraphs.length) return false

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Absatz mutieren: Outline-Tiefe abstreifen. Der Typ bleibt — moveParagraph
        // wird in v4 ohnehin nur fuer Normal-Paras gerufen (Outline-Items laufen
        // ueber moveOutlineSubtree).
        const para = this._mutate(this.paragraphs[idx])
        para.outlineDepth = 0

        // Swap
        this.paragraphs[idx] = this.paragraphs[targetIdx]
        this.paragraphs[targetIdx] = para

        // Cursor bleibt auf gleichem Absatz, Field auf text korrigieren
        if (this.cursor && this.cursor.paragraphId === paragraphId) {
            this.cursor = new Position(paragraphId, this.cursor.offset, 'text')
        }

        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(para.id)
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        return true
    }

    /**
     * Vorschau: Würde moveParagraph in dieser Richtung etwas tun? moveParagraph
     * tauscht mit dem direkten Nachbarn — über Section-Grenzen hinweg —, also
     * gilt nur die Dokumentgrenze. Geteilt mit der Menü-Vorschau.
     * @param {number} paragraphId
     * @param {'up'|'down'} direction
     * @returns {boolean}
     */
    canMoveParagraph(paragraphId, direction) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx === -1) return false
        const targetIdx = direction === 'up' ? idx - 1 : idx + 1
        return targetIdx >= 0 && targetIdx < this.paragraphs.length
    }

    /**
     * Range einer Separator-Section inklusive aller untergeordneten Subsections:
     * vom Separator bis zum letzten Absatz vor dem nächsten Separator gleicher
     * oder höherer Ebene (outlineDepth <= eigene Tiefe).
     * @param {number} separatorIndex
     * @returns {{startIndex:number, endIndex:number}}
     */
    _separatorSectionRange(separatorIndex) {
        const depth = this.paragraphs[separatorIndex].outlineDepth
        let endIndex = this.paragraphs.length - 1
        for (let i = separatorIndex + 1; i < this.paragraphs.length; i++) {
            const p = this.paragraphs[i]
            if (p.type === PARA_TYPE.SEPARATOR && p.outlineDepth <= depth) { endIndex = i - 1; break }
        }
        return { startIndex: separatorIndex, endIndex }
    }

    /**
     * Sucht den Geschwister-Separator gleicher Ebene innerhalb derselben
     * Eltern-Section. Tiefere Subsections werden übersprungen, ein Separator
     * höherer Ebene (outlineDepth < eigene) beendet die Suche (Eltern-Grenze).
     * Geteilt von moveSection und canMoveSection.
     * @param {number} separatorIndex
     * @param {'up'|'down'} direction
     * @returns {number} Index des Geschwister-Separators oder -1
     */
    _findSiblingSeparator(separatorIndex, direction) {
        const depth = this.paragraphs[separatorIndex].outlineDepth
        if (direction === 'up') {
            for (let i = separatorIndex - 1; i >= 0; i--) {
                const p = this.paragraphs[i]
                if (p.type !== PARA_TYPE.SEPARATOR) continue
                if (p.outlineDepth === depth) return i
                if (p.outlineDepth < depth) break  // Eltern-Grenze erreicht
            }
        } else {
            const myRange = this._separatorSectionRange(separatorIndex)
            for (let i = myRange.endIndex + 1; i < this.paragraphs.length; i++) {
                const p = this.paragraphs[i]
                if (p.type !== PARA_TYPE.SEPARATOR) continue
                if (p.outlineDepth === depth) return i
                if (p.outlineDepth < depth) break  // Eltern-Grenze erreicht
            }
        }
        return -1
    }

    /**
     * Verschiebt eine ganze Section (Separator + Inhalt + Subsections) nach oben
     * oder unten — tauscht als Block mit der Geschwister-Section gleicher Ebene.
     * Pendant zu moveOutlineSubtree, aber auf Separator-Hierarchie-Ebene. Die
     * Background-Section (Story Bible) ist nicht verschiebbar.
     * @param {number} separatorId - ID des Separators
     * @param {'up'|'down'} direction
     * @returns {boolean} true wenn verschoben
     */
    moveSection(separatorId, direction) {
        const idx = this.getParagraphIndex(separatorId)
        if (idx === -1) return false
        const sep = this.paragraphs[idx]
        if (sep.type !== PARA_TYPE.SEPARATOR) return false
        if (sep.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return false

        const siblingIdx = this._findSiblingSeparator(idx, direction)
        if (siblingIdx === -1) return false

        const myRange = this._separatorSectionRange(idx)
        const sibRange = this._separatorSectionRange(siblingIdx)

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Die beiden adjazenten Section-Blöcke in getauschter Reihenfolge einsetzen
        const firstRange = direction === 'up' ? sibRange : myRange
        const secondRange = direction === 'up' ? myRange : sibRange
        const firstSlice = this.paragraphs.slice(firstRange.startIndex, firstRange.endIndex + 1)
        const secondSlice = this.paragraphs.slice(secondRange.startIndex, secondRange.endIndex + 1)

        this.paragraphs.splice(
            firstRange.startIndex,
            secondRange.endIndex - firstRange.startIndex + 1,
            ...secondSlice, ...firstSlice
        )
        this._mapsStale = true

        this._markStructuralDirty()
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        return true
    }

    /**
     * Vorschau: Würde moveSection in dieser Richtung etwas tun?
     * @param {number} separatorId
     * @param {'up'|'down'} direction
     * @returns {boolean}
     */
    canMoveSection(separatorId, direction) {
        const idx = this.getParagraphIndex(separatorId)
        if (idx === -1) return false
        const sep = this.paragraphs[idx]
        if (sep.type !== PARA_TYPE.SEPARATOR) return false
        if (sep.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return false
        return this._findSiblingSeparator(idx, direction) !== -1
    }

    /**
     * Einheitlicher Einstieg für Alt+Up/Down und das Context-Menü: verschiebt
     * das Element am Cursor je nach Typ/Feld. Separator → ganze Section,
     * Outline-Item im Note-Feld → Subtree, sonst → einzelner Absatz.
     * @param {number} paragraphId
     * @param {'text'|'note'|'info'} field - cursor.field
     * @param {'up'|'down'} direction
     * @returns {boolean} true wenn verschoben
     */
    moveAtCursor(paragraphId, field, direction) {
        const para = this.getParagraph(paragraphId)
        if (para && para.type === PARA_TYPE.SEPARATOR) return this.moveSection(paragraphId, direction)
        if (field === 'note') return this.moveOutlineSubtree(paragraphId, direction)
        return this.moveParagraph(paragraphId, direction)
    }

    /**
     * Vorschau zu moveAtCursor — dieselbe Verzweigung, damit Menü-Ausgrauen und
     * tatsächliche Bewegung nie auseinanderlaufen.
     * @param {number} paragraphId
     * @param {'text'|'note'|'info'} field - cursor.field
     * @param {'up'|'down'} direction
     * @returns {boolean}
     */
    canMoveAtCursor(paragraphId, field, direction) {
        const para = this.getParagraph(paragraphId)
        if (!para) return false
        if (para.type === PARA_TYPE.SEPARATOR) return this.canMoveSection(paragraphId, direction)
        if (field === 'note') return this.canMoveOutlineSubtree(paragraphId, direction)
        return this.canMoveParagraph(paragraphId, direction)
    }

    /**
     * Lock-Status der Section, die eine Einfuegeposition besitzt: naechster
     * Separator bei oder vor dem Index. Kein Separator davor = Implicit Head
     * (nie gesperrt). Geteilt von moveParagraphTo fuer den Ziel-Lock-Check.
     * @param {number} index - letzter Index VOR der Einfuegeposition
     * @returns {boolean}
     * @private
     */
    _sectionLockedAtIndex(index) {
        for (let i = index; i >= 0; i--) {
            const p = this.paragraphs[i]
            if (p.type === PARA_TYPE.SEPARATOR) return !!p.locked
        }
        return false
    }

    /**
     * Verschiebt eine ganze Section (Separator + Inhalt + Subsections) an
     * eine anker-definierte Position — das anker-basierte Pendant zu
     * moveSection fuer den Agent-Pfad (Option+Arrow bleibt beim
     * Sibling-Stepping). Spec: docs/archive/move-tool-specification.md.
     *
     * Ziel: GENAU EINER von afterSeparatorId / beforeSeparatorId.
     *   - beforeSeparatorId: direkt VOR den Block dieser Section
     *   - afterSeparatorId:  direkt HINTER den kompletten Block (inkl.
     *     Subsections) dieser Section
     *
     * Die Section behaelt ihre outlineDepth (kein Re-Basing — Tiefe aendert
     * man explizit via updateSection); Main↔Background-Wechsel ist erlaubt,
     * die Area ergibt sich aus der Position relativ zum Background-Separator.
     *
     * @param {string} separatorId
     * @param {{afterSeparatorId?: string, beforeSeparatorId?: string}} target
     * @returns {{ok: boolean, reason?: string, noChange?: boolean}}
     */
    moveSectionTo(separatorId, target = {}) {
        const idx = this.getParagraphIndex(separatorId)
        if (idx === -1) return { ok: false, reason: 'unknown_section' }
        const sep = this.paragraphs[idx]
        if (sep.type !== PARA_TYPE.SEPARATOR) return { ok: false, reason: 'not_a_separator' }
        if (sep.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            return { ok: false, reason: 'background_boundary' }
        }
        if (sep.locked) return { ok: false, reason: 'section_locked' }

        const hasAfter = target.afterSeparatorId != null
        const hasBefore = target.beforeSeparatorId != null
        if (hasAfter === hasBefore) return { ok: false, reason: 'invalid_anchor' }

        const anchorId = hasAfter ? target.afterSeparatorId : target.beforeSeparatorId
        const aIdx = this.getParagraphIndex(anchorId)
        if (aIdx === -1) return { ok: false, reason: 'unknown_section' }
        const anchorSep = this.paragraphs[aIdx]
        if (anchorSep.type !== PARA_TYPE.SEPARATOR) return { ok: false, reason: 'not_a_separator' }
        // Der Background-Separator ist als ANKER erlaubt (before = Ende Main,
        // after = Ende Background) — nur er selbst ist unverschiebbar (oben).

        const myRange = this._separatorSectionRange(idx)
        if (aIdx >= myRange.startIndex && aIdx <= myRange.endIndex) {
            return { ok: false, reason: 'anchor_inside_moved_range' }
        }

        // Einfuegeposition im AKTUELLEN Array (vor dem Entfernen). "Hinter
        // eine Section" heisst hinter deren kompletten Block.
        const insertAt = hasBefore
            ? aIdx
            : this._separatorSectionRange(aIdx).endIndex + 1

        if (insertAt === myRange.startIndex || insertAt === myRange.endIndex + 1) {
            return { ok: true, noChange: true }
        }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        const block = this.paragraphs.splice(
            myRange.startIndex, myRange.endIndex - myRange.startIndex + 1
        )
        const insertIdx = insertAt > myRange.startIndex ? insertAt - block.length : insertAt
        this.paragraphs.splice(insertIdx, 0, ...block)
        this._mapsStale = true

        this._markStructuralDirty()
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        return { ok: true }
    }

    /**
     * Verschiebt einen Absatz an eine anker-definierte Position — typ-bewusst
     * wie Option+Arrow: Normal-Paragraph einzeln, Outline-Item mit komplettem
     * Subtree. Agent-Pendant zu moveParagraph/moveOutlineSubtree.
     * Spec: docs/archive/move-tool-specification.md.
     *
     * Ziel: GENAU EINER von afterParagraphId / beforeParagraphId.
     *   - afterParagraphId darf eine Separator-UUID sein = an den Anfang
     *     dieser Section (Konvention wie write_sketch).
     *   - beforeParagraphId muss ein Nicht-Separator sein.
     *
     * Quell- UND Ziel-Section muessen entsperrt sein. Wird die Quell-Section
     * durch den Move leer, wird ein leerer Absatz eingesetzt (Section-Schutz
     * wie deleteOutlineSubtree); der Implicit Head darf leer verschwinden.
     *
     * @param {string} paragraphId
     * @param {{afterParagraphId?: string, beforeParagraphId?: string}} target
     * @returns {{ok: boolean, reason?: string, noChange?: boolean, movedCount?: number}}
     */
    moveParagraphTo(paragraphId, target = {}) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx === -1) return { ok: false, reason: 'unknown_paragraph' }
        const para = this.paragraphs[idx]
        if (para.type === PARA_TYPE.SEPARATOR) return { ok: false, reason: 'is_separator' }

        const lockCheck = this._assertSectionUnlocked(paragraphId)
        if (!lockCheck.ok) return lockCheck

        const hasAfter = target.afterParagraphId != null
        const hasBefore = target.beforeParagraphId != null
        if (hasAfter === hasBefore) return { ok: false, reason: 'invalid_anchor' }

        const anchorId = hasAfter ? target.afterParagraphId : target.beforeParagraphId
        const aIdx = this.getParagraphIndex(anchorId)
        if (aIdx === -1) return { ok: false, reason: 'unknown_paragraph' }
        // beforeParagraphId auf einem Separator ist positionell wohldefiniert
        // (Ende der Vorgaenger-Section bzw. Dokumentanfang) — das Server-Tool
        // lehnt EXPLIZITE Separator-before-Anker ab, nutzt die Position aber
        // intern fuer die section_0-Uebersetzung (Dokumentanfang vor dem
        // ersten Separator).

        const range = para.isOutlineItem()
            ? this.getSubtreeRange(idx)
            : { startIndex: idx, endIndex: idx }
        if (aIdx >= range.startIndex && aIdx <= range.endIndex) {
            return { ok: false, reason: 'anchor_inside_moved_range' }
        }
        const movedCount = range.endIndex - range.startIndex + 1

        // Einfuegeposition im AKTUELLEN Array. after auf Separator = direkt
        // hinter dem Separator = Section-Anfang (gleiche Formel).
        const insertAt = hasBefore ? aIdx : aIdx + 1

        if (this._sectionLockedAtIndex(insertAt - 1)) {
            return { ok: false, reason: 'section_locked' }
        }

        if (insertAt === range.startIndex || insertAt === range.endIndex + 1) {
            return { ok: true, noChange: true, movedCount }
        }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        const block = this.paragraphs.splice(range.startIndex, movedCount)
        let insertIdx = insertAt > range.startIndex ? insertAt - movedCount : insertAt

        // Section-Schutz: bleibt die Quell-Section ohne Absatz zurueck,
        // leeren Platzhalter einsetzen. Implicit Head (startIndex 0 ohne
        // fuehrenden Separator) darf organisch verschwinden.
        const prevIsBoundary = range.startIndex === 0
            || this.paragraphs[range.startIndex - 1].type === PARA_TYPE.SEPARATOR
        const nextIsBoundary = range.startIndex >= this.paragraphs.length
            || this.paragraphs[range.startIndex].type === PARA_TYPE.SEPARATOR
        if (prevIsBoundary && nextIsBoundary && range.startIndex > 0) {
            const emptyPara = new Paragraph(this._getNextId(), '')
            this.paragraphs.splice(range.startIndex, 0, emptyPara)
            this._markParagraphDirty(emptyPara.id)
            if (insertIdx >= range.startIndex) insertIdx += 1
        }

        this.paragraphs.splice(insertIdx, 0, ...block)
        this._mapsStale = true

        // Outline-Tiefen-Invarianten an Quelle UND Ziel reparieren.
        this._correctOutlineDepths(Math.min(range.startIndex, insertIdx))

        this._markStructuralDirty()
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        return { ok: true, movedCount }
    }

    deleteOutlineSubtree(startIndex, endIndex) {
        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null
        const oldOutlineSelection = this._outlineSelection
            ? { ...this._outlineSelection } : null

        // Section-Schutz: wenn der gesamte Inhalt einer Section gelöscht wird,
        // leeren Absatz einfügen
        const firstPara = this.paragraphs[startIndex]
        const sectionBounds = this.getSectionBounds(firstPara.id)
        const sectionEmpty = startIndex <= sectionBounds.startIndex
            && endIndex >= sectionBounds.endIndex

        // Löschen
        this.paragraphs.splice(startIndex, endIndex - startIndex + 1)
        this._mapsStale = true

        // Leeren Absatz einfügen falls Section leer wäre
        if (sectionEmpty) {
            const emptyPara = new Paragraph(this._getNextId(), '')
            this.paragraphs.splice(startIndex, 0, emptyPara)
        }

        // Cursor setzen: auf den nächsten Nicht-Separator-Absatz ab startIndex
        let cursorIdx = Math.min(startIndex, this.paragraphs.length - 1)
        // Separator überspringen (vorwärts, dann rückwärts)
        if (this.paragraphs[cursorIdx].type === PARA_TYPE.SEPARATOR) {
            let found = false
            for (let i = cursorIdx + 1; i < this.paragraphs.length; i++) {
                if (this.paragraphs[i].type !== PARA_TYPE.SEPARATOR) {
                    cursorIdx = i; found = true; break
                }
            }
            if (!found) {
                for (let i = cursorIdx - 1; i >= 0; i--) {
                    if (this.paragraphs[i].type !== PARA_TYPE.SEPARATOR) {
                        cursorIdx = i; break
                    }
                }
            }
        }
        const cursorPara = this.paragraphs[cursorIdx]
        this.cursor = new Position(cursorPara.id, 0)

        // Outline-Tiefen korrigieren
        this._correctOutlineDepths(startIndex)

        this._outlineSelection = null
        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = oldSelection
            this._outlineSelection = oldOutlineSelection
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Fügt einen kopierten Outline-Subtree ein.
     * Passt outlineDepth relativ zum Zielort an.
     * @param {Paragraph[]} paragraphClones - Array von Paragraph-Klonen
     * @param {number} insertIndex - Index vor dem eingefügt wird
     * @returns {Function} Undo-Funktion
     */
    pasteOutlineSubtree(paragraphClones, insertIndex) {
        if (!paragraphClones || paragraphClones.length === 0) return () => {}

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null
        const oldOutlineSelection = this._outlineSelection
            ? { ...this._outlineSelection } : null

        // Tiefenberechnung
        const rootDepth = paragraphClones[0].outlineDepth
        const targetPara = this.paragraphs[insertIndex]
        let targetDepth
        if (targetPara.isOutlineItem()) {
            targetDepth = targetPara.outlineDepth  // Sibling
        } else {
            targetDepth = targetPara.outlineDepth + 1  // Kind
        }
        const delta = targetDepth - rootDepth

        // Neue Paragraphen erzeugen mit neuen IDs und angepasster Tiefe
        // ID-Mapping für interne Link-Umschreibung
        const idMap = new Map()
        const newParagraphs = []
        for (const clone of paragraphClones) {
            const newId = this._getNextId()
            idMap.set(clone.id, newId)
            const p = new Paragraph(newId, clone.text, clone.type)
            p.note = clone.note
            p.info = clone.info
            p.formats = {
                text: clone.formats.text.map(f => ({ ...f })),
                note: clone.formats.note.map(f => ({ ...f })),
                info: clone.formats.info.map(f => ({ ...f }))
            }
            p.links = {
                text: clone.links.text.map(l => ({ ...l })),
                note: clone.links.note.map(l => ({ ...l })),
                info: clone.links.info.map(l => ({ ...l }))
            }
            p.outlineDepth = Math.max(0, Math.min(EDITOR.MAX_OUTLINE_ITEM_DEPTH, clone.outlineDepth + delta))
            p.headingLevel = clone.headingLevel
            p.listType = clone.listType
            p.listLevel = clone.listLevel
            p.listStart = clone.listStart
            p.blockquote = clone.blockquote
            p.codeBlock = clone.codeBlock
            newParagraphs.push(p)
        }

        // Links umschreiben: interne Verweise auf neue IDs anpassen
        const cloneIdSet = new Set(paragraphClones.map(c => c.id))
        for (const p of newParagraphs) {
            for (const field of ['text', 'note', 'info']) {
                for (const link of p.links[field]) {
                    if (link.targetId && cloneIdSet.has(link.targetId)) {
                        link.targetId = idMap.get(link.targetId)
                    }
                }
            }
        }

        // Einfügen
        this.paragraphs.splice(insertIndex, 0, ...newParagraphs)
        this._mapsStale = true

        // Outline-Tiefen korrigieren (nach dem eingefügten Block)
        this._correctOutlineDepths(insertIndex + newParagraphs.length)

        // Cursor: nach dem eingefügten Block
        const afterIdx = Math.min(insertIndex + newParagraphs.length,
            this.paragraphs.length - 1)
        this.cursor = new Position(this.paragraphs[afterIdx].id, 0)

        this._outlineSelection = null
        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = oldSelection
            this._outlineSelection = oldOutlineSelection
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    // ===== Separator-Methoden =====

    /**
     * Prüft ob ein Absatz ein Separator ist.
     * @param {number} paragraphId - ID des Absatzes
     * @returns {boolean}
     */
    isSeparator(paragraphId) {
        const para = this.getParagraph(paragraphId)
        return para ? para.type === PARA_TYPE.SEPARATOR : false
    }

    /**
     * Gibt die Grenzen des Abschnitts zurück, in dem der Absatz liegt.
     * Ein Abschnitt ist begrenzt durch Separatoren oder Dokumentgrenzen.
     * @param {number} paragraphId - ID des Absatzes
     * @returns {{startIndex: number, endIndex: number}} Inklusive Grenzen (ohne Separatoren)
     */
    getSectionBounds(paragraphId) {
        const paraIndex = this.getParagraphIndex(paragraphId)
        if (paraIndex < 0) return { startIndex: 0, endIndex: this.paragraphs.length - 1 }

        // Wenn der Para selbst ein Separator ist, ist er der Anfang seiner eigenen Section
        // (nicht der zuvor liegenden) — Rückwärts-Suche überspringen.
        let startIndex = paraIndex
        if (this.paragraphs[paraIndex].type !== PARA_TYPE.SEPARATOR) {
            for (let i = paraIndex - 1; i >= 0; i--) {
                if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) break
                startIndex = i
            }
        }

        // Endgrenze: vorwärts suchen bis Separator oder Dokumentende
        let endIndex = paraIndex
        for (let i = paraIndex + 1; i < this.paragraphs.length; i++) {
            if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) break
            endIndex = i
        }

        return { startIndex, endIndex }
    }

    /**
     * Prüft ob der Abschnitt, in dem der Paragraph liegt, gesperrt ist.
     * Gesperrt = der besitzende Separator hat locked === true,
     * oder (erster Abschnitt) _firstSectionLocked === true.
     * @param {number} paragraphId
     * @returns {boolean}
     */
    isSectionLocked(paragraphId) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx < 0) return false
        // Rückwärts zum besitzenden Separator
        for (let i = idx; i >= 0; i--) {
            if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) {
                return !!this.paragraphs[i].locked
            }
        }
        // Kein Separator gefunden → erster Abschnitt
        return this._firstSectionLocked
    }

    /**
     * Gibt den besitzenden Separator für einen Paragraph zurück.
     * Für den ersten Abschnitt (vor erstem Separator) → null.
     * @param {number} paragraphId
     * @returns {Paragraph|null}
     */
    getSectionSeparator(paragraphId) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx < 0) return null
        for (let i = idx; i >= 0; i--) {
            if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) {
                return this.paragraphs[i]
            }
        }
        return null
    }

    /**
     * Toggelt die Sperre des Abschnitts, in dem der Paragraph liegt.
     * Setzt locked direkt (ohne COW/_mutate) → kein Undo-Eintrag.
     * @param {number} paragraphId
     * @returns {boolean} Neuer Lock-Status
     */
    toggleSectionLock(paragraphId) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx < 0) return false
        // Rückwärts zum besitzenden Separator
        for (let i = idx; i >= 0; i--) {
            if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) {
                // Direkt setzen — KEIN COW, kein Undo
                this.paragraphs[i].locked = !this.paragraphs[i].locked
                // STRUCTURAL dirty + inkrementell: Lock betrifft Marker aller Absätze
                // im Abschnitt. STRUCTURAL erzwingt vollen Screen-Rebuild (fillRect
                // in Separator-Rendering braucht das). Separator als dirty-ID beibehalten
                // damit Persistence nur diesen Absatz flusht.
                this._markStructuralDirtyIncremental()
                this._dirtyParagraphIds.add(this.paragraphs[i].id)
                return this.paragraphs[i].locked
            }
        }
        // Erster Abschnitt (vor erstem Separator)
        this._firstSectionLocked = !this._firstSectionLocked
        this._markStructuralDirty()
        return this._firstSectionLocked
    }

    /**
     * Toggelt Section-Collapse auf einem Separator-Paragraph.
     * Nur an Paragraphen vom Typ 'separator' zulässig — sonst Ping via _actionBlocked.
     * Undo-fähig via COW-Snapshot.
     * @param {number} paragraphId
     * @returns {boolean|null} Neuer Collapse-Status, null bei ungültigem Target
     */
    toggleSectionCollapse(paragraphId) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx < 0) { this._actionBlocked = true; return null }
        const sep = this.paragraphs[idx]
        if (!sep || sep.type !== PARA_TYPE.SEPARATOR) {
            this._actionBlocked = true
            return null
        }

        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null

        const mutated = this._mutate(sep)
        mutated.sectionCollapsed = !mutated.sectionCollapsed

        // Selektion aufheben — kann nach dem Toggle in verstecktem Bereich liegen.
        this.selection = null

        this._markStructuralDirty()
        this._markParagraphDirty(mutated.id)
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = oldSelection
            this._mapsStale = true
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        return mutated.sectionCollapsed
    }

    /**
     * Toggelt Active-Collapse auf einem aktiven Absatz.
     * Nur zulässig wenn para.isOutlineItem() === true und NICHT separator.
     * Undo-fähig via COW-Snapshot.
     * @param {number} paragraphId
     * @returns {boolean|null} Neuer Collapse-Status, null bei ungültigem Target
     */
    toggleActiveCollapse(paragraphId) {
        const idx = this.getParagraphIndex(paragraphId)
        if (idx < 0) { this._actionBlocked = true; return null }
        const para = this.paragraphs[idx]
        if (!para || para.type === PARA_TYPE.SEPARATOR || !para.isOutlineItem()) {
            this._actionBlocked = true
            return null
        }

        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null

        const mutated = this._mutate(para)
        mutated.activeCollapsed = !mutated.activeCollapsed

        this.selection = null

        this._markStructuralDirty()
        this._markParagraphDirty(mutated.id)
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = oldSelection
            this._mapsStale = true
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        return mutated.activeCollapsed
    }

    /**
     * Fügt einen Separator vor dem aktuellen Cursor-Absatz ein.
     * @returns {Function} Undo-Funktion
     */
    insertSeparator() {
        if (!this.cursor) return () => {}

        const paraIndex = this.getParagraphIndex(this.cursor.paragraphId)
        if (paraIndex < 0) return () => {}

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor.clone()

        // Separator-Paragraph erstellen
        const sep = new Paragraph(this._getNextId(), '', 'separator')
        sep.outlineDepth = 0
        sep._gen = this._gen

        // Vor aktuellem Cursor-Absatz einfügen
        this.paragraphs.splice(paraIndex, 0, sep)
        this._mapsStale = true

        // ZB-Zerteilung: Passive Absätze nach dem Separator,
        // die zu einem aktiven Absatz VOR dem Separator gehörten → outlineDepth = 0
        for (let i = paraIndex + 1; i < this.paragraphs.length; i++) {
            const p = this.paragraphs[i]
            if (p.type === PARA_TYPE.SEPARATOR) break
            if (p.isOutlineItem()) break  // Erster aktiver Absatz im neuen Abschnitt — ab hier gilt eigene Struktur
            if (p.outlineDepth > 0) {
                const mp = this._mutate(p)
                mp.outlineDepth = 0
            }
        }

        // Separator-Invariante sicherstellen
        this._ensureSectionInvariant()

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Wandelt einen normalen Absatz in einen Separator um (===-Tipp-Makro).
     * Der Absatz behält seine ID (Paragraph-Level-Sync sieht nur eine
     * Änderung, keinen Delete+Insert). Label startet leer, Tiefe 0.
     * @param {number|string} paraId
     * @returns {number|string|null} Separator-ID oder null wenn nicht umwandelbar
     */
    convertParagraphToSeparator(paraId) {
        const paraIndex = this.getParagraphIndex(paraId)
        if (paraIndex < 0) return null
        if (!this.paragraphs[paraIndex].isNormal()) return null

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        const sep = this._mutate(this.paragraphs[paraIndex])
        sep.type = PARA_TYPE.SEPARATOR
        sep.text = ''
        sep.formats.text = []
        sep.links.text = []
        sep.outlineDepth = 0

        // ZB-Zerteilung: Passive Absätze nach dem Separator,
        // die zu einem aktiven Absatz VOR dem Separator gehörten → outlineDepth = 0
        for (let i = paraIndex + 1; i < this.paragraphs.length; i++) {
            const p = this.paragraphs[i]
            if (p.type === PARA_TYPE.SEPARATOR) break
            if (p.isOutlineItem()) break  // Erster aktiver Absatz im neuen Abschnitt — ab hier gilt eigene Struktur
            if (p.outlineDepth > 0) {
                const mp = this._mutate(p)
                mp.outlineDepth = 0
            }
        }

        // Separator-Invariante sicherstellen (leerer Folge-Absatz bei Bedarf)
        this._ensureSectionInvariant()

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            if (oldCursor) this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return sep.id
    }

    /**
     * Scan-Helper: findet den innersten aktiven Section-Collapse-Host-Index
     * BEVOR paraIdx (exklusiv). Nutzt einen Stack analog zu rebuildLines.
     * @param {number} paraIdx
     * @returns {number} Host-Index oder -1
     */
    _findActiveCollapseHostBefore(paraIdx) {
        const stack = []
        for (let i = 0; i < paraIdx; i++) {
            const p = this.paragraphs[i]
            if (p.type !== PARA_TYPE.SEPARATOR) continue
            while (stack.length > 0 &&
                p.outlineDepth <= this.paragraphs[stack[stack.length - 1]].outlineDepth) {
                stack.pop()
            }
            if (p.sectionCollapsed) stack.push(i)
        }
        return stack.length > 0 ? stack[stack.length - 1] : -1
    }

    /**
     * Scan-Helper: findet den Index des ersten sichtbaren Separators NACH
     * paraIdx (exklusiv). Simuliert den Collapse-Tracker ab 0.
     * @param {number} paraIdx
     * @returns {number} Separator-Index oder -1
     */
    _findNextVisibleSeparatorAfter(paraIdx) {
        let sectionCollapseDepth = null
        for (let i = 0; i <= paraIdx; i++) {
            const p = this.paragraphs[i]
            if (sectionCollapseDepth !== null && p.type === PARA_TYPE.SEPARATOR &&
                p.outlineDepth <= sectionCollapseDepth) {
                sectionCollapseDepth = null
            }
            if (p.type === PARA_TYPE.SEPARATOR && p.sectionCollapsed && sectionCollapseDepth === null) {
                sectionCollapseDepth = p.outlineDepth
            }
        }
        for (let i = paraIdx + 1; i < this.paragraphs.length; i++) {
            const p = this.paragraphs[i]
            if (sectionCollapseDepth !== null && p.type === PARA_TYPE.SEPARATOR &&
                p.outlineDepth <= sectionCollapseDepth) {
                sectionCollapseDepth = null
            }
            if (p.type === PARA_TYPE.SEPARATOR && sectionCollapseDepth === null) {
                return i
            }
            if (p.type === PARA_TYPE.SEPARATOR && p.sectionCollapsed && sectionCollapseDepth === null) {
                sectionCollapseDepth = p.outlineDepth
            }
        }
        return -1
    }

    /**
     * Klappt den Ziel-Separator und alle Ancestor-Separatoren auf, damit der
     * Ziel-Bereich beim Link-Sprung sichtbar wird.
     * View-Operation: kein Undo-Push, aber Cloud-Sync via _markParagraphDirty.
     * @param {string} targetId - ID des Ziel-Separators
     * @returns {boolean} true wenn mindestens ein Sep aufgeklappt wurde
     */
    expandSectionForLinkTarget(targetId) {
        const targetIdx = this.getParagraphIndex(targetId)
        if (targetIdx < 0) return false
        const target = this.paragraphs[targetIdx]
        if (!target || target.type !== PARA_TYPE.SEPARATOR) return false

        // Ancestor-Stack ueber alle Seps vor targetIdx aufbauen, dann Siblings
        // (Tiefe >= target.outlineDepth) wieder poppen — uebrig bleibt die echte
        // Ancestor-Hierarchie mit strikt kleineren Tiefen.
        const stack = []
        for (let i = 0; i < targetIdx; i++) {
            const p = this.paragraphs[i]
            if (p.type !== PARA_TYPE.SEPARATOR) continue
            while (stack.length > 0 &&
                this.paragraphs[stack[stack.length - 1]].outlineDepth >= p.outlineDepth) {
                stack.pop()
            }
            stack.push(i)
        }
        while (stack.length > 0 &&
            this.paragraphs[stack[stack.length - 1]].outlineDepth >= target.outlineDepth) {
            stack.pop()
        }

        const toExpandIdxs = []
        for (const i of stack) {
            if (this.paragraphs[i].sectionCollapsed) toExpandIdxs.push(i)
        }
        if (target.sectionCollapsed) toExpandIdxs.push(targetIdx)

        if (toExpandIdxs.length === 0) return false

        this._gen++
        for (const i of toExpandIdxs) {
            const mutated = this._mutate(this.paragraphs[i])
            mutated.sectionCollapsed = false
            this._markParagraphDirty(mutated.id)
        }
        this._markStructuralDirtyIncremental()

        return true
    }

    /**
     * Setzt die Gliederungstiefe eines Separators (0-6).
     * Bei collapsem Separator wird der gesamte Sub-Bereich um Delta mitverschoben.
     * Indent-Limits (siehe pane-formatting-specification.md §5.1):
     *   — sichtbarer Separator, > : newDepth ≤ Tiefe des vorhergehenden collapse-Hosts
     *   — sichtbarer Separator, < : newDepth ≥ Tiefe des ersten nachfolgenden sichtbaren Sep
     *   — collapser Separator: alle inneren Tiefen + Delta müssen in [0, MAX] liegen
     * Bei Verletzung: _actionBlocked = true, keine Änderung.
     * @param {number} paragraphId - ID des Separator-Paragraphs
     * @param {number} depth - Neue Tiefe (0-6)
     * @returns {Function} Undo-Funktion
     */
    setSeparatorDepth(paragraphId, depth) {
        if (depth < 0 || depth > EDITOR.MAX_SEPARATOR_DEPTH) return () => {}
        const para = this.getParagraph(paragraphId)
        if (!para || para.type !== PARA_TYPE.SEPARATOR) return () => {}
        // Background-Separator hat fixe Tiefe
        if (para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return () => {}
        if (para.outlineDepth === depth) return () => {}

        const paraIdx = this.getParagraphIndex(paragraphId)
        const oldDepth = para.outlineDepth
        const delta = depth - oldDepth

        // ── Constraint-Checks ───────────────────────────────────────────
        if (para.sectionCollapsed) {
            // Collapster Separator: Alle Sub-Separatoren im Section-Bereich
            // müssen mit-verschoben werden können, ohne max/min zu verletzen.
            for (let i = paraIdx + 1; i < this.paragraphs.length; i++) {
                const p = this.paragraphs[i]
                if (p.type === PARA_TYPE.SEPARATOR && p.outlineDepth <= oldDepth) break
                if (p.type === PARA_TYPE.SEPARATOR) {
                    const nd = p.outlineDepth + delta
                    if (nd < 0 || nd > EDITOR.MAX_SEPARATOR_DEPTH) {
                        this._actionBlocked = true
                        return () => {}
                    }
                }
            }
        } else {
            // Sichtbarer Separator
            if (delta > 0) {
                const hostIdx = this._findActiveCollapseHostBefore(paraIdx)
                if (hostIdx >= 0) {
                    const hostDepth = this.paragraphs[hostIdx].outlineDepth
                    if (depth > hostDepth) {
                        this._actionBlocked = true
                        return () => {}
                    }
                }
            } else if (delta < 0) {
                const nextVisIdx = this._findNextVisibleSeparatorAfter(paraIdx)
                if (nextVisIdx >= 0) {
                    const nextDepth = this.paragraphs[nextVisIdx].outlineDepth
                    if (depth < nextDepth) {
                        this._actionBlocked = true
                        return () => {}
                    }
                }
            }
        }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()

        if (para.sectionCollapsed) {
            // Alle Separatoren im Sub-Bereich (inkl. para selbst) mit Delta verschieben
            const mpSelf = this._mutate(para)
            mpSelf.outlineDepth = depth
            for (let i = paraIdx + 1; i < this.paragraphs.length; i++) {
                const p = this.paragraphs[i]
                if (p.type === PARA_TYPE.SEPARATOR && p.outlineDepth <= oldDepth) break
                if (p.type === PARA_TYPE.SEPARATOR) {
                    const mp2 = this._mutate(p)
                    mp2.outlineDepth = mp2.outlineDepth + delta
                    this._markParagraphDirty(mp2.id)
                }
            }
        } else {
            const mp = this._mutate(para)
            mp.outlineDepth = depth
        }

        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(paragraphId)

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Fügt einen neuen Separator VOR dem angegebenen Separator ein.
     * @param {number} separatorIndex - Index des bestehenden Separators
     * @param {number} depth - Gliederungstiefe für den neuen Separator (0-6)
     * @returns {Function} Undo-Funktion
     */
    insertSeparatorBefore(separatorIndex, depth = 0) {
        if (separatorIndex < 0 || separatorIndex >= this.paragraphs.length) return () => {}
        if (this.paragraphs[separatorIndex].type !== PARA_TYPE.SEPARATOR) return () => {}
        if (depth < 0 || depth > EDITOR.MAX_SEPARATOR_DEPTH) return () => {}

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Neuen Separator + leeren Absatz VOR dem bestehenden einfügen
        const sep = new Paragraph(this._getNextId(), '', 'separator')
        sep.outlineDepth = depth
        sep._gen = this._gen
        const emptyPara = new Paragraph(this._getNextId(), '')
        emptyPara._gen = this._gen

        // Reihenfolge: ... sep emptyPara [bestehender Separator] ...
        this.paragraphs.splice(separatorIndex, 0, sep, emptyPara)
        this._mapsStale = true

        // Separator-Invariante sicherstellen
        this._ensureSectionInvariant()

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            if (oldCursor) this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Fuegt einen neuen Separator NACH dem angegebenen Separator ein.
     * Reihenfolge: [bestehender Separator] ... emptyPara newSep ...
     * @param {number} separatorIndex - Index des bestehenden Separators
     * @param {number} depth - Gliederungstiefe fuer den neuen Separator (0-6)
     * @returns {{undoFn: Function, separatorId: number}} Undo-Funktion und ID des neuen Separators
     */
    insertSeparatorAfter(separatorIndex, depth = 0) {
        if (separatorIndex < 0 || separatorIndex >= this.paragraphs.length) return { undoFn: () => {}, separatorId: -1 }
        if (this.paragraphs[separatorIndex].type !== PARA_TYPE.SEPARATOR) return { undoFn: () => {}, separatorId: -1 }
        if (depth < 0 || depth > EDITOR.MAX_SEPARATOR_DEPTH) return { undoFn: () => {}, separatorId: -1 }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Einfuegeposition: direkt nach dem aktuellen Separator
        const insertAt = separatorIndex + 1

        // Leerer Absatz + neuer Separator
        const emptyPara = new Paragraph(this._getNextId(), '')
        emptyPara._gen = this._gen
        const sep = new Paragraph(this._getNextId(), '', 'separator')
        sep.outlineDepth = depth
        sep._gen = this._gen

        // Reihenfolge: ... [letzter Absatz des Abschnitts] emptyPara sep ...
        this.paragraphs.splice(insertAt, 0, emptyPara, sep)
        this._mapsStale = true

        // Separator-Invariante sicherstellen
        this._ensureSectionInvariant()

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            if (oldCursor) this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return { undoFn, separatorId: sep.id }
    }

    // ===== Agent/Review Apply-API =====
    // Diese Methoden werden vom ReviewController aufgerufen, wenn der User
    // einen Agent-Vorschlag annimmt. Sie folgen dem COW-Undo-Muster der
    // bestehenden Mutations-Methoden (_splitParagraphText, deleteParagraph).
    // Apply passiert atomar pro Vorschlag — jede Annahme ist ein eigener
    // Undo-Schritt.

    /**
     * Ersetzt den Text eines bestehenden Paragraphen komplett. Wird vom
     * ReviewController bei angenommenem suggest_rewrite aufgerufen.
     *
     * Markdown-autoritativ: Block-Felder (headingLevel, listType, listLevel,
     * blockquote) im opts ueberschreiben immer — fehlen sie, gehen sie auf
     * Default (0/null/false). Fehlt formats/links → leere Arrays.
     *
     * @param {string} paragraphId
     * @param {string} newText
     * @param {object} [opts]
     * @param {Array} [opts.formats] - FormatSpan[] fuer text
     * @param {Array} [opts.links] - LinkEntry[] fuer text
     * @param {number} [opts.headingLevel=0]
     * @param {string|null} [opts.listType=null]
     * @param {number} [opts.listLevel=0]
     * @param {boolean} [opts.blockquote=false]
     * @param {boolean} [opts.moveCursor=true]
     * @returns {{ok: boolean, reason?: string}}
     */
    replaceParagraphText(paragraphId, newText, opts = {}) {
        const paraIndex = this.getParagraphIndex(paragraphId)
        if (paraIndex === -1) return { ok: false, reason: 'paragraph_not_found' }

        const para = this.paragraphs[paraIndex]
        if (para.type === PARA_TYPE.SEPARATOR) return { ok: false, reason: 'is_separator' }

        // Lock-Guard (Phase 3) — gesperrte Section ist read-only fuer alle Apply-Tools.
        const lockCheck = this._assertSectionUnlocked(paragraphId)
        if (!lockCheck.ok) return lockCheck

        // COW-Snapshot (analog deleteParagraph)
        this._gen++
        const oldText = para.text
        const oldFormats = para.formats.text.map(f => ({ ...f }))
        const oldLinks = para.links.text.map(l => ({ ...l }))
        const oldHeadingLevel = para.headingLevel
        const oldListType = para.listType
        const oldListLevel = para.listLevel
        const oldBlockquote = para.blockquote
        const oldCodeBlock = para.codeBlock

        const mutated = this._mutate(para)
        mutated.text = newText
        mutated.formats.text = Array.isArray(opts.formats) ? opts.formats.map(s => ({ ...s })) : []
        mutated.links.text = Array.isArray(opts.links) ? opts.links.map(l => ({ ...l })) : []
        // Block-Felder autoritativ — was nicht im Patch steht, geht auf Default.
        mutated.headingLevel = Number.isInteger(opts.headingLevel) ? opts.headingLevel : 0
        mutated.listType = opts.listType === 'bullet' || opts.listType === 'numbered' ? opts.listType : null
        mutated.listLevel = Number.isInteger(opts.listLevel) ? opts.listLevel : 0
        mutated.blockquote = !!opts.blockquote
        mutated.codeBlock = !!opts.codeBlock

        this._markParagraphDirty(paragraphId)
        this._markStructuralDirtyIncremental()

        // Cursor an die Apply-Stelle: Ende des neuen Texts. Damit der User
        // direkt sieht, wo der Vorschlag akzeptiert wurde, statt der Cursor
        // irgendwo im alten Pane stehen bleibt.
        // Review v1.4 #17: Auto-Apply-Pfade rufen mit moveCursor:false —
        // Modell mutiert im Hintergrund, der User schreibt vielleicht
        // gerade woanders.
        if (opts.moveCursor !== false) {
            this.cursor = new Position(paragraphId, newText.length, 'text')
            this.selection = null
        }

        this._pushUndo(() => {
            const p = this._mutate(this.getParagraph(paragraphId))
            if (!p) return
            p.text = oldText
            p.formats.text = oldFormats
            p.links.text = oldLinks
            p.headingLevel = oldHeadingLevel
            p.listType = oldListType
            p.listLevel = oldListLevel
            p.blockquote = oldBlockquote
            p.codeBlock = oldCodeBlock
            this._markParagraphDirty(paragraphId)
            this._markStructuralDirty()
            // Beim UNDO Cursor wieder an die Aenderungs-Stelle (Ende des
            // restaurierten Texts), nicht an die Pre-Apply-Position. Sonst
            // wirkt der Sprung des Cursors nach UNDO desorientierend.
            this.cursor = new Position(paragraphId, oldText.length, 'text')
            this.selection = null
        })
        // Konsolidierungs-Gruppe explizit brechen — nächste User-Eingabe
        // soll keinen Undo-Punkt mit dem Review-Apply zusammenziehen.
        this._resetUndoConsolidation()

        return { ok: true }
    }

    /**
     * Prüft, ob ein Absatz ein komplett leerer Normal-Absatz ist (kein Text,
     * keine Notiz/Info, keine Formate/Links, kein Block-Format). Nur solche
     * Absätze werden beim implicit-head Insert "geschluckt" (ersetzt statt
     * davor eingefügt) — siehe insertParagraphAfter.
     * @param {Paragraph} p
     * @returns {boolean}
     */
    _isEmptyNormalParagraph(p) {
        return !!p
            && p.type === 'normal'
            && p.text === ''
            && p.note === ''
            && p.info === ''
            && p.formats.text.length === 0
            && p.links.text.length === 0
            && p.headingLevel === 0
            && !p.listType
            && !p.blockquote
            && !p.codeBlock
    }

    /**
     * Fuegt einen neuen Paragraph nach dem angegebenen Anchor ein. Wird vom
     * ReviewController bei angenommenem suggest_insert aufgerufen.
     *
     * Wenn afterParagraphId null/-1 ist, wird am Dokumentanfang eingefuegt
     * (vor dem ersten Absatz).
     *
     * Markdown-autoritativ: opts uebergibt formats/links + Block-Felder.
     * Default-Werte fuer Block-Felder = 0/null/false (ungesetzt = nicht da).
     *
     * @param {string|null} afterParagraphId
     * @param {string} newText
     * @param {object} [opts]
     * @param {Array} [opts.formats]
     * @param {Array} [opts.links]
     * @param {number} [opts.headingLevel=0]
     * @param {string|null} [opts.listType=null]
     * @param {number} [opts.listLevel=0]
     * @param {boolean} [opts.blockquote=false]
     * @param {boolean} [opts.moveCursor=true]
     * @returns {{ok: boolean, newParagraphId?: string, reason?: string}}
     */
    insertParagraphAfter(afterParagraphId, newText, opts = {}) {
        // Schlucken: Insert am Dokumentanfang über das implicit-head Token
        // (section_0) ERSETZT einen führenden leeren Normal-Absatz, statt
        // davor einzufügen. So füllt suggest_insert ein leeres Dokument in
        // einem Call (sonst bliebe der initiale leere Absatz übrig). Der
        // ReviewController verkettet Folge-Inserts über newParagraphId — die
        // hier zurückgegebene ID des wiederverwendeten Absatzes.
        if (afterParagraphId === IMPLICIT_HEAD_ID
                && this.paragraphs.length > 0
                && this._isEmptyNormalParagraph(this.paragraphs[0])) {
            const targetId = this.paragraphs[0].id
            const res = this.replaceParagraphText(targetId, newText, opts)
            return (res && res.ok) ? { ok: true, newParagraphId: targetId } : res
        }

        let insertIndex
        let depth = 1

        if (afterParagraphId == null || afterParagraphId === -1
                || afterParagraphId === IMPLICIT_HEAD_ID) {
            // Vor dem ersten Paragraph einfuegen (auch bei nicht-geschlucktem
            // implicit-head Anker — z.B. erster Absatz nicht leer).
            insertIndex = 0
        } else {
            const anchorIndex = this.getParagraphIndex(afterParagraphId)
            if (anchorIndex === -1) return { ok: false, reason: 'anchor_not_found' }
            insertIndex = anchorIndex + 1
            const anchor = this.paragraphs[anchorIndex]
            // Tiefe vom Anchor erben — der eingefuegte Para ist immer ein
            // Normal-Para (Outline-Items werden ueber dedizierte Tools angelegt).
            depth = anchor.outlineDepth

            // Lock-Guard (Phase 3) — gesperrte Section blockt Insert-Apply.
            const lockCheck = this._assertSectionUnlocked(afterParagraphId)
            if (!lockCheck.ok) return lockCheck
        }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        // UNDO-Cursor-Anker: nach dem UNDO ist der eingefuegte Para weg, also
        // ans Ende des Vorgaengers (afterParagraphId), oder — beim Insert am
        // Dokumentanfang — Pos 0 des urspruenglichen ersten Paragraphen. Das
        // ist die natuerliche "Naht-Stelle", an der der Insert war.
        let undoCursorParaId = null
        let undoCursorOffset = 0
        if (afterParagraphId != null && afterParagraphId !== -1
                && afterParagraphId !== IMPLICIT_HEAD_ID) {
            const anchor = this.paragraphs[insertIndex - 1]
            undoCursorParaId = anchor.id
            undoCursorOffset = anchor.text.length
        } else if (oldParagraphs.length > 0) {
            undoCursorParaId = oldParagraphs[0].id
            undoCursorOffset = 0
        }

        const newPara = new Paragraph(this._getNextId(), newText)
        newPara._gen = this._gen
        newPara.outlineDepth = depth
        if (Array.isArray(opts.formats)) newPara.formats.text = opts.formats.map(s => ({ ...s }))
        if (Array.isArray(opts.links)) newPara.links.text = opts.links.map(l => ({ ...l }))
        if (Number.isInteger(opts.headingLevel)) newPara.headingLevel = opts.headingLevel
        if (opts.listType === 'bullet' || opts.listType === 'numbered') newPara.listType = opts.listType
        if (Number.isInteger(opts.listLevel)) newPara.listLevel = opts.listLevel
        if (opts.blockquote) newPara.blockquote = true
        if (opts.codeBlock) newPara.codeBlock = true

        this.paragraphs.splice(insertIndex, 0, newPara)
        this._mapsStale = true

        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(newPara.id)

        // Cursor an die Apply-Stelle: Ende des eingefuegten Absatzes.
        // Review v1.4 #17: Auto-Apply-Pfade rufen mit moveCursor:false.
        if (opts.moveCursor !== false) {
            this.cursor = new Position(newPara.id, newText.length, 'text')
            this.selection = null
        }

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._markStructuralDirty()
            // Beim UNDO Cursor an die Naht-Stelle (siehe oben).
            if (undoCursorParaId != null) {
                this.cursor = new Position(undoCursorParaId, undoCursorOffset, 'text')
            }
            this.selection = null
        })
        this._resetUndoConsolidation()

        return { ok: true, newParagraphId: newPara.id }
    }

    /**
     * Löscht einen Absatz komplett (text, note, info).
     * Cursor wird auf Position 0 des Folgeabsatzes gesetzt.
     * Background-Separator kann nicht gelöscht werden.
     * @param {number} paragraphId - ID des zu löschenden Absatzes
     */
    /**
     * Loescht einen Absatz komplett. Im Default-Modus (User-Aktion via Cmd+D
     * oder Context-Menu) wird beim UNDO die Pre-Delete-Cursor-Position
     * wiederhergestellt — der Editor verhaelt sich wie gewohnt.
     *
     * Mit `opts.reviewApply = true` (Aufruf vom ReviewController) wird beim
     * UNDO der Cursor stattdessen an die Stelle der Aenderung gesetzt
     * (Pos 0 des wiederhergestellten Absatzes). So sieht der User nach Ctrl-Z
     * direkt, wo der Vorschlag rueckgaengig gemacht wurde — sonst kann der
     * Cursor irgendwo im Dokument landen, wo er vor dem Accept stand.
     *
     * @param {number} paragraphId
     * @param {object} [opts]
     * @param {boolean} [opts.reviewApply=false]
     * @returns {{ok: boolean, reason?: string}|undefined} Phase-3-Aufrufer (ReviewController,
     *   Auto-Apply-Pfad) lesen das Ergebnis; alte User-Cmd+D-Pfade ignorieren es weiterhin
     *   (rueckwaertskompatibel, da der nicht-existente Absatz oder Background-Separator
     *   einfach ein No-Op war und das auch bleibt).
     */
    deleteParagraph(paragraphId, opts = {}) {
        const paraIndex = this.getParagraphIndex(paragraphId)
        if (paraIndex === -1) return { ok: false, reason: 'paragraph_not_found' }

        const para = this.paragraphs[paraIndex]

        // Background-Separator nicht löschbar
        if (para.type === PARA_TYPE.SEPARATOR && para.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            return { ok: false, reason: 'is_background_separator' }
        }

        // Lock-Guard (Phase 3) — gesperrte Section ist read-only fuer Apply-Tools.
        const lockCheck = this._assertSectionUnlocked(paragraphId)
        if (!lockCheck.ok) return lockCheck

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        this.paragraphs.splice(paraIndex, 1)
        // Lookup-Maps sofort invalidieren — _correctOutlineDepths ruft ggf.
        // _mutate() → getParagraphIndex() auf, das sonst mit stalem _indexMap
        // den falschen Slot ueberschreibt (Zombie-Absatz nach Cmd+D).
        this._mapsStale = true

        // Mindestens ein Absatz muss übrig bleiben
        if (this.paragraphs.length === 0) {
            this.paragraphs.push(new Paragraph(this._getNextId(), ''))
        }

        // Section-Invariante sicherstellen
        this._ensureSectionInvariant()

        // Cursor auf Folgeabsatz (oder letzten) setzen, Position 0, Feld text.
        // Review v1.4 #17: Auto-Apply-Pfade rufen mit moveCursor:false.
        if (opts.moveCursor !== false) {
            const newIdx = Math.min(paraIndex, this.paragraphs.length - 1)
            this.cursor = new Position(this.paragraphs[newIdx].id, 0, 'text')
            this.selection = null
        }

        // Outline-Tiefen korrigieren (nur ab der Löschstelle — der Vorgänger hat sich nicht geändert)
        this._correctOutlineDepths(paraIndex)

        this._markStructuralDirtyIncremental()
        // Keine _markParagraphDirty noetig — Loeschung wird ueber paragraphOrder-Diff erkannt,
        // _correctOutlineDepths markiert ggf. korrigierte Absaetze

        const reviewApply = !!opts.reviewApply
        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._markStructuralDirty()
            if (reviewApply) {
                // Cursor an Pos 0 des wiederhergestellten Absatzes — die
                // sichtbare Stelle der Aenderung, nicht die alte User-Position.
                this.cursor = new Position(paragraphId, 0, 'text')
                this.selection = null
            } else {
                this.cursor = oldCursor
            }
        })
        this.charsSinceUndoPoint = 0
        return { ok: true }
    }

    /**
     * Lock-Guard fuer Phase-3 Apply-API. Akzeptiert paragraph_id ODER separator_id —
     * `isSectionLocked` traegt die Logik, weil sie rueckwaerts zum besitzenden
     * Separator wandert und beim Separator selbst sofort dessen `locked`-Flag
     * zurueckgibt.
     *
     * Heuristik: nicht throwen — `{ok, reason}` ist der einheitliche Apply-API-Stil
     * (replaceParagraphText/insertParagraphAfter/deleteParagraph), und der
     * ReviewController-Pfad lebt davon, dass keine Exceptions hochbubbeln.
     *
     * @param {number} paragraphIdOrSeparatorId
     * @returns {{ok: true}|{ok: false, reason: 'section_locked', separatorId: number}}
     */
    _assertSectionUnlocked(paragraphIdOrSeparatorId) {
        if (!this.isSectionLocked(paragraphIdOrSeparatorId)) return { ok: true }
        const sep = this.getSectionSeparator(paragraphIdOrSeparatorId)
        // Erster Abschnitt vor erstem Separator: kein owning Separator. Wir reichen
        // die paragraph_id selbst zurueck — der Server sieht das und kann eine
        // sinnvolle Fehlermeldung bauen.
        const separatorId = sep ? sep.id : paragraphIdOrSeparatorId
        return { ok: false, reason: 'section_locked', separatorId }
    }

    // ===== Outline-Redesign Phase 3b — Lazy-Cursor & Auto-Cleanup =====
    // Statt einen passiven Normal-Para via _activateTemporarily zu mutieren,
    // sitzt der Cursor in einer "Phantom"-Note-Zeile. Erst der erste Edit-
    // Tastendruck materialisiert das Outline-Item als realen Para. Verlaesst
    // der Cursor einen frisch materialisierten, leer gebliebenen Outline-
    // Item-Para, wird der Para als eigener Undo-Punkt geloescht (Spec §4.4).

    /**
     * Setzt den Pending-Cursor in eine Phantom-Note-Zeile. Der Anchor ist die
     * UUID des Paras, NACH dem der Outline-Item bei Materialisierung eingefuegt
     * wird (`null` = vor dem ersten Paragraph). `depth` ist die Default-Tiefe,
     * die das materialisierte Item erbt (vom umschliessenden Outline-Item oder
     * von der heutigen passiven-Note-Stelle abgeleitet).
     * Existierende Pending-Cursor werden ueberschrieben.
     *
     * @param {{insertAfterParagraphId: string|null, depth: number}} opts
     */
    setPendingCursor(opts) {
        if (!opts || typeof opts !== 'object') {
            if (this._pendingCursor) {
                this._pendingCursor = null
                this._markCursorDirty()
            }
            return
        }
        const insertAfterParagraphId = opts.insertAfterParagraphId ?? null
        const depth = Number.isInteger(opts.depth)
            ? Math.max(0, Math.min(10, opts.depth))
            : 0
        // v4: Pending kann entweder einen Outline-Item (default, Klick im
        // Outline-Pane) oder einen Normal-Para (Klick in Phantom-Main-Zeile,
        // Spec §3.4 + §4.3) materialisieren.
        const type = opts.type === PARA_TYPE.NORMAL ? PARA_TYPE.NORMAL : PARA_TYPE.OUTLINE_ITEM
        this._pendingCursor = {
            insertAfterParagraphId,
            depth,
            type,
        }
        this._markCursorDirty()
    }

    /**
     * Schutz-Invariante "Text darf nie verschwinden" (Spec §3.4/§4.3):
     * Sitzt der Cursor mit field='text' auf einem Outline-Item (Phantom-
     * Main-Zeile), ist das Edit-Ziel IMMER ein Normal-Para direkt nach dem
     * Item. Dieser Guard materialisiert ihn — und re-verankert dabei einen
     * ggf. stalen Pending-Cursor auf das Item unter dem Cursor.
     *
     * Ohne den Guard landet die Eingabe im nicht existierenden text-Slot
     * des Outline-Items: unsichtbar fuer den Sync und beim naechsten
     * COW-Clone verworfen (Paragraph.clone() haelt Outline-Items kanonisch
     * textlos) — genau so verschwand am 2026-07-17 getippter Text beim
     * Enter am Absatzende (Lesson 103).
     *
     * Aufrufer: insertText() und splitParagraph() als Model-Level-Choke-
     * Points — damit sind Tastatur, Touch, Diktat und Paste abgedeckt,
     * egal wie der Cursor in den Phantom-Zustand kam.
     *
     * @returns {boolean} true wenn ein Normal-Para materialisiert wurde
     */
    _materializePhantomTextTarget() {
        if (!this.cursor) return false
        if ((this.cursor.field || 'text') !== 'text') return false
        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para || !para.isOutlineItem()) return false
        this.setPendingCursor({
            insertAfterParagraphId: para.id,
            depth: 0,
            type: PARA_TYPE.NORMAL,
        })
        return this.materializePendingCursor().ok === true
    }

    /** Loescht den Pending-Cursor still (kein Materialize, kein Undo-Frame). */
    clearPendingCursor() {
        if (!this._pendingCursor) return
        this._pendingCursor = null
        this._markCursorDirty()
    }

    /** True wenn ein Pending-Cursor aktiv ist. */
    hasPendingCursor() {
        return this._pendingCursor !== null
    }

    /** Liefert das Pending-Cursor-Objekt (oder null). */
    getPendingCursor() {
        return this._pendingCursor
    }

    /**
     * Materialisiert den Pending-Cursor: legt ein leeres Outline-Item nach dem
     * Anchor an, markiert es als auto-created (fuer Cleanup), setzt cursor auf
     * dessen note offset 0, leert pending. Eigener Undo-Frame — Cmd+Z entfernt
     * den frisch angelegten Para. No-Op wenn kein Pending aktiv ist.
     *
     * @returns {{ok: boolean, newParagraphId?: string, reason?: string}}
     */
    materializePendingCursor() {
        if (!this._pendingCursor) return { ok: false, reason: 'no_pending' }
        const { insertAfterParagraphId, depth, type } = this._pendingCursor

        let insertIndex
        if (insertAfterParagraphId === null) {
            insertIndex = 0
        } else {
            const anchorIdx = this.getParagraphIndex(insertAfterParagraphId)
            if (anchorIdx === -1) {
                this._pendingCursor = null
                this._markCursorDirty()
                return { ok: false, reason: 'anchor_not_found' }
            }
            insertIndex = anchorIdx + 1
        }

        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null

        const isNormal = type === PARA_TYPE.NORMAL
        const newPara = new Paragraph(this._getNextId(), '', isNormal ? PARA_TYPE.NORMAL : PARA_TYPE.OUTLINE_ITEM)
        newPara._gen = this._gen
        if (!isNormal) {
            newPara.outlineDepth = depth
        }

        this.paragraphs.splice(insertIndex, 0, newPara)
        this._mapsStale = true
        this._autoCreatedParagraphIds.add(newPara.id)

        // Normal-Para → cursor in text; Outline-Item → cursor in note (Spec §2.4).
        this.cursor = new Position(newPara.id, 0, isNormal ? 'text' : 'note')
        this.selection = null
        this._pendingCursor = null

        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(newPara.id)

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this.cursor = oldCursor
            this.selection = oldSelection
            this._autoCreatedParagraphIds.delete(newPara.id)
            this._markStructuralDirty()
        })
        this._resetUndoConsolidation()

        return { ok: true, newParagraphId: newPara.id }
    }

    /**
     * Loescht einen auto-created Outline-Item-Para, sofern dessen Note nach
     * Trim leer ist. Eigener Undo-Frame (Spec §4.4: User kann den Cleanup
     * via Cmd+Z rueckgaengig machen). No-Op wenn paraId kein registrierter
     * Auto-Para, nicht (mehr) ein Outline-Item oder die Note nicht leer ist.
     *
     * Aufrufer ist typischerweise EditorInput beim Verlassen eines Paragraphs
     * (Pfeil raus, Tab, Pane-Wechsel, Klick anderswo). Der Cursor sollte
     * VORHER schon auf das neue Ziel zeigen — falls er noch auf paraId
     * zeigt, wird er defensiv an den Nachbar-Para gesetzt.
     *
     * @param {string} paraId
     * @returns {boolean} true wenn der Para geloescht wurde
     */
    cleanupAutoCreatedIfEmpty(paraId) {
        if (!this._autoCreatedParagraphIds.has(paraId)) return false
        const para = this.getParagraph(paraId)
        if (!para) {
            this._autoCreatedParagraphIds.delete(paraId)
            return false
        }
        if (para.type !== PARA_TYPE.OUTLINE_ITEM) {
            this._autoCreatedParagraphIds.delete(paraId)
            return false
        }
        if (para.note.trim() !== '') return false

        const paraIndex = this.getParagraphIndex(paraId)
        if (paraIndex === -1) {
            this._autoCreatedParagraphIds.delete(paraId)
            return false
        }

        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null

        this.paragraphs.splice(paraIndex, 1)
        this._mapsStale = true
        this._autoCreatedParagraphIds.delete(paraId)

        if (this.cursor && this.cursor.paragraphId === paraId) {
            const fallbackIdx = Math.min(paraIndex, this.paragraphs.length - 1)
            const fallback = this.paragraphs[Math.max(0, fallbackIdx)]
            this.cursor = fallback
                ? new Position(fallback.id, 0, 'text')
                : null
        }

        this._markStructuralDirtyIncremental()

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this.cursor = oldCursor
            this.selection = oldSelection
            // Auto-Marker bewusst NICHT wiederherstellen — der User hat den
            // Cleanup gerade rueckgaengig gemacht, will den Para also behalten.
            this._markStructuralDirty()
        })
        this._resetUndoConsolidation()

        return true
    }

    /**
     * Outline-Redesign Phase 3b §4.2 — Tiefen-Anpassung an einem Outline-Item.
     * Pure +/- 1 mit Cap 0..10, kein Active-Toggle, keine Kinder-Shift. Aufrufer
     * sind handleOutlineLeft/Right wenn der Para ein Outline-Item ist. No-Op
     * (return false) wenn Cap erreicht ist — Tab/Shift-Tab geht dann leer aus.
     *
     * Active-Collapse-Schutz §4.8.1:
     *  - Regel B: para selbst ist kollabiert → Tab/Shift-Tab blockt komplett
     *    (Tiefenaenderung wuerde den unsichtbaren Range neu definieren).
     *  - Regel A: Tiefen-Erhoehung darf nicht groesser werden als die Tiefe
     *    eines vorausgehenden kollabierten Items O — sonst wuerde para selbst
     *    in den unsichtbaren Range fallen und verschwinden.
     *
     * @param {Paragraph} para
     * @param {number} delta - +1 (Tab) oder -1 (Shift-Tab)
     * @returns {boolean} true wenn Tiefe sich aenderte
     */
    _adjustOutlineItemDepth(para, delta) {
        // Regel B: kollabiertes Item ist in seiner Tiefe eingefroren.
        if (para.activeCollapsed) {
            this._actionBlocked = true
            return false
        }

        const clamped = Math.max(0, Math.min(EDITOR.MAX_OUTLINE_ITEM_DEPTH, para.outlineDepth + delta))
        if (clamped === para.outlineDepth) return false

        // Regel A: vorausgehendes kollabiertes Item mit kleinerer Tiefe darf
        // nicht "umschliessen". Nur bei Tiefen-Erhoehung relevant.
        if (delta > 0) {
            const paraIdx = this.getParagraphIndex(para.id)
            for (let i = paraIdx - 1; i >= 0; i--) {
                const q = this.paragraphs[i]
                if (q.type === PARA_TYPE.SEPARATOR) break
                if (q.isOutlineItem() && q.outlineDepth < clamped) {
                    if (q.activeCollapsed) {
                        this._actionBlocked = true
                        return false
                    }
                    break  // erster qualifizierter Vorgaenger ist nicht kollabiert → OK
                }
            }
        }

        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null
        const oldSelection = this.selection ? this.selection.clone() : null

        const mutated = this._mutate(para)
        mutated.outlineDepth = clamped

        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(mutated.id)

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this.cursor = oldCursor
            this.selection = oldSelection
            this._markStructuralDirty()
        })
        this._resetUndoConsolidation()

        return true
    }

    // ===== Phase-3 Apply-API: Sketch / Note / Section =====
    // Diese Methoden sind die Empfaenger fuer die Phase-3 Auto-Apply-Tools
    // (write_sketch, write_note, create_section, update_section, delete_section).
    // Alle folgen dem COW-Undo-Pattern und liefern einen einzigen Undo-Schritt
    // pro Tool-Call — Cmd+Z rollt eine ganze Skizze, einen Note-Edit oder
    // eine Section-Erzeugung in einem Rutsch zurueck.

    /**
     * Atomarer Multi-Insert von neuen aktiven Skizzen-Absaetzen in einer Section.
     * Jedes Item wird zu einem neuen Outline-Item-Paragraph (`type='outline-item'`,
     * `note=...`, `outlineDepth=...`). Ein einziger Undo-Schritt deckt die ganze
     * Skizze ab.
     *
     * Sketch-Items werden nativ als `type='outline-item'` angelegt.
     *
     * Default-Anker (`afterParagraphId=null`): ans Ende der Section, vor dem
     * naechsten Separator. Wenn `afterParagraphId` eine Separator-UUID ist,
     * wird direkt nach dem Separator eingefuegt (= Section-Anfang).
     *
     * @param {string} separatorId - UUID des Section-Separators (Lock-Guard-Anker)
     * @param {string|null} afterParagraphId - Anker oder null fuer Section-Ende
     * @param {Array<{outlineDepth: number, note: string, noteFormats?: Array, noteLinks?: Array}>} items
     * @returns {{ok: boolean, reason?: string, paragraphIds?: Array<string>}}
     */
    insertSketchAfter(separatorId, afterParagraphId, items, opts = {}) {
        if (!Array.isArray(items) || items.length === 0) {
            return { ok: false, reason: 'no_items' }
        }

        // Implicit-head Section: kein realer Separator, also kein sepIndex /
        // sep / Lock-Check. Insert-Bereich = von Doc-Anfang bis erster echter
        // Separator (oder Doc-Ende). Anchor-Spezialfaelle bleiben symmetrisch:
        // null = Section-Ende, Token == Token = Section-Anfang, Paragraph =
        // muss in implicit-head liegen.
        const isImplicitHead = separatorId === IMPLICIT_HEAD_ID

        let sepIndex = -1
        if (!isImplicitHead) {
            sepIndex = this.getParagraphIndex(separatorId)
            if (sepIndex === -1) return { ok: false, reason: 'unknown_section' }
            const sep = this.paragraphs[sepIndex]
            if (sep.type !== PARA_TYPE.SEPARATOR) return { ok: false, reason: 'not_a_separator' }

            // Lock-Guard auf der Ziel-Section. (Implicit-head ist nie gesperrt
            // — Spec §6 / writeGuards.isSeparatorLocked.)
            const lockCheck = this._assertSectionUnlocked(separatorId)
            if (!lockCheck.ok) return lockCheck
        }

        // Insert-Index bestimmen: nach Anchor oder ans Section-Ende.
        // (Review v1.4 #16): Klar gestaffelte Faelle statt verschachtelter
        // Defensiven — Anchor=null, Anchor=Separator selbst, Anchor=Paragraph
        // in der Section.
        let insertIndex
        if (afterParagraphId == null) {
            if (isImplicitHead) {
                // Vor erstem echten Separator, oder Doc-Ende falls keiner existiert.
                insertIndex = this.paragraphs.length
                for (let i = 0; i < this.paragraphs.length; i++) {
                    if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) { insertIndex = i; break }
                }
            } else {
                // Section-Ende — vor dem naechsten Separator (oder am Dokumentende).
                insertIndex = this.paragraphs.length
                for (let i = sepIndex + 1; i < this.paragraphs.length; i++) {
                    if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) { insertIndex = i; break }
                }
            }
        } else if (afterParagraphId === separatorId) {
            // Spezialfall: Anchor IST der Separator (oder der Token bei
            // implicit-head) → Section-Anfang.
            insertIndex = isImplicitHead ? 0 : sepIndex + 1
        } else {
            // Anchor ist ein Paragraph — muss in derselben Section liegen.
            const anchorIdx = this.getParagraphIndex(afterParagraphId)
            if (anchorIdx === -1) return { ok: false, reason: 'anchor_not_found' }
            if (isImplicitHead) {
                // Implicit-head: Anchor muss VOR erstem Separator liegen UND
                // selbst kein Separator sein.
                if (this.paragraphs[anchorIdx].type === PARA_TYPE.SEPARATOR) {
                    return { ok: false, reason: 'anchor_outside_section' }
                }
                for (let i = 0; i < anchorIdx; i++) {
                    if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) {
                        return { ok: false, reason: 'anchor_outside_section' }
                    }
                }
            } else {
                const anchorSep = this.getSectionSeparator(afterParagraphId)
                if (!anchorSep || anchorSep.id !== separatorId) {
                    return { ok: false, reason: 'anchor_outside_section' }
                }
            }
            insertIndex = anchorIdx + 1
        }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        const newParas = items.map(item => {
            // Phase 4.2: nativ als Outline-Item anlegen.
            const p = new Paragraph(this._getNextId(), '', PARA_TYPE.OUTLINE_ITEM)
            p._gen = this._gen
            p.outlineDepth = Math.max(0, Math.min(EDITOR.MAX_OUTLINE_ITEM_DEPTH, item.outlineDepth | 0))
            p.note = item.note || ''
            if (Array.isArray(item.noteFormats)) {
                p.formats.note = item.noteFormats.map(s => ({ ...s }))
            }
            if (Array.isArray(item.noteLinks)) {
                p.links.note = item.noteLinks.map(l => ({ ...l }))
            }
            return p
        })

        this.paragraphs.splice(insertIndex, 0, ...newParas)
        this._mapsStale = true

        // Cursor → letztes eingefuegtes Item, Note-Feld am Anfang.
        // Review v1.4 #17: Auto-Apply-Pfade rufen mit moveCursor:false.
        if (opts.moveCursor !== false) {
            const lastNew = newParas[newParas.length - 1]
            this.cursor = new Position(lastNew.id, 0, 'note')
            this.selection = null
        }

        for (const p of newParas) this._markParagraphDirty(p.id)
        this._markStructuralDirtyIncremental()

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this._markStructuralDirty()
            if (oldCursor) this.cursor = oldCursor
            this.selection = null
        })
        this._resetUndoConsolidation()

        return { ok: true, paragraphIds: newParas.map(p => p.id) }
    }

    /**
     * Aendert Note-Text und/oder outlineDepth eines bestehenden Outline-Item-
     * Paragraphen in einem einzigen Undo-Schritt. Mind. eines der zwei Felder
     * muss gesetzt sein — sonst {ok:false, reason:'no_fields'}.
     *
     * Outline-Redesign Phase 4.2: das outline_active-Feld ist entfallen —
     * Outline-Items sind ein eigener Paragraph-Typ, kein Flag mehr. Aufrufe
     * auf Normal-Paras liefern `not_an_outline_item`.
     *
     * @param {string} paragraphId
     * @param {{note?: string, outlineDepth?: number, noteFormats?: Array, noteLinks?: Array}} update
     * @returns {{ok: boolean, reason?: string, updated?: Array<string>}}
     */
    updateParagraphNote(paragraphId, update) {
        if (!update || typeof update !== 'object') return { ok: false, reason: 'invalid_update' }

        const hasNote = typeof update.note === 'string'
        const hasDepth = Number.isInteger(update.outlineDepth)
        if (!hasNote && !hasDepth) {
            return { ok: false, reason: 'no_fields' }
        }

        const paraIndex = this.getParagraphIndex(paragraphId)
        if (paraIndex === -1) return { ok: false, reason: 'paragraph_not_found' }
        const para = this.paragraphs[paraIndex]
        if (para.type === PARA_TYPE.SEPARATOR) return { ok: false, reason: 'is_separator' }
        // Nur Outline-Items dürfen via updateParagraphNote angefasst werden.
        if (!para.isOutlineItem()) {
            return { ok: false, reason: 'not_an_outline_item' }
        }

        return this._updateParagraphFieldCore(paragraphId, 'note', (mutated) => {
            const updated = []
            if (hasNote) {
                mutated.note = update.note
                mutated.formats.note = Array.isArray(update.noteFormats)
                    ? update.noteFormats.map(s => ({ ...s }))
                    : []
                mutated.links.note = Array.isArray(update.noteLinks)
                    ? update.noteLinks.map(l => ({ ...l }))
                    : []
                updated.push('note')
            }
            if (hasDepth) {
                mutated.outlineDepth = Math.max(0, Math.min(EDITOR.MAX_OUTLINE_ITEM_DEPTH, update.outlineDepth | 0))
                updated.push('outlineDepth')
                // Nachfolgende Tiefen koennten jetzt out-of-range sein.
                this._correctOutlineDepths(paraIndex)
            }
            return updated
        })
    }

    /**
     * Gemeinsamer Kern fuer updateParagraphNote/updateParagraphInfo
     * (System-Review 2026-06-10, Kleinigkeit 18 — vorher ~70 % dupliziert
     * mit divergierenden Rueckgabe-Shapes). Erwartet, dass der Aufrufer
     * die methodenspezifischen Vorbedingungen (Feld-Validierung,
     * Typ-Checks, paragraph_not_found) bereits geprueft hat.
     *
     * Ablauf: Lock-Check → COW-Snapshot → mutateFn → Dirty-Marks →
     * EIN Undo-Schritt (stellt Feld-Trio + outlineDepth zurueck) →
     * Konsolidierungs-Reset.
     *
     * @param {string} paragraphId
     * @param {'note'|'info'} field
     * @param {(mutated: Paragraph) => Array<string>} mutateFn - fuehrt die
     *   Mutation aus und liefert die Liste der geaenderten Felder
     * @returns {{ok: boolean, reason?: string, updated?: Array<string>}}
     * @private
     */
    _updateParagraphFieldCore(paragraphId, field, mutateFn) {
        const lockCheck = this._assertSectionUnlocked(paragraphId)
        if (!lockCheck.ok) return lockCheck

        const para = this.getParagraph(paragraphId)

        // COW-Snapshot der alten Werte. outlineDepth wird generisch mit
        // gesichert — fuer info ist die Wiederherstellung ein No-Op.
        this._gen++
        const oldValue = para[field]
        const oldFormats = para.formats[field].map(f => ({ ...f }))
        const oldLinks = para.links[field].map(l => ({ ...l }))
        const oldDepth = para.outlineDepth

        const mutated = this._mutate(para)
        const updated = mutateFn(mutated)

        this._markParagraphDirty(paragraphId)
        this._markStructuralDirtyIncremental()

        this._pushUndo(() => {
            const p = this._mutate(this.getParagraph(paragraphId))
            if (!p) return
            p[field] = oldValue
            p.formats[field] = oldFormats
            p.links[field] = oldLinks
            p.outlineDepth = oldDepth
            this._markParagraphDirty(paragraphId)
            this._markStructuralDirty()
        })
        this._resetUndoConsolidation()

        return { ok: true, updated }
    }

    /**
     * Aendert das info-Feld (Context-Info) eines bestehenden Absatzes oder
     * Separators. Ersetzt info + formats.info + links.info zusammen — alte
     * Inline-Formate auf neuem Text-Offset waeren sonst inkonsistent.
     *
     * Anders als updateParagraphNote: info darf auch auf Separatoren gesetzt
     * werden (Frontend rendert info auch dort).
     *
     * @param {string} paragraphId
     * @param {{info?: string, infoFormats?: Array, infoLinks?: Array}} update
     * @returns {{ok: boolean, reason?: string, updated?: Array<string>}}
     */
    updateParagraphInfo(paragraphId, update) {
        if (!update || typeof update !== 'object') return { ok: false, reason: 'invalid_update' }
        if (typeof update.info !== 'string') return { ok: false, reason: 'no_fields' }

        const paraIndex = this.getParagraphIndex(paragraphId)
        if (paraIndex === -1) return { ok: false, reason: 'paragraph_not_found' }

        return this._updateParagraphFieldCore(paragraphId, 'info', (mutated) => {
            mutated.info = update.info
            mutated.formats.info = Array.isArray(update.infoFormats)
                ? update.infoFormats.map(s => ({ ...s }))
                : []
            mutated.links.info = Array.isArray(update.infoLinks)
                ? update.infoLinks.map(l => ({ ...l }))
                : []
            return ['info']
        })
    }

    /**
     * Bewegt den User-Viewport auf einen Section-Separator und pushed
     * Quell- und Zielposition in die NavigationHistory — verhalten ist
     * identisch zum manuellen Klick auf einen Section-Link.
     *
     * Delegiert an EditorInput._navigateToLink, das die ganze
     * Pane-Aktivierungs-/Cursor-/Viewport-Logik enthaelt. Der Wrapper
     * hier existiert, damit die Auto-Apply-Tool-Map homogen modelMethod
     * aufrufen kann (Phase-4-Doc Q-I, Variante B).
     *
     * @param {string} paragraphId — UUID des Ziel-Separators
     * @returns {{ok: boolean, reason?: string}}
     */
    jumpToSection(paragraphId) {
        const para = this.getParagraph(paragraphId)
        if (!para) return { ok: false, reason: 'paragraph_not_found' }
        if (para.type !== PARA_TYPE.SEPARATOR) return { ok: false, reason: 'not_a_separator' }

        const input = this._editorInput || this._input
        if (!input || typeof input._navigateToLink !== 'function') {
            return { ok: false, reason: 'no_editor_input' }
        }
        input._navigateToLink(paragraphId)
        return { ok: true }
    }

    /**
     * Legt eine neue Section an. Zwei Anker-Modi (mutex):
     *
     *   Mode 1 — frische Section (Standard):
     *     [neuer Separator + neuer initialPara]. afterSeparatorId optional
     *     (Default-Anker: ans Bereichs-Ende, siehe unten). Bei area='background'
     *     und fehlendem Background-Separator wird dieser automatisch im selben
     *     Undo-Schritt erzeugt.
     *
     *     Default-Anker (afterSeparatorId=null):
     *       - area='main'      → vor Background-Separator (oder Doc-Ende)
     *       - area='background'→ Doc-Ende
     *
     *   Mode 2 — Separator vor existierendem Paragraph (beforeParagraphId):
     *     Nur ein neuer Separator wird vor dem Anker-Paragraph eingefuegt.
     *     KEIN initialPara — der Anker-Paragraph wird zum ersten Inhalt der
     *     neuen Section. initialText wird ignoriert. area muss zur Section
     *     des Anker-Paragraphen passen.
     *
     * @param {{area: 'main'|'background',
     *          afterSeparatorId?: string|null,
     *          beforeParagraphId?: string|null,
     *          title: string, depth: number,
     *          initialText?: string|null}} input
     * @returns {{ok: boolean, reason?: string,
     *            separatorId?: string, firstParagraphId?: string}}
     */
    createSection(input, opts = {}) {
        if (!input || (input.area !== 'main' && input.area !== 'background')) {
            return { ok: false, reason: 'invalid_area' }
        }
        const title = typeof input.title === 'string' ? input.title : ''
        if (title.length === 0) return { ok: false, reason: 'empty_title' }
        const depth = Number.isInteger(input.depth) ? input.depth : 0
        if (depth < 0 || depth > 5) return { ok: false, reason: 'invalid_depth' }

        // Mutex-Check (Defense-in-Depth — Server-Schema lehnt das ohnehin ab,
        // aber direkte Aufrufer am Modell vorbei sollen nicht in inkonsistente
        // Pfade rutschen). IDs koennen Numbers (Legacy-Tests) oder Strings
        // (Production-UUIDs) sein — beides als "gesetzt" werten.
        const hasAfter = input.afterSeparatorId != null && input.afterSeparatorId !== ''
        const hasBefore = input.beforeParagraphId != null && input.beforeParagraphId !== ''
        if (hasAfter && hasBefore) return { ok: false, reason: 'mutex_anchors' }

        if (hasBefore) {
            return this._createSectionBeforeParagraph(input, opts, title, depth)
        }

        // Anker-Section validieren (falls gesetzt). Lock-Check entfaellt
        // (Review v1.4 #18) — Insert *nach* X mutiert X' Inhalt nicht.
        // Stattdessen Defense-in-Depth gegen cross-area: Anker und area
        // muessen zur selben Seite (Main bzw. Background) gehoeren, sonst
        // wandert die neue Section in den falschen Bereich (Review v1.4 #1).
        //
        // Sonderfall implicit-head Token: kein DB-Lookup (er hat keinen
        // Separator), Bereich ist immer 'main'. Insert-Position = vor erstem
        // echten Separator (Logik unten im Insert-Index-Block).
        const anchorIsImplicitHead = input.afterSeparatorId === IMPLICIT_HEAD_ID
        if (anchorIsImplicitHead) {
            if (input.area !== 'main') {
                return {
                    ok: false,
                    reason: 'anchor_area_mismatch',
                    expectedArea: input.area,
                    actualArea: 'main'
                }
            }
        } else if (input.afterSeparatorId != null) {
            const ankIdx = this.getParagraphIndex(input.afterSeparatorId)
            if (ankIdx === -1 || this.paragraphs[ankIdx].type !== PARA_TYPE.SEPARATOR) {
                return { ok: false, reason: 'unknown_section' }
            }
            const bgIdx = this.getBackgroundSeparatorIndex()
            const ankerInBackground = bgIdx !== -1 && ankIdx >= bgIdx
            const expectedBackground = input.area === 'background'
            if (ankerInBackground !== expectedBackground) {
                return {
                    ok: false,
                    reason: 'anchor_area_mismatch',
                    expectedArea: input.area,
                    actualArea: ankerInBackground ? 'background' : 'main'
                }
            }
        }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Auto-Create Background-Separator wenn noetig (im selben Undo-Schritt).
        if (input.area === 'background' && !this.hasBackgroundSeparator()) {
            const bgSep = new Paragraph(this._getNextId(), '', 'separator')
            bgSep._gen = this._gen
            bgSep.outlineDepth = EDITOR.BACKGROUND_SEPARATOR_DEPTH
            this.paragraphs.push(bgSep)
            // Background-Separator braucht selbst keinen Trailing-Para — der wird
            // gleich durch unsere neue Section bedient (Section-Invariante: Separator
            // gefolgt von Empty-Para oder weiterer Separator). Wir fuegen also
            // direkt den neuen Section-Separator + Empty-Para dahinter an.
        }

        // Insert-Index bestimmen.
        let insertAt
        if (anchorIsImplicitHead) {
            // Implicit-head: neue Section landet direkt vor dem ersten
            // echten Separator (egal welcher Bereich; Background-Sep oder
            // Main-Sep, beides beendet die implicit-head). Wenn keiner
            // existiert → Doc-Ende.
            insertAt = this.paragraphs.length
            for (let i = 0; i < this.paragraphs.length; i++) {
                if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) { insertAt = i; break }
            }
        } else if (input.afterSeparatorId != null) {
            // Direkt nach dem angegebenen Separator UND seinem Trailing-Section-Inhalt
            // — wir wollen den Anker-Separator NICHT zerschneiden, sondern eine
            // neue Section AB der naechsten Section-Grenze einfuegen. Praktisch:
            // Insert direkt VOR dem naechsten Separator (oder ans Ende).
            const ankIdx = this.getParagraphIndex(input.afterSeparatorId)
            insertAt = this.paragraphs.length
            for (let i = ankIdx + 1; i < this.paragraphs.length; i++) {
                if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) { insertAt = i; break }
            }
        } else if (input.area === 'main') {
            // Ende der Main-Sections — vor Background-Separator oder am Dokumentende.
            insertAt = this.paragraphs.length
            for (let i = 0; i < this.paragraphs.length; i++) {
                const p = this.paragraphs[i]
                if (p.type === PARA_TYPE.SEPARATOR && p.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                    insertAt = i; break
                }
            }
        } else {
            // Background-Ende — am Dokumentende (Background-Separator existiert
            // jetzt garantiert nach dem Auto-Create-Block oben).
            insertAt = this.paragraphs.length
        }

        // Neue Section: [emptyPara mit initialText, separator]
        const firstPara = new Paragraph(this._getNextId(), input.initialText || '')
        firstPara._gen = this._gen
        // Markdown-Patch fuer den firstPara — Format/Block-Felder vom Agent.
        if (Array.isArray(input.initialFormats)) {
            firstPara.formats.text = input.initialFormats.map(s => ({ ...s }))
        }
        if (Array.isArray(input.initialLinks)) {
            firstPara.links.text = input.initialLinks.map(l => ({ ...l }))
        }
        if (Number.isInteger(input.initialHeadingLevel)) {
            firstPara.headingLevel = input.initialHeadingLevel
        }
        if (input.initialListType === 'bullet' || input.initialListType === 'numbered') {
            firstPara.listType = input.initialListType
        }
        if (Number.isInteger(input.initialListLevel)) {
            firstPara.listLevel = input.initialListLevel
        }
        if (input.initialBlockquote) firstPara.blockquote = true
        if (input.initialCodeBlock) firstPara.codeBlock = true
        const sep = new Paragraph(this._getNextId(), title, 'separator')
        sep.outlineDepth = depth
        sep._gen = this._gen

        // Hinweis: insertSeparatorAfter (Z. 3580) erzeugt [emptyPara, sep], wobei
        // emptyPara *zur vorigen* Section gehoert und sep den Beginn der neuen
        // Section markiert. Bei createSection wollen wir aber den initialText
        // *innerhalb* der neuen Section haben → Pattern hier ist:
        //   ... [emptyPara_OldEnd][newSep][initialPara] ...
        // Die Section-Invariante fordert nach jedem Separator einen Nicht-Separator;
        // wir setzen also: erst sep, direkt dahinter firstPara.
        this.paragraphs.splice(insertAt, 0, sep, firstPara)
        this._mapsStale = true

        // Section-Invariante (falls insertAt am Dokumentende und Vorgaenger ein
        // Separator war, fuegt der Helper einen leeren Absatz dazwischen).
        this._ensureSectionInvariant()

        // Cursor → in den initialPara.
        // Review v1.4 #17: Auto-Apply-Pfade rufen mit moveCursor:false.
        if (opts.moveCursor !== false) {
            this.cursor = new Position(firstPara.id, (input.initialText || '').length, 'text')
            this.selection = null
        }

        this._markParagraphDirty(sep.id)
        this._markParagraphDirty(firstPara.id)
        this._markStructuralDirtyIncremental()

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this._markStructuralDirty()
            if (oldCursor) this.cursor = oldCursor
            this.selection = null
        })
        this.charsSinceUndoPoint = 0
        this._isInConsolidationGroup = false

        return { ok: true, separatorId: sep.id, firstParagraphId: firstPara.id }
    }

    /**
     * Mode-2-Pfad fuer createSection: schiebt nur einen neuen Separator vor
     * den Anker-Paragraphen. Der Anker-Paragraph wird zum ersten Inhalt der
     * neuen Section (kein initialPara erzeugt).
     *
     * Edge-Case Section-Invariante: war der Anker der erste Para seiner alten
     * Section, steht der neue Separator jetzt direkt nach dem alten Separator.
     * `_ensureSectionInvariant` schiebt dann einen leeren Para zwischen die
     * beiden — die alte Section bleibt also nicht ohne Inhalt.
     *
     * @private
     */
    _createSectionBeforeParagraph(input, opts, title, depth) {
        const ankIdx = this.getParagraphIndex(input.beforeParagraphId)
        if (ankIdx === -1) return { ok: false, reason: 'paragraph_not_found' }
        const ank = this.paragraphs[ankIdx]
        if (ank.type === PARA_TYPE.SEPARATOR) return { ok: false, reason: 'is_separator' }

        // area-Match: ergibt sich aus der Position des Anker-Paragraphen
        // relativ zum Background-Separator.
        const bgIdx = this.getBackgroundSeparatorIndex()
        const ankInBackground = bgIdx !== -1 && ankIdx > bgIdx
        const expectedBackground = input.area === 'background'
        if (ankInBackground !== expectedBackground) {
            return {
                ok: false,
                reason: 'anchor_area_mismatch',
                expectedArea: input.area,
                actualArea: ankInBackground ? 'background' : 'main'
            }
        }

        // Lock-Check auf der OWNING Section — anders als Mode 1 mutiert
        // dieser Insert den logischen Inhalt der bestehenden Section
        // (Splittung), also gilt der Lock.
        const lockCheck = this._assertSectionUnlocked(input.beforeParagraphId)
        if (!lockCheck.ok) return lockCheck

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Nur Separator erzeugen, kein initialPara.
        const sep = new Paragraph(this._getNextId(), title, 'separator')
        sep.outlineDepth = depth
        sep._gen = this._gen

        this.paragraphs.splice(ankIdx, 0, sep)
        this._mapsStale = true

        // Falls Anker erster Para seiner Section war, steht jetzt sep direkt
        // nach dem alten Separator → Invariante schiebt einen leeren Para
        // zwischen die beiden (gehoert zur alten Section).
        this._ensureSectionInvariant()

        // Cursor → Anker-Paragraph (jetzt erster Inhalt der neuen Section).
        if (opts.moveCursor !== false) {
            this.cursor = new Position(input.beforeParagraphId, 0, 'text')
            this.selection = null
        }

        this._markParagraphDirty(sep.id)
        this._markStructuralDirtyIncremental()

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this._markStructuralDirty()
            if (oldCursor) this.cursor = oldCursor
            this.selection = null
        })
        this.charsSinceUndoPoint = 0
        this._isInConsolidationGroup = false

        // Mode 2 hat keinen separaten firstParagraphId — der Anker IST der.
        // Verdict an den Server liefert nur separatorId.
        return { ok: true, separatorId: sep.id }
    }

    /**
     * Aendert Titel und/oder Tiefe eines bestehenden Section-Separators.
     * Mind. eines der beiden Felder muss gesetzt sein. Background-Separator
     * selbst nicht editierbar. `_correctOutlineDepths` laeuft automatisch falls
     * `depth` mutiert — Subsections werden NICHT mit verschoben (Spec v1.4 Q42).
     *
     * @param {number} separatorId
     * @param {{title?: string, depth?: number}} update
     * @returns {{ok: boolean, reason?: string, updated?: Array<string>}}
     */
    updateSection(separatorId, update) {
        if (!update || typeof update !== 'object') return { ok: false, reason: 'invalid_update' }
        const hasTitle = typeof update.title === 'string'
        const hasDepth = Number.isInteger(update.depth)
        if (!hasTitle && !hasDepth) return { ok: false, reason: 'no_fields' }
        if (hasTitle && update.title.length === 0) return { ok: false, reason: 'empty_title' }
        if (hasDepth && (update.depth < 0 || update.depth > 5)) return { ok: false, reason: 'invalid_depth' }

        const sepIdx = this.getParagraphIndex(separatorId)
        if (sepIdx === -1) return { ok: false, reason: 'unknown_section' }
        const sep = this.paragraphs[sepIdx]
        if (sep.type !== PARA_TYPE.SEPARATOR) return { ok: false, reason: 'not_a_separator' }
        if (sep.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            return { ok: false, reason: 'is_background_separator' }
        }

        const lockCheck = this._assertSectionUnlocked(separatorId)
        if (!lockCheck.ok) return lockCheck

        // COW-Snapshot
        this._gen++
        const oldTitle = sep.text
        const oldFormats = sep.formats.text.map(f => ({ ...f }))
        const oldLinks = sep.links.text.map(l => ({ ...l }))  // Review v1.4 #12
        const oldDepth = sep.outlineDepth

        const mutated = this._mutate(sep)
        const updated = []
        if (hasTitle) {
            mutated.text = update.title
            mutated.formats.text = []
            mutated.links.text = []
            updated.push('title')
        }
        if (hasDepth) {
            mutated.outlineDepth = update.depth
            updated.push('depth')
        }

        this._markParagraphDirty(separatorId)
        if (hasDepth) {
            // Tiefen-Korrektur ab Section-Start. Subsections behalten ihre
            // eigenen Tiefen, _correctOutlineDepths clamps nur wo noetig.
            this._correctOutlineDepths(sepIdx + 1)
        }
        this._markStructuralDirtyIncremental()

        this._pushUndo(() => {
            const p = this._mutate(this.getParagraph(separatorId))
            if (!p) return
            if (hasTitle) {
                p.text = oldTitle
                p.formats.text = oldFormats
                p.links.text = oldLinks  // Review v1.4 #12: alte Links wiederherstellen
            }
            if (hasDepth) p.outlineDepth = oldDepth
            this._markParagraphDirty(separatorId)
            this._markStructuralDirty()
        })
        this.charsSinceUndoPoint = 0
        this._isInConsolidationGroup = false

        return { ok: true, updated }
    }

    /**
     * Loescht eine leere Section: den Separator + alle nachfolgenden Paragraphen
     * bis zur naechsten Section-Grenze, sofern die Section nur den Trailing-
     * Empty-Placeholder enthaelt. Sonst {ok:false, reason:'section_not_empty',
     * paragraphCount}.
     *
     * Background-Separator selbst nicht loeschbar (Sub-Sections im Background
     * sind weiterhin loeschbar, solange sie leer sind).
     *
     * @param {number} separatorId
     * @returns {{ok: boolean, reason?: string, paragraphCount?: number}}
     */
    deleteSection(separatorId, opts = {}) {
        const sepIdx = this.getParagraphIndex(separatorId)
        if (sepIdx === -1) return { ok: false, reason: 'unknown_section' }
        const sep = this.paragraphs[sepIdx]
        if (sep.type !== PARA_TYPE.SEPARATOR) return { ok: false, reason: 'not_a_separator' }
        if (sep.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            return { ok: false, reason: 'is_background_separator' }
        }

        const lockCheck = this._assertSectionUnlocked(separatorId)
        if (!lockCheck.ok) return lockCheck

        // Section-Inhalt zaehlen — alle Paragraphen bis zum naechsten Separator
        // (oder Dokumentende). "Leer" = entweder gar keine Inhalt-Absaetze, oder
        // genau ein Trailing-Empty-Placeholder mit text='' und note='' und info=''.
        let endIdx = this.paragraphs.length
        for (let i = sepIdx + 1; i < this.paragraphs.length; i++) {
            if (this.paragraphs[i].type === PARA_TYPE.SEPARATOR) { endIdx = i; break }
        }
        const sectionParas = this.paragraphs.slice(sepIdx + 1, endIdx)
        const isEmpty = sectionParas.length === 0
            || (sectionParas.length === 1
                && sectionParas[0].text === ''
                && sectionParas[0].note === ''
                && sectionParas[0].info === ''
                && !sectionParas[0].isOutlineItem())
        if (!isEmpty) {
            return { ok: false, reason: 'section_not_empty', paragraphCount: sectionParas.length }
        }

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Separator + Trailing-Empty-Placeholder (falls vorhanden) entfernen.
        const removeCount = 1 + sectionParas.length
        this.paragraphs.splice(sepIdx, removeCount)
        this._mapsStale = true

        // Mindestens ein Absatz muss uebrig bleiben.
        if (this.paragraphs.length === 0) {
            this.paragraphs.push(new Paragraph(this._getNextId(), ''))
        }

        // Section-Invariante (z.B. wenn jetzt zwei Separatoren nebeneinander stehen).
        this._ensureSectionInvariant()

        // Cursor an die Loeschstelle (Position 0 des nachfolgenden Absatzes).
        // Review v1.4 #17: Auto-Apply-Pfade rufen mit moveCursor:false.
        if (opts.moveCursor !== false) {
            const newIdx = Math.min(sepIdx, this.paragraphs.length - 1)
            this.cursor = new Position(this.paragraphs[newIdx].id, 0, 'text')
            this.selection = null
        }

        this._markStructuralDirtyIncremental()

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this._markStructuralDirty()
            if (oldCursor) this.cursor = oldCursor
            this.selection = null
        })
        this.charsSinceUndoPoint = 0
        this._isInConsolidationGroup = false

        return { ok: true }
    }

    /**
     * Entfernt einen Separator.
     * @param {number} separatorIndex - Index des Separator-Paragraphs
     * @returns {Function} Undo-Funktion
     */
    removeSeparator(separatorIndex) {
        if (separatorIndex < 0 || separatorIndex >= this.paragraphs.length) return () => {}
        if (this.paragraphs[separatorIndex].type !== PARA_TYPE.SEPARATOR) return () => {}
        // Background-Separator kann nicht per removeSeparator gelöscht werden
        if (this.paragraphs[separatorIndex].outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) return () => {}

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        this.paragraphs.splice(separatorIndex, 1)
        // Lookup-Maps sofort invalidieren — getParagraph() unten liest sonst
        // stale _paragraphMap-Einträge für den entfernten Separator.
        this._mapsStale = true

        // Mindestens ein Absatz muss übrig bleiben
        if (this.paragraphs.length === 0) {
            this.paragraphs.push(new Paragraph(this._getNextId(), ''))
        }

        // Section-Invariante sicherstellen (Separator am Ende → leeren Absatz anhängen)
        this._ensureSectionInvariant()

        // Cursor korrigieren falls nötig
        if (this.cursor) {
            const cursorPara = this.getParagraph(this.cursor.paragraphId)
            if (!cursorPara) {
                // Cursor auf den Absatz nach dem entfernten Separator
                const newIdx = Math.min(separatorIndex, this.paragraphs.length - 1)
                this.cursor = new Position(this.paragraphs[newIdx].id, 0)
            }
        }

        this._markStructuralDirtyIncremental()
        // Keine _markParagraphDirty noetig — Loeschung wird ueber paragraphOrder-Diff erkannt

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Setzt den Titel eines Separators (im text-Feld).
     * @param {number} paragraphId - ID des Separator-Paragraphs
     * @param {string} title - Neuer Titel
     * @returns {Function} Undo-Funktion
     */
    setSeparatorTitle(paragraphId, title) {
        const para = this.getParagraph(paragraphId)
        if (!para || para.type !== PARA_TYPE.SEPARATOR) return () => {}
        if (para.text === title) return () => {}

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()

        const mp = this._mutate(para)
        mp.text = title

        this._markStructuralDirtyIncremental()
        this._markParagraphDirty(paragraphId)

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Stellt sicher, dass nach jedem Separator mindestens ein Nicht-Separator folgt.
     * Am Ende des Dokuments darf kein Separator stehen.
     */
    _ensureSectionInvariant() {
        // Defense-in-Depth: Aufrufer hat paragraphs evtl. gerade mutiert.
        // Ohne Invalidierung könnte _indexMap stale sein, und falls diese
        // Methode künftig Index-Lookups nutzt wäre das eine Fehlerquelle.
        this._mapsStale = true

        // Nach jedem Separator muss ein Nicht-Separator folgen
        for (let i = 0; i < this.paragraphs.length; i++) {
            if (this.paragraphs[i].type !== PARA_TYPE.SEPARATOR) continue

            if (i === this.paragraphs.length - 1 || this.paragraphs[i + 1].type === PARA_TYPE.SEPARATOR) {
                const emptyPara = new Paragraph(this._getNextId(), '')
                emptyPara._gen = this._gen
                this.paragraphs.splice(i + 1, 0, emptyPara)
                // Maps sofort invalidieren — nachfolgender Code (z.B.
                // _correctOutlineDepths) nutzt sonst stale Indizes.
                this._mapsStale = true
            }
        }
    }

    // ===== Background-Separator =====

    /**
     * Prüft ob ein Background-Separator existiert.
     * @returns {boolean}
     */
    hasBackgroundSeparator() {
        return this.paragraphs.some(p => p.type === PARA_TYPE.SEPARATOR && p.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH)
    }

    /**
     * Gibt den Background-Separator zurück (oder null).
     * @returns {Paragraph|null}
     */
    getBackgroundSeparator() {
        return this.paragraphs.find(p => p.type === PARA_TYPE.SEPARATOR && p.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) || null
    }

    /**
     * Gibt den Index des Background-Separators zurück (oder -1).
     * @returns {number}
     */
    getBackgroundSeparatorIndex() {
        return this.paragraphs.findIndex(p => p.type === PARA_TYPE.SEPARATOR && p.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH)
    }

    /**
     * Fügt den Background-Separator am Dokumentende ein.
     * Erstellt den Separator + einen leeren Absatz danach.
     * @returns {Function} Undo-Funktion
     */
    insertBackgroundSeparator() {
        if (this.hasBackgroundSeparator()) return () => {}

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Background-Separator erstellen
        const sep = new Paragraph(this._getNextId(), '', 'separator')
        sep.outlineDepth = EDITOR.BACKGROUND_SEPARATOR_DEPTH
        sep._gen = this._gen

        // Leerer Absatz danach (Invariante + User kann sofort tippen)
        const emptyPara = new Paragraph(this._getNextId(), '')
        emptyPara._gen = this._gen

        // Am Dokumentende einfügen
        this.paragraphs.push(sep, emptyPara)
        this._mapsStale = true

        // Cursor auf den leeren Absatz setzen
        this.cursor = new Position(emptyPara.id, 0)
        this.selection = null

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            this.cursor = oldCursor
            this.selection = null
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Entfernt den Background-Separator und den leeren Absatz danach.
     * Nur wenn der Background-Bereich leer ist (genau ein leerer Absatz nach Separator).
     * @returns {Function} Undo-Funktion
     */
    removeBackgroundSeparator() {
        const sepIdx = this.getBackgroundSeparatorIndex()
        if (sepIdx < 0) return () => {}

        // Prüfen ob nur ein leerer Absatz nach dem Separator steht
        const afterSep = this.paragraphs.slice(sepIdx + 1)
        if (afterSep.length !== 1 || afterSep[0].text !== '') return () => {}

        // COW-Snapshot
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        // Separator + leeren Absatz entfernen
        this.paragraphs.splice(sepIdx, 2)
        this._mapsStale = true

        // Mindestens ein Absatz muss übrig bleiben
        if (this.paragraphs.length === 0) {
            this.paragraphs.push(new Paragraph(this._getNextId(), ''))
        }

        // Cursor korrigieren
        if (this.cursor) {
            const cursorPara = this.getParagraph(this.cursor.paragraphId)
            if (!cursorPara) {
                const lastIdx = this.paragraphs.length - 1
                this.cursor = new Position(this.paragraphs[lastIdx].id, this.paragraphs[lastIdx].text.length)
            }
        }

        this._markStructuralDirty()

        const undoFn = () => {
            this.paragraphs = oldParagraphs
            if (oldCursor) this.cursor = oldCursor
            this.selection = null
            this._markStructuralDirty()
        }

        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        return undoFn
    }

    /**
     * Prüft ob ein Absatz im Background-Bereich liegt (nach dem Background-Separator).
     * @param {number} paragraphId - ID des Absatzes
     * @returns {boolean}
     */
    isInBackground(paragraphId) {
        const bgIdx = this.getBackgroundSeparatorIndex()
        if (bgIdx < 0) return false
        const paraIdx = this.getParagraphIndex(paragraphId)
        return paraIdx >= bgIdx
    }

    /**
     * Prüft ob der Background-Bereich leer ist (nur ein leerer Absatz nach dem Separator).
     * @returns {boolean}
     */
    isBackgroundEmpty() {
        const sepIdx = this.getBackgroundSeparatorIndex()
        if (sepIdx < 0) return false
        const afterSep = this.paragraphs.slice(sepIdx + 1)
        return afterSep.length === 1 && afterSep[0].text === ''
    }

    // ===== Import/Export =====

    /**
     * Gibt den gesamten Text als Markdown zurück.
     * Separatoren werden als '---' repräsentiert.
     * @returns {string}
     */
    getText() {
        return this._paragraphsToMarkdown(p =>
            richTextToMarkdownFull(p.text, p.formats.text, p.headingLevel,
                p.listType, p.listLevel, p.blockquote, p.links.text)
        )
    }

    /**
     * Gibt den Text mit aufgelösten Link-Labels zurück (für Export).
     * [[@id]] → aufgelöster Label-Text (Titel oder Nummer).
     * @returns {string}
     */
    getExportText() {
        const separatorMap = this.buildSeparatorMap()
        return this._paragraphsToMarkdown(p => {
            // Rich-Text Export: Links zu Klartext-Labels auflösen, Block-Formate als Markdown-Prefix
            const linkLabels = new Map()
            for (const link of p.links.text) {
                const { label } = this.resolveLinkLabel(link.targetId, separatorMap)
                linkLabels.set(link.targetId, label)
            }
            return richTextToMarkdownFull(p.text, p.formats.text, p.headingLevel,
                p.listType, p.listLevel, p.blockquote, p.links.text, false, linkLabels)
        }, { excludeBackground: true })
    }

    /**
     * Serialisiert die Paragraph-Liste zu Markdown mit Fenced-Code-Block-
     * Wrapping: aufeinanderfolgende codeBlock-Paragraphs werden zu einem
     * ` ``` `-Block zusammengefasst (einmal Open, n Zeilen Inhalt, einmal Close).
     * Separatoren werden inline als `--- Titel` bzw. `===` behandelt.
     *
     * @param {(para: Paragraph) => string} renderNonCodeLine - Rendert eine
     *   nicht-code Paragraph-Zeile (Inline + Block-Prefixes). Code-Paragraphs
     *   werden separat als raw text zwischen Fences ausgegeben.
     * @param {{excludeBackground?: boolean}} [options] - excludeBackground:true
     *   überspringt den Background-Bereich (Story Bible) komplett — für den
     *   Markdown-Export. Default false (getText behält alles, roundtrip-treu).
     * @returns {string}
     */
    _paragraphsToMarkdown(renderNonCodeLine, options = {}) {
        const excludeBackground = options.excludeBackground === true
        const out = []
        let inCodeBlock = false
        let inBackground = false
        for (const p of this.paragraphs) {
            // Background-Bereich-Tracking: ein Background-Separator öffnet ihn
            // und er reicht bis zum Dokumentende — normale Separatoren INNERHALB
            // (Story-Bible-Sections via Cmd+= oder create_section area:'background')
            // schließen ihn NICHT (gleiche Semantik wie EditorView-bg-Flag und
            // buildSeparatorMap-B_-Nummerierung; sonst leakt Story-Bible-Inhalt
            // in den Markdown-Export). Für den Export (Story Bible erscheint
            // NICHT) wird der ganze Bereich übersprungen; ein evtl. offener
            // Code-Block davor wird durch das abschließende `if (inCodeBlock)`
            // unten sauber geschlossen.
            if (p.type === PARA_TYPE.SEPARATOR
                    && p.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                inBackground = true
            }
            if (excludeBackground && inBackground) continue
            const isCode = p.type !== PARA_TYPE.SEPARATOR && !!p.codeBlock
            if (isCode && !inCodeBlock) {
                out.push('```')
                inCodeBlock = true
            } else if (!isCode && inCodeBlock) {
                out.push('```')
                inCodeBlock = false
            }
            if (p.type === PARA_TYPE.SEPARATOR) {
                if (p.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                    out.push('===')
                } else {
                    out.push(p.text ? `--- ${p.text}` : '---')
                }
            } else if (p.type === 'hr') {
                // HR-Paragraph: als '---' serialisieren. richTextToMarkdownFull
                // wuerde sonst nur einen Leerstring liefern (p.text='') und der
                // Roundtrip via MD-Import ginge verloren. Konflikt mit
                // empty-title separator (gleiches '---') ist akzeptabel — beim
                // Re-Import wird ein leerer Separator zu HR, was praktisch
                // dem gleichen visuellen Ergebnis entspricht.
                out.push('---')
            } else if (isCode) {
                out.push(p.text || '')
            } else {
                out.push(renderNonCodeLine(p))
            }
        }
        if (inCodeBlock) out.push('```')
        return out.join('\n')
    }

    /**
     * Setzt den Text (Markdown).
     * Löscht den Undo/Redo-Stack.
     * @param {string} markdown
     */
    setText(markdown) {
        // Single-Pass: Separator-Pattern, Fenced-Code-Blocks und reguläre
        // Markdown-Zeilen müssen gemeinsam erkannt werden, weil ein Fence-
        // Marker den Modus für nachfolgende Zeilen ändert (innerhalb eines
        // Fences sind '--- Titel' und '# Heading' raw-Text).
        const lines = markdown.split('\n')
        this.paragraphs = []
        let inFence = false
        for (const line of lines) {
            if (/^```/.test(line)) {
                inFence = !inFence
                continue   // Fence-Marker werden nicht in den Output übernommen
            }
            if (inFence) {
                const para = new Paragraph(this._getNextId(), line)
                para.codeBlock = true
                this.paragraphs.push(para)
                continue
            }
            // HR-Markdown: 3+ identische `-`, `*` oder `_` (optional trailing
            // whitespace, kein anderer Content). Konsistent mit dem in-Editor
            // `---`-Block-Makro in EditorInput. Vor dem Separator-Check,
            // damit '--- ' (mit trailing-Whitespace, ohne echten Titel) auch
            // als HR landet — sonst wuerde der Separator-Check '--- ' mit
            // leerem/whitespace-Titel aufnehmen.
            if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
                const hr = new Paragraph(this._getNextId(), '', 'hr')
                this.paragraphs.push(hr)
                continue
            }
            // '--- Titel' als Separator mit Titel erkennen.
            if (line.startsWith('--- ') && line.length > 4) {
                const title = line.slice(4)
                const sep = new Paragraph(this._getNextId(), title, 'separator')
                sep.outlineDepth = 0
                        this.paragraphs.push(sep)
                continue
            }
            this.paragraphs.push(new Paragraph(this._getNextId(), line))
        }

        // Mindestens ein leerer Paragraph
        if (this.paragraphs.length === 0) {
            this.paragraphs.push(new Paragraph(this._getNextId(), ''))
        }

        // Cursor am Anfang
        this.cursor = new Position(this.paragraphs[0].id, 0)
        this.selection = null

        // Stacks zurücksetzen
        this.undoStack = []
        this.redoStack = []
        this.charsSinceUndoPoint = 0

        // Markdown → Rich-Text konvertieren (für Import von .md/.txt).
        // Code-Block-Paragraphs bleiben raw — kein Inline-Format-Parsing.
        for (const para of this.paragraphs) {
            if (para.type === PARA_TYPE.SEPARATOR) continue
            for (const field of ['text', 'note', 'info']) {
                const fieldText = field === 'info' ? para.info : field === 'note' ? para.note : para.text
                if (!fieldText) {
                    para.formats[field] = []
                    para.links[field] = []
                    continue
                }
                if (para.codeBlock && field === 'text') {
                    para.formats[field] = []
                    para.links[field] = []
                    continue
                }
                const result = markdownToRichText(fieldText)
                if (field === 'info') para.info = result.text
                else if (field === 'note') para.note = result.text
                else para.text = result.text
                para.formats[field] = result.formats
                para.links[field] = result.links
                if (field === 'text') {
                    para.headingLevel = result.headingLevel
                    para.listType = result.listType
                    para.listLevel = result.listLevel
                    para.blockquote = result.blockquote
                }
            }
        }

        this._markStructuralDirty()
    }

    /**
     * Erstellt Paragraphen direkt aus Rich-Text-Daten (localStorage v5, CBL v4.0).
     * Übernimmt IDs falls vorhanden (wichtig für Link-targetIds).
     * @param {Array<Object>} data - Array von serialisierten RT-Paragraphen
     */
    setParagraphsRT(data) {
        // RAM ist sauber v4 (drei Paragraph-Typen, adressiert ueber `para.type`).
        const built = data.map(d => {
            const fields = deserializeParagraphRT(d)
            // ID aus Daten übernehmen oder neue vergeben
            const id = fields.id != null ? fields.id : this._getNextId()
            const para = new Paragraph(id, fields.text, fields.type)
            para.note = fields.note
            para.info = fields.info
            para.outlineDepth = fields.outlineDepth
            para.formats = fields.formats
            para.links = fields.links
            para.headingLevel = fields.headingLevel
            para.listType = fields.listType
            para.listLevel = fields.listLevel
            para.listStart = fields.listStart
            para.blockquote = fields.blockquote
            para.codeBlock = fields.codeBlock
            para.locked = fields.locked
            para.sectionCollapsed = fields.sectionCollapsed
            para.activeCollapsed = fields.activeCollapsed
            return para
        })
        this.paragraphs = built

        if (this.paragraphs.length === 0) {
            this.paragraphs.push(new Paragraph(this._getNextId(), ''))
        }

        this.cursor = new Position(this.paragraphs[0].id, 0)
        this.selection = null
        this.undoStack = []
        this.redoStack = []
        this.charsSinceUndoPoint = 0
        this._firstSectionLocked = false

        this._markStructuralDirty()
    }

    /**
     * Setzt den Paragraph-Typ (für Cleanup).
     * @param {number} paragraphId
     * @param {string} type
     */
    setParagraphType(paragraphId, type) {
        let para = this.getParagraph(paragraphId)
        if (para) {
            para = this._mutate(para)
            para.type = type
            this._markParagraphDirty(paragraphId)
        }
    }

    /**
     * Leert ein Feld (text/note/info) eines Absatzes komplett — Text, Formate
     * und Links. Der Absatz selbst bleibt erhalten (anders als deleteParagraph).
     * Undobar.
     * @param {number} paragraphId
     * @param {'text'|'note'|'info'} field
     * @returns {boolean} true wenn etwas geleert wurde (false: war schon leer)
     */
    clearField(paragraphId, field) {
        const para = this.getParagraph(paragraphId)
        if (!para) return false
        const oldText = this._getFieldText(para, field) || ''
        if (oldText === '' && para.formats[field].length === 0 && para.links[field].length === 0) {
            return false
        }

        // COW-Snapshot — Undo stellt das gesamte alte Paragraph-Objekt wieder her.
        this._gen++
        const oldParagraphs = this.paragraphs.slice()
        const oldCursor = this.cursor ? this.cursor.clone() : null

        this._setFieldText(para, field, '')
        const freshPara = this.getParagraph(paragraphId)
        freshPara.formats[field] = []
        freshPara.links[field] = []
        this._markParagraphDirty(paragraphId)

        this._pushUndo(() => {
            this.paragraphs = oldParagraphs
            this._mapsStale = true
            this.cursor = oldCursor
            this._markParagraphDirty(paragraphId)
        })
        this.charsSinceUndoPoint = 0
        return true
    }

    // === Section Links ===

    /**
     * Fuegt einen Section-Link an der aktuellen Cursor-Position ein.
     * @param {number} targetSeparatorId - Paragraph-ID des Ziel-Separators
     * @returns {Function} Undo-Funktion
     */
    insertLink(targetSeparatorId) {
        if (!this.cursor) return () => {}

        // Separator-Guard: keine Links in Separatoren einfuegen
        if (this.isSeparator(this.cursor.paragraphId)) {
            this._actionBlocked = true
            return () => {}
        }

        // Bei Selection: erst loeschen
        if (this.hasSelection()) {
            this.deleteSelection()
        }

        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return () => {}

        const field = this.cursor.field
        const offset = this.cursor.offset
        const oldFieldText = this._getFieldText(para, field)

        // Rich-Text: \uFFFC + LinkEntry einfügen
        const newFieldText = oldFieldText.slice(0, offset) + '\uFFFC' + oldFieldText.slice(offset)
        this._setFieldText(para, field, newFieldText)
        const freshPara = this.getParagraph(para.id)
        freshPara.formats[field] = adjustSpansForInsert(freshPara.formats[field], offset, 1)
        freshPara.links[field] = adjustLinksForInsert(freshPara.links[field], offset, 1)
        freshPara.links[field].push({ offset, targetId: targetSeparatorId })
        freshPara.links[field].sort((a, b) => a.offset - b.offset)
        this.cursor.offset += 1
        this._markParagraphDirty(para.id)

        this._isInConsolidationGroup = false
        const undoFn = () => {
            this._setFieldText(freshPara, field, oldFieldText)
            // Spans/Links wiederherstellen via COW
            this.cursor = new Position(para.id, offset, field)
            this._markParagraphDirty(para.id)
        }
        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0
        return undoFn
    }

    /**
     * Loescht einen Link an der angegebenen Position.
     * @param {number} paragraphId
     * @param {string} field - 'text', 'note' oder 'info'
     * @param {number} dataStart - Start des Link-Markers im Text
     * @param {number} dataEnd - Ende des Link-Markers im Text
     * @returns {Function} Undo-Funktion
     */
    deleteLink(paragraphId, field, dataStart, dataEnd) {
        const para = this.getParagraph(paragraphId)
        if (!para) return () => {}

        const oldFieldText = this._getFieldText(para, field)
        const newFieldText = oldFieldText.slice(0, dataStart) + oldFieldText.slice(dataEnd)
        this._setFieldText(para, field, newFieldText)

        // RT: Spans und Links anpassen
        const freshPara = this.getParagraph(paragraphId)
        const savedFormats = freshPara.formats[field].map(f => ({ ...f }))
        const savedLinks = freshPara.links[field].map(l => ({ ...l }))
        freshPara.formats[field] = adjustSpansForDeleteRange(freshPara.formats[field], dataStart, dataEnd)
        freshPara.links[field] = adjustLinksForDeleteRange(freshPara.links[field], dataStart, dataEnd)

        // Cursor vor den geloeschten Link setzen
        this.cursor = new Position(paragraphId, dataStart, field)
        this._markParagraphDirty(paragraphId)

        // Konsolidierung beenden
        this._isInConsolidationGroup = false

        const undoFn = () => {
            this._setFieldText(para, field, oldFieldText)
            const p = this.getParagraph(paragraphId)
            if (p) { p.formats[field] = savedFormats; p.links[field] = savedLinks }
            this.cursor = new Position(paragraphId, dataEnd, field)
            this._markParagraphDirty(paragraphId)
        }
        this._pushUndo(undoFn)
        this.charsSinceUndoPoint = 0

        // Link-Selektion aufheben
        this._selectedLink = null

        return undoFn
    }

    /**
     * Findet alle Section-Links im gesamten Dokument.
     * @returns {Array<{paragraphId: number, field: string, dataStart: number, dataEnd: number, targetId: number}>}
     */
    getAllLinks() {
        const links = []
        for (const para of this.paragraphs) {
            if (para.type === PARA_TYPE.SEPARATOR) continue
            for (const field of ['text', 'note', 'info']) {
                // RT: Links in para.links[field]
                const fieldLinks = para.links[field]
                if (!fieldLinks || fieldLinks.length === 0) continue
                for (const link of fieldLinks) {
                    links.push({
                        paragraphId: para.id,
                        field,
                        dataStart: link.offset,
                        dataEnd: link.offset + 1,
                        targetId: link.targetId
                    })
                }
            }
        }
        return links
    }

    /**
     * Findet alle Links die auf ein bestimmtes Ziel zeigen.
     * @param {number} targetId - Paragraph-ID des Ziel-Separators
     * @returns {Array<{paragraphId: number, field: string, dataStart: number, dataEnd: number, targetId: number}>}
     */
    getLinksToTarget(targetId) {
        return this.getAllLinks().filter(l => l.targetId === targetId)
    }

    /**
     * Findet den naechsten/vorigen Link mit gleichem Ziel im Dokument.
     * Fuer Tab/Shift+Tab Navigation auf selektierten Links.
     *
     * @param {number} targetId - Ziel-ID
     * @param {number} currentParagraphId - Aktueller Absatz
     * @param {string} currentField - Aktuelles Feld
     * @param {number} currentDataStart - Position des aktuellen Links
     * @param {'forward'|'backward'} direction
     * @returns {{paragraphId: number, field: string, dataStart: number, dataEnd: number, targetId: number}|null}
     */
    findNextLinkWithTarget(targetId, currentParagraphId, currentField, currentDataStart, direction) {
        const allLinks = this.getLinksToTarget(targetId)
        if (allLinks.length <= 1) return null  // Nur ein Link → nichts zum navigieren

        // Links in Dokument-Reihenfolge sortieren
        const fieldOrder = { text: 0, note: 1, info: 2 }
        allLinks.sort((a, b) => {
            const idxA = this.getParagraphIndex(a.paragraphId)
            const idxB = this.getParagraphIndex(b.paragraphId)
            if (idxA !== idxB) return idxA - idxB
            if (fieldOrder[a.field] !== fieldOrder[b.field]) return fieldOrder[a.field] - fieldOrder[b.field]
            return a.dataStart - b.dataStart
        })

        // Aktuellen Link finden
        const currentIdx = allLinks.findIndex(l =>
            l.paragraphId === currentParagraphId &&
            l.field === currentField &&
            l.dataStart === currentDataStart
        )
        if (currentIdx === -1) return null

        // Wrap-around Navigation
        if (direction === 'forward') {
            return allLinks[(currentIdx + 1) % allLinks.length]
        } else {
            return allLinks[(currentIdx - 1 + allLinks.length) % allLinks.length]
        }
    }

    /**
     * Prueft ob an der aktuellen Cursor-Position ein Link direkt vor dem Cursor ist.
     * Wird fuer Link-Selection bei Pfeil-Navigation verwendet.
     *
     * @returns {{paragraphId: number, field: string, dataStart: number, dataEnd: number, targetId: number}|null}
     */
    getLinkBeforeCursor() {
        if (!this.cursor) return null
        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return null
        const field = this.cursor.field || 'text'

        const links = para.links[field]
        if (!links || links.length === 0) return null
        for (const link of links) {
            if (link.offset + 1 === this.cursor.offset) {
                return { paragraphId: para.id, field, dataStart: link.offset, dataEnd: link.offset + 1, targetId: link.targetId }
            }
        }
        return null
    }

    /**
     * Prueft ob an der aktuellen Cursor-Position ein Link direkt nach dem Cursor ist.
     * @returns {{paragraphId: number, field: string, dataStart: number, dataEnd: number, targetId: number}|null}
     */
    getLinkAfterCursor() {
        if (!this.cursor) return null
        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return null
        const field = this.cursor.field || 'text'

        const links = para.links[field]
        if (!links || links.length === 0) return null
        for (const link of links) {
            if (link.offset === this.cursor.offset) {
                return { paragraphId: para.id, field, dataStart: link.offset, dataEnd: link.offset + 1, targetId: link.targetId }
            }
        }
        return null
    }

    /**
     * Prueft ob der Cursor innerhalb eines Link-Markers steht.
     * @returns {{paragraphId: number, field: string, dataStart: number, dataEnd: number, targetId: number}|null}
     */
    getLinkAtCursor() {
        if (!this.cursor) return null
        const para = this.getParagraph(this.cursor.paragraphId)
        if (!para) return null

        const field = this.cursor.field || 'text'

        // RT: Links in para.links[field], Platzhalter ist \uFFFC (1 Zeichen)
        const links = para.links[field]
        if (!links || links.length === 0) return null
        const offset = this.cursor.offset
        for (const link of links) {
            if (offset === link.offset) {
                return { paragraphId: para.id, field, dataStart: link.offset, dataEnd: link.offset + 1, targetId: link.targetId }
            }
        }
        return null
    }

    /**
     * Baut eine Map aller Separatoren: ID → {number, title, depth}.
     * Preiswerte Operation: einmal durch die Paragraphs gehen.
     * @returns {Map<number, {number: string, title: string, depth: number}>}
     */
    buildSeparatorMap() {
        const map = new Map()
        const maxDepth = EDITOR.MAX_SEPARATOR_DEPTH
        const counters = new Array(maxDepth + 1).fill(0)
        const bgCounters = new Array(maxDepth + 1).fill(0)
        let inBackground = false

        for (const para of this.paragraphs) {
            if (para.type !== PARA_TYPE.SEPARATOR) continue

            const d = para.outlineDepth
            let num = ''
            // Default: Title aus dem Separator-Text. Background-Separator hat
            // keinen Text (Editier-Guard), bekommt aber den festen Label
            // "Background" — damit er im LinkPicker auswaehlbar erscheint
            // (statt leerer Zeile) und resolveLinkLabel "Background" liefert
            // (statt § Fallback).
            let title = para.text || ''

            if (d === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
                inBackground = true
                title = 'Background'
            } else if (inBackground) {
                bgCounters[d]++
                for (let i = d + 1; i <= maxDepth; i++) bgCounters[i] = 0
                num = 'B_'
                for (let i = 0; i <= d; i++) num += bgCounters[i] + '.'
            } else {
                counters[d]++
                for (let i = d + 1; i <= maxDepth; i++) counters[i] = 0
                for (let i = 0; i <= d; i++) num += counters[i] + '.'
            }

            map.set(para.id, { number: num, title, depth: d })
        }
        return map
    }

    /**
     * Loest ein Link-Label auf: Titel oder automatische Nummer.
     * @param {number} targetId - Paragraph-ID des Ziel-Separators
     * @param {Map} separatorMap - Ergebnis von buildSeparatorMap()
     * @returns {{label: string, isBroken: boolean}}
     */
    resolveLinkLabel(targetId, separatorMap) {
        const info = separatorMap.get(targetId)
        if (!info) {
            return { label: 'target deleted', isBroken: true }
        }
        // Titel hat Prioritaet, sonst Nummer (ohne trailing Punkt)
        const label = info.title || info.number.replace(/\.$/, '') || '§'
        return { label, isBroken: false }
    }
}
